/**
 * Session Detail Page
 *
 * Tab strip order (Setup sits immediately before Run): Overview → Participants → Items →
 * Criteria → Value ranges → Values → Setup → Run → Results → Review → Ranking.
 */

import { SessionItemChangesNote } from '../../../components/prioritization/session/SessionItemChangesNote';
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { ItemRationaleCell } from '../../../components/prioritization/deliberation/ItemRationaleCell';
import { SessionRankComparison } from '../../../components/prioritization/session/SessionRankComparison';
import { SessionDeliberationThread } from '../../../components/prioritization/deliberation/SessionDeliberationThread';
import { ReverseActionButton } from '../../../components/reversals/ReverseActionButton';
import { RetiredSessionPanel } from '../../../components/reversals/RetiredSessionPanel';
import { unlinkSessionFromTrack, retractSessionPublication } from '../../../lib/reversals/reversalsService';
import { useDeliberationStore } from '../../../stores/prioritization/deliberationStore';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowLeft,
  Settings,
  Target,
  ListChecks,
  Play,
  BarChart3,
  Users,
  Clock,
  AlertCircle,
  CheckCircle,
  ChevronRight,
  ChevronDown,
  Plus,
  Trash2,
  RefreshCw,
  Filter,
  Save,
  Send,
  Scale,
  UserCheck,
  X,
  ClipboardList,
  MessageSquare,
  Sparkles,
  ScrollText,
  Video,
  Link2,
  ExternalLink,
  Copy,
  Loader2,
  GripVertical,
  Pencil,
  FileText,
  ListOrdered,
  LayoutDashboard,
  Sliders,
  Flag,
  CheckCircle2,
  Circle,
  Radio,
  AlertTriangle,
  Mic,
  Scissors,
  DoorOpen,
} from 'lucide-react';
import { detectSessionTrackConflicts, type BindingConflict } from '../../../lib/bindingConflictDetector';
import { BindingConflictReview } from '../../../components/prioritization/BindingConflictReview';
import { SessionRealtimeIndicator } from '../../../components/prioritization/SessionRealtimeIndicator';
import * as XLSX from 'xlsx';
import {
  useSessionStore,
  publishSessionResults,
  PUBLISH_RESULTS_LABEL,
  fetchSessionResultStatus,
  type SessionResultStatus,
  type SessionResultProvenance,
} from '../../../stores/prioritization/sessionStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { isOfficialValue, valueCountsFor, valueScopeKind, VALUE_SCOPE_LABEL } from '../../../lib/valueScope';
import { valueStatus } from '../../../lib/valueStatus';
import { aiCanJudgeValue, valueMarkFor } from '../../../lib/valueMark';
import { useValueAiSettingsStore } from '../../../stores/prioritization/valueAiSettingsStore';
import { useActorDisplayMap } from '../../../hooks/prioritization/useActorDisplayMap';
import type { AcceptedValue } from '../../../types/prioritization/itemLifecycle';
import { saveSessionValue } from '../../../lib/sessionValueEntry';
import { useWriteBackOnClose } from '../../../hooks/prioritization/useWriteBackOnClose';
import { SessionValuesWaitingNote } from '../../../components/prioritization/sessions/SessionValuesWaitingNote';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { usePermissions } from '../../../hooks/prioritization/usePermissions';
import { FacilitatorControls } from '../../../components/prioritization/session/FacilitatorControls';
import { SessionWaitingRoomTab } from '../../../components/prioritization/session/SessionWaitingRoomTab';
import { DivergencePanel } from '../../../components/prioritization/tools/DivergencePanel';
import { FairnessFlagPanel } from '../../../components/prioritization/tools/FairnessFlagPanel';
import { useFeatureFlag, FEATURE_FLAGS } from '../../../lib/featureFlags';
import { useModuleGate } from '../../../lib/moduleGate';
import { SessionCopilotPanel } from '../../../components/prioritization/sessions/SessionCopilotPanel';
import {
  SessionVoicesPanel,
  VoiceContributionCard,
  VoiceBadge,
  useAgentsAsUsersEnabled,
  useVoicesStore,
  isVoiceInvitable,
  voiceSeatCap,
} from '../../../features/voices';
import type { SessionVoicesPanelApi } from '../../../features/voices';
import { getItemCountState } from '../../../components/collection/types';
import { ToolSuitabilityDots } from '../../../components/prioritization/ToolSuitabilityDots';
import { useTrackStore } from '../../../stores/prioritization/trackStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useWorkflowStore } from '../../../stores/prioritization/workflowStore';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useAttributeGovernanceStore } from '../../../stores/prioritization/attributeGovernanceStore';
import { useValueProposalStore } from '../../../stores/prioritization/valueProposalStore';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import { useEffectiveSessionParticipants } from '../../../hooks/useEffectiveSessionParticipants';
import { useCycleParticipantsStore } from '../../../stores/prioritization/cycleParticipantsStore';
import { useCycleParticipantGroupsStore } from '../../../stores/prioritization/cycleParticipantGroupsStore';
import { useTrackParticipantsStore } from '../../../stores/prioritization/trackParticipantsStore';
import { useTrackStepLocks } from '../../../hooks/useTrackStepLocks';
import { describeTrackWeights, formatWeight } from '../../../lib/trackCriteriaWeights';
import { isSameScoringScale, readToolSettings, scoringScaleLabel } from '../../../lib/toolSettings';
import { TrackStepNote } from '../../../features/session-engine-v3/components/TrackStepNote';
import { useTrackParticipantGroupsStore } from '../../../stores/prioritization/trackParticipantGroupsStore';
import { useSessionParticipantsStore } from '../../../stores/prioritization/sessionParticipantsStore';
import { useSessionParticipantGroupsStore } from '../../../stores/prioritization/sessionParticipantGroupsStore';
import { useGroupsStore } from '../../../stores/prioritization/groupsStore';
import type { PrioritizationSession, SessionState, ItemEligibilityStatus, SessionContextMode } from '../../../types/prioritization/session';
import { sessionHasPrioritizationResults } from '../../../types/prioritization/session';
import type { Track } from '../../../types/prioritization/track';
import { validateSessionCandidatesAltitude } from '../../../lib/altitudeValidation';
import { useAltitudeText } from '../../../hooks/prioritization/useAltitudeText';
import type { PrioritizationToolId } from '../../../types/prioritization/prioritizationTask';
import { GridCellSelector } from '../../../components/prioritization/sessions/GridCellSelector';
import { DecisionClassBadge, SessionTypeBadge } from '../../../components/prioritization/DecisionClassBadge';
import type { GridCellFilter } from '../../../types/prioritization/session';
import { 
  evaluateToolCompatibility, 
  type ToolCompatibilityResult,
  type ToolCompatibilityContext,
} from '../../../lib/toolCompatibilityEvaluator';
import { buildSessionClassifications, buildSessionScores } from '../../../lib/toolResultNormalization';
import { getVariantsForTool } from '../../../lib/methodRegistry';
import { routeToolResults } from '../../../lib/sessionResultRouter';
import { collectReadinessBlockers } from '../../../lib/trackReadinessEvaluator';
import { computeValueSufficiency } from '../../../lib/valueSufficiency';
import { getVisibleClassificationEntries } from '../../../lib/classificationDisplay';
import { uniqueLines } from '../../../lib/uniqueLines';
import { ToolItemsProvider, type ToolItem, type ToolSituationalContext } from '../../../contexts/ToolItemsContext';
import { describeResultDestinations } from '../../../lib/sessionResultRouter';
import { recommendCriteria } from '../../../lib/llmCriteriaRecommendationService';
import type { RecommendCriteriaResult } from '../../../lib/llmCriteriaRecommendationService';
import { adviseToolSelection } from '../../../lib/llmToolAdvisorService';
import type { ToolAdvisorResult } from '../../../lib/llmToolAdvisorService';
import { useAiCapability } from '../../../lib/ai/useAiCapability';
import type { ResultsExplainerContext } from '../../../lib/llmResultsExplainerService';
import { SessionMemoAction } from '../../../components/prioritization/memo/SessionMemoAction';
import { TranscriptLinksPanel } from '../../../components/prioritization/transcript/TranscriptLinksPanel';
import { CognitiveBiasPanel } from '../../../components/prioritization/insights/CognitiveBiasPanel';
import type { BiasAnalysisContext } from '../../../types/prioritization/cognitiveBias';
import { NoiseAuditPanel } from '../../../components/prioritization/insights/NoiseAuditPanel';
import { computeNoiseAudit } from '../../../lib/noiseAuditService';
import {
  generateReportArtifact,
  fetchReportTemplates,
  fetchArtifactsForSession,
  SYSTEM_REPORT_TEMPLATES,
} from '../../../lib/reportGenerationService';
import type { ReportTemplate, ReportArtifact } from '../../../lib/reportGenerationService';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { LiftToProposalModal } from '../../../components/prioritization/governance/LiftToProposalModal';
import { StrategicNominationBadge } from '../../../components/prioritization/governance/StrategicNominationBadge';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { ProposalFormationDrawer } from '../../../components/prioritization/catchball/ProposalFormationDrawer';
import { normalizeLoopState, LOOP_STATE_LABELS } from '../../../types/prioritization/catchballLoop';
import { useWorkspaceModules, isModuleEnabled } from '../../../lib/workspaceModulesService';
import {
  catchballQueryFromSearch,
  eventDesignerHref,
  withCatchballQuery,
} from '../../../lib/catchballSessionLink';
import { DecisionUnitPanel } from '../../../components/prioritization/DecisionUnitPanel';
import { useContextsStore } from '../../../stores/prioritization/contextsStore';
import SimpleWeightedScoring from '../../simple-weighted-scoring/SimpleWeightedScoring';
import DotVoting from '../../dot-voting/DotVoting';
import PairwiseRanking from '../../pairwise-ranking/PairwiseRanking';
import StackRanking from '../../stack-ranking/StackRanking';
import BuyAFeature from '../../buy-a-feature/BuyAFeature';
import ImpactEffortMatrix from '../../impact-effort-matrix/ImpactEffortMatrix';
import RiceScoring from '../../rice-scoring/RiceScoring';
import MoscowMethod from '../../moscow-method/MoscowMethod';
import WeightedSmallestJobFirst from '../../wsjf/WeightedSmallestJobFirst';
import CostOfDelay from '../../cost-of-delay/CostOfDelay';
import ParetoAnalysis from '../../pareto-analysis/ParetoAnalysis';
import MaxPrioritiesPyramid from '../../max-priorities-pyramid/MaxPrioritiesPyramid';
import AHP from '../../ahp/AHP';
import FastVsRight from '../../fast-vs-right/FastVsRight';
import EisenhowerMatrix from '../../eisenhower-matrix/EisenhowerMatrix';
import SpeedBoat from '../../speed-boat/SpeedBoat';
import PruneProductTree from '../../prune-product-tree/PruneProductTree';
import MagicPrioritization from '../../magic-prioritization/MagicPrioritization';
import { generateCSV, parseCSV } from '../../../lib/connectors';
import { DateTimePicker } from '../../../components/prioritization/DateTimePicker';
import { getIntegrationsApi } from '../../../lib/integrationsApi';

// Chat & presence
import { SessionChatPanel } from '../../../components/prioritization/sessions/SessionChatPanel';
import { notifySessionLiveNow } from '../../../lib/notificationService';
import { ensureSessionJoinable } from '../../../lib/sessionGoLive';
import { useSessionMessagesStore } from '../../../stores/prioritization/sessionMessagesStore';
import { usePresenceStore } from '../../../stores/prioritization/presenceStore';
import { rankingOutputForToolSession, useToolSessionStore } from '../../../stores/prioritization/toolSessionStore';

// Import tool result type for session integration
import type { ToolHostProps, ToolResultItem } from '../../../components/prioritization/ToolWrapper';
import { AskButton } from '../../../components/ask/AskSheet';
import { FollowButton } from '../../../components/follow/FollowButton';
import { AssignmentsSection } from '../../../components/prioritization/commitments/AssignmentsSection';
import { usePrioritizationTaskStore } from '../../../stores/prioritization/prioritizationTaskStore';
import {
  normalizeSessionToolId,
  SESSION_TOOL_DESCRIPTORS,
} from '../../../lib/sessionToolCatalog';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { SituationPanel } from '../../../components/prioritization/sessions/SituationPanel';
import { SituationReadout } from '../../../components/prioritization/sessions/SituationReadout';
import {
  SituationBasisNote,
  SituationEscapeNotice,
  SituationToolNote,
} from '../../../components/prioritization/sessions/SituationRecommendationNotes';
import { evaluateSituationRules } from '../../../lib/situationRecommendation';
import {
  matrixToolIdsToRegistryIds,
  registryIdToMatrixToolId,
  registryIdsToMatrixToolIds,
} from '../../../lib/situationToolIds';
import { recordMethodChoice } from '../../../lib/methodRecommendationOverrides';
import {
  getSessionDefaultsInheritanceBreakdown,
  cycleLayerFromCycle,
  trackLayerFromTrack,
  workspaceLayerFromWorkspace,
  sessionLayerFromPriorSession,
  DEFAULT_GLOBAL_SESSION_CONFIG,
  type SessionDefaultsScope,
} from '../../../lib/effectiveConfigResolver';
import { LineageTimeline } from '../../../components/prioritization/activity/LineageTimeline';
import { EntityCommentsTab } from '../../../components/prioritization/activity/EntityCommentsTab';
import { SessionTranscriptTab } from '../../../components/prioritization/sessions/SessionTranscriptTab';
import { SessionArtifactsPanel } from '../../../components/prioritization/activity/SessionArtifactsPanel';
import { useSessionLineageEvents } from '../../../components/prioritization/activity/useSessionLineageEvents';
import { getCurrentWorkspaceId, getSafeSupabaseClient } from '../../../lib/supabaseSync';
import { NestedPrioritizationEventPanel } from '../../../components/prioritization/sessions/NestedPrioritizationEventPanel';
import { getSessionReopenTarget, getSessionReopenRefusal, SESSION_REOPEN_LABEL } from '@/lib/sessions/sessionReopen';
import { SESSION_STATE_LABELS, sessionStateLabel, LOCK_CRITERIA_LABEL, RUN_SESSION_LABEL, SUBMIT_MY_RANKING_LABEL, REVISE_MY_RANKING_LABEL, RANK_OVERRIDES_HEADING } from '@/lib/sessions/vocabulary';
import { RankOverrideForm, RankOverrideList } from '@/components/prioritization/RankOverridePanel';
import { RationaleFields } from '@/components/prioritization/decisions/RationaleFields';
import { EMPTY_RATIONALE, toRationaleParts, type RationaleValue } from '@/lib/rationaleParts';
import { useSessionRankChangeRule, rankChangeNeedsLine } from '@/lib/sessions/rankChangeRule';
import { RankOrderChangeSetting } from '@/components/prioritization/cycle-config/RankOrderChangeSetting';
import { RoomRulesSettings } from '@/components/prioritization/cycle-config/RoomRulesSettings';
import { anonymousLabel, namesHidden, useSessionRoomRules } from '@/lib/sessions/roomRules';
import { Panel, ValueMark } from '@/components/ds';
import { isToolSwitchedOff } from '@/lib/switchedOffTools';
import { ItemRelationshipFlags } from '@/components/prioritization/ItemRelationshipFlags';
import { StructureVersionFinePrint } from '@/components/hierarchy/StructureVersionFinePrint';
import { useSessionPublishRight } from '@/hooks/prioritization/useActRight';
import { refusedByRight } from '@/lib/actRight';
import { useItemHref } from '@/lib/itemLinkScope';
import { useItemLinkScope } from '@/lib/itemLinkScope';

// ============================================================================
// Session Header Helpers
// ============================================================================

function SessionStateBadge({ state }: { state: SessionState }) {
  const labelByState = SESSION_STATE_LABELS;

  const classByState: Record<SessionState, string> = {
    DRAFT: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
    CRITERIA_FINALIZED: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    RUN_COMPLETE: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    PUBLISHED: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${classByState[state]}`}>
      {labelByState[state]}
    </span>
  );
}

// ============================================================================
// Tab Components
// ============================================================================

type Tab = 'overview' | 'setup' | 'participants' | 'items' | 'criteria' | 'value-ranges' | 'values' | 'run' | 'results' | 'review' | 'ranking' | 'prioritization-event' | 'artifacts' | 'discussion' | 'transcript' | 'waiting-room';

interface SessionToolDefinition {
  id: string;
  aliases?: string[];
  name: string;
  description: string;
  compatibilityId: string;
  component: React.ComponentType<ToolHostProps>;
}

const SESSION_TOOL_COMPONENTS: Record<string, React.ComponentType<ToolHostProps>> = {
  pairwise_ranking: PairwiseRanking,
  stack_ranking: StackRanking,
  dot_voting: DotVoting,
  simple_weighted_scoring: SimpleWeightedScoring,
  wsjf: WeightedSmallestJobFirst,
  cost_of_delay: CostOfDelay,
  pareto_analysis: ParetoAnalysis,
  buy_a_feature: BuyAFeature,
  impact_effort_matrix: ImpactEffortMatrix,
  rice: RiceScoring,
  moscow: MoscowMethod,
  max_priorities_pyramid: MaxPrioritiesPyramid,
  ahp: AHP,
  fast_vs_right: FastVsRight,
  eisenhower_matrix: EisenhowerMatrix,
  speed_boat: SpeedBoat,
  prune_product_tree: PruneProductTree,
  magic_prioritization: MagicPrioritization,
};

const SESSION_TOOL_DEFINITIONS: SessionToolDefinition[] = SESSION_TOOL_DESCRIPTORS.map((descriptor) => {
  const component = SESSION_TOOL_COMPONENTS[descriptor.compatibilityId];
  if (!component) {
    throw new Error(`Missing session component binding for ${descriptor.compatibilityId}`);
  }

  return {
    ...descriptor,
    component,
  };
});

const SESSION_TOOL_DEFINITION_BY_COMPATIBILITY_ID: Record<string, SessionToolDefinition> = Object.fromEntries(
  SESSION_TOOL_DEFINITIONS.map((tool) => [tool.compatibilityId, tool])
);

/** Tools offered for picking. Switched-off tools stay resolvable above so sessions already run with them still open. */
const OFFERED_SESSION_TOOL_DEFINITIONS = SESSION_TOOL_DEFINITIONS.filter(
  (tool) => !isToolSwitchedOff(tool.compatibilityId)
);

function resolveSessionToolDefinition(toolId?: string | null): SessionToolDefinition | undefined {
  const canonicalToolId = normalizeSessionToolId(toolId);
  if (!canonicalToolId) return undefined;
  return SESSION_TOOL_DEFINITION_BY_COMPATIBILITY_ID[canonicalToolId];
}

interface TabProps {
  session: PrioritizationSession;
}

// Helper to format inheritance source for display
function formatSourceLabel(source: SessionDefaultsScope): string {
  switch (source) {
    case 'track': return 'from track';
    case 'cycle': return 'from cycle';
    case 'global': return 'default';
    case 'session': return 'session override';
    default: return '';
  }
}

// Setup Tab — session config (name, description, mode)
// onNavigate lets the footer button jump straight to Run
export function SetupTab({ session, onNavigate }: TabProps & { onNavigate?: (tab: Tab) => void }) {
  const updateSession = useSessionStore(state => state.updateSession);
  const updateModeConfig = useSessionStore(state => state.updateModeConfig);
  const setupUserId = useUserStore(state => state.userId);
  const { capabilities: setupCapabilities } = usePermissions();
  const cycles = useDegapProcessStore(state => state.cycles);
  const tracks = useTrackStore(state => state.tracks);
  const getCurrentWorkspace = useTenantStore(state => state.getCurrentWorkspace);

  const [name, setName] = useState(session.name);
  const [description, setDescription] = useState(session.description || '');
  const [hasChanges, setHasChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showOverrideControls, setShowOverrideControls] = useState(false);
  const [meetingLoading, setMeetingLoading] = useState(false);
  const [meetingError, setMeetingError] = useState<string | null>(null);
  const [meetingCopied, setMeetingCopied] = useState(false);

  // Read which video connectors are currently connected from localStorage
  const connectedVideoProviders = useMemo<Array<'zoom' | 'google-meet'>>(() => {
    try {
      const stored = localStorage.getItem('peak-priorities-connector-connections');
      if (!stored) return [];
      const connections: Array<{ type: string; status: string }> = JSON.parse(stored);
      return connections
        .filter(c => (c.type === 'zoom' || c.type === 'google-meet') && c.status === 'connected')
        .map(c => c.type as 'zoom' | 'google-meet');
    } catch {
      return [];
    }
  }, []);

  const handleCreateMeeting = async (provider: 'zoom' | 'google-meet') => {
    setMeetingLoading(true);
    setMeetingError(null);
    try {
      const api = getIntegrationsApi();
      const startTime = session.scheduledFor ?? new Date();
      if (provider === 'zoom') {
        const result = await api.createZoomMeeting({
          taskId: session.id,
          taskName: session.name,
          toolName: 'Prioritization Session',
          scheduledStart: startTime,
          durationMinutes: 60,
        });
        if (result.success && result.joinUrl) {
          const saved = await updateSession(session.id, { meetingLink: result.joinUrl, meetingProvider: 'zoom' });
          if (!saved.success) setMeetingError(saved.error ?? 'The Zoom meeting was created but could not be saved to this session');
        } else {
          setMeetingError(result.error ?? 'Failed to create Zoom meeting');
        }
      } else {
        const result = await api.createGoogleMeetMeeting({
          taskId: session.id,
          taskName: session.name,
          toolName: 'Prioritization Session',
          scheduledStart: startTime,
          durationMinutes: 60,
        });
        if (result.success && result.meetingUrl) {
          const saved = await updateSession(session.id, { meetingLink: result.meetingUrl, meetingProvider: 'google-meet' });
          if (!saved.success) setMeetingError(saved.error ?? 'The Google Meet link was created but could not be saved to this session');
        } else {
          setMeetingError(result.error ?? 'Failed to create Google Meet link');
        }
      }
    } catch (err) {
      setMeetingError(err instanceof Error ? err.message : 'Failed to create meeting');
    } finally {
      setMeetingLoading(false);
    }
  };

  const handleCopyMeetingLink = () => {
    if (!session.meetingLink) return;
    navigator.clipboard.writeText(session.meetingLink).then(() => {
      setMeetingCopied(true);
      setTimeout(() => setMeetingCopied(false), 2000);
    });
  };

  const handleRemoveMeeting = async () => {
    setMeetingError(null);
    const result = await updateSession(session.id, { meetingLink: undefined, meetingProvider: undefined });
    if (!result.success) setMeetingError(result.error ?? 'Could not remove the meeting link');
  };

  const modeConfig = session.modeConfig || {
    timingMode: 'live' as const,
    entryMode: 'facilitator' as const,
    allowViewOtherResponses: false,
    allowResponseChanges: true,
    requireAllResponses: false,
  };

  const cycle = useMemo(() => (session.cycleId ? cycles.find(c => c.id === session.cycleId) : null), [session.cycleId, cycles]);
  const track = useMemo(() => (session.trackId ? tracks.find(t => t.id === session.trackId) : null), [session.trackId, tracks]);

  const inheritanceBreakdown = useMemo(() => {
    const workspaceLayer = workspaceLayerFromWorkspace(getCurrentWorkspace());
    const cycleLayer = cycle ? cycleLayerFromCycle(cycle) : null;
    const trackLayer = track ? trackLayerFromTrack(track) : null;
    const sessionLayer = sessionLayerFromPriorSession({ modeConfig: session.modeConfig, facilitatorId: session.facilitatorId });
    return getSessionDefaultsInheritanceBreakdown(
      workspaceLayer ?? DEFAULT_GLOBAL_SESSION_CONFIG,
      cycleLayer,
      trackLayer,
      sessionLayer
    );
  }, [session.modeConfig, session.facilitatorId, cycle, track, getCurrentWorkspace]);

  const timingFromInheritance = inheritanceBreakdown.timingMode.source !== 'session';
  const entryFromInheritance = inheritanceBreakdown.entryMode.source !== 'session';
  const bothInherited = timingFromInheritance && entryFromInheritance;

  const handleSave = async () => {
    setSaveError(null);
    const result = await updateSession(session.id, { name: name.trim(), description });
    if (result.success) {
      setHasChanges(false);
    } else {
      setSaveError(result.error ?? 'Could not save the session name and description');
    }
  };

  const handleModeChange = (field: string, value: unknown) => {
    updateModeConfig(session.id, { [field]: value });
  };

  const nameIsEmpty = name.trim().length === 0;
  
  return (
    <div className="space-y-6">
      {/* Header row with "Select tool and run" top-right */}
      <div className="flex items-center justify-between">
        <span />
        <button
          type="button"
          onClick={() => onNavigate?.('run')}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 transition-colors shrink-0"
        >
          <Play className="h-4 w-4" />
          Select tool and run
        </button>
      </div>

      {/* Session name + description — inline, no card wrapper */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
            Session name
            <span className="text-[var(--coral)] ml-0.5">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setHasChanges(true); }}
            placeholder="e.g. Q2 Product Priorities, Platform Track Scoring"
            className={`w-full px-3 py-2 border  focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              nameIsEmpty ? 'border-[var(--mustard)] bg-[var(--mustard-dim)]/50' : 'border-[var(--rule)]'
            }`}
          />
          {nameIsEmpty && (
            <p className="text-xs text-[var(--mustard)] mt-1">
              A name helps identify this session in the provenance feed and reports.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => { setDescription(e.target.value); setHasChanges(true); }}
            rows={3}
            className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={nameIsEmpty}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            <Save className="h-4 w-4" />
            Save Changes
          </button>
        )}
        {saveError && (
          <p role="alert" className="text-xs text-[var(--coral)] flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" />
            {saveError}
          </p>
        )}
      </div>

      {/* The room — declared situation, corroborated by existing signals */}
      <SituationPanel session={session} />

      {/* Session Mode Configuration */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Session Mode</h3>
        
        {bothInherited && !showOverrideControls ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between border border-[var(--rule)] bg-[var(--surface)] px-4 py-3">
              <div className="text-sm text-[var(--text-sec)]">
                <span className="font-medium">{inheritanceBreakdown.effective.timingMode === 'live' ? 'Live' : 'Async'}</span>
                {', '}
                <span className="font-medium">{inheritanceBreakdown.effective.entryMode === 'facilitator' ? 'Facilitator entry' : 'Participant entry'}</span>
                <span className="text-[var(--text-ter)] ml-2">
                  ({formatSourceLabel(
                    inheritanceBreakdown.timingMode.source === 'track' || inheritanceBreakdown.entryMode.source === 'track'
                      ? 'track'
                      : inheritanceBreakdown.timingMode.source === 'cycle' || inheritanceBreakdown.entryMode.source === 'cycle'
                        ? 'cycle'
                        : 'global'
                  )})
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowOverrideControls(true)}
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                Override
              </button>
            </div>
          </div>
        ) : (
        <div className="space-y-6">
          {showOverrideControls && (
            <p className="text-xs text-[var(--text-ter)]">
              Overriding inherited session mode. Changes apply to this session only.
            </p>
          )}
          {/* Timing Mode */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Timing Mode
            </label>
            <p className="text-xs text-[var(--text-ter)] mb-3">
              Is this session happening live (real-time) or asynchronously over a period?
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleModeChange('timingMode', 'live')}
                className={`p-4  border-2 text-left transition-all ${
                  modeConfig.timingMode === 'live'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Play className="h-4 w-4 text-primary-600" />
                  <span className="font-medium text-[var(--text-pri)]">Live Session</span>
                </div>
                <p className="text-xs text-[var(--text-ter)]">
                  Everyone participates at the same time
                </p>
              </button>
              <button
                onClick={() => handleModeChange('timingMode', 'async')}
                className={`p-4  border-2 text-left transition-all ${
                  modeConfig.timingMode === 'async'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-[var(--mustard)]" />
                  <span className="font-medium text-[var(--text-pri)]">Async Session</span>
                </div>
                <p className="text-xs text-[var(--text-ter)]">
                  Open for a period, then closes
                </p>
              </button>
            </div>
          </div>
          
          {/* Async-specific options */}
          {modeConfig.timingMode === 'async' && (
            <div className="grid grid-cols-2 gap-4 p-4 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Opens At
                </label>
                <DateTimePicker
                  value={modeConfig.opensAt ? new Date(modeConfig.opensAt) : undefined}
                  onChange={(date) => handleModeChange('opensAt', date)}
                  placeholder="Pick open date & time…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Closes At
                </label>
                <DateTimePicker
                  value={modeConfig.closesAt ? new Date(modeConfig.closesAt) : undefined}
                  onChange={(date) => handleModeChange('closesAt', date)}
                  minDate={modeConfig.opensAt ? new Date(modeConfig.opensAt) : undefined}
                  placeholder="Pick close date & time…"
                />
              </div>
            </div>
          )}

          {/* The room's timer rule and names (S-2, S-3; set here, or inherited from the track, cycle or workflow) */}
          <div className="border border-[var(--rule)] bg-[var(--bg-raised)]">
            <RoomRulesSettings
              scopeKind="session"
              scopeId={session.id}
              ids={{
                workspaceId: session.workspaceId ?? null,
                workflowId: cycle?.workflowId ?? null,
                cycleId: session.cycleId ?? null,
                trackId: session.trackId ?? null,
                sessionId: session.id,
              }}
              canEdit={setupUserId === session.facilitatorId || setupCapabilities.canAdmin}
            />
          </div>

          {/* Video Meeting */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2 flex items-center gap-1.5">
              <Video className="h-4 w-4 text-[var(--text-ter)]" />
              Video Meeting
            </label>

            {session.meetingLink ? (
              <div className="p-3 bg-[var(--slate-dim)] border border-[var(--slate)] space-y-2">
                <div className="flex items-center gap-2">
                  {session.meetingProvider === 'zoom' ? (
                    <svg viewBox="0 0 20 20" className="w-4 h-4 shrink-0" fill="none">
                      <rect width="20" height="20" rx="3" fill="#2D8CFF" />
                      <path d="M3 7C3 6.45 3.45 6 4 6h7c.55 0 1 .45 1 1v6c0 .55-.45 1-1 1H4c-.55 0-1-.45-1-1V7Z" fill="white" />
                      <path d="M13 8.5l3.2-1.9c.37-.22.8.05.8.48v5.84c0 .43-.43.7-.8.48L13 11.5V8.5Z" fill="white" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 20 20" className="w-4 h-4 shrink-0">
                      <path d="M11.25 10l3.75 2.72V7.28L11.25 10z" fill="#00832D" />
                      <path d="M5 12.92h6.25V7.08H5a.83.83 0 0 0-.83.83v4.18c0 .46.37.83.83.83z" fill="#0066DA" />
                      <path d="M11.25 7.08v2.92l-2.5-2.17V7.08h2.5z" fill="#E94235" />
                      <path d="M11.25 10l-2.5 2.17v.75h2.5V10z" fill="#2684FC" />
                      <path d="M5 7.08h3.75l2.5 2.17V7.08H5z" fill="#00AC47" />
                      <path d="M5 12.92h3.75l2.5-2.17v2.17H5z" fill="#FFBA00" />
                    </svg>
                  )}
                  <span className="text-sm font-medium text-[var(--text-sec)]">
                    {session.meetingProvider === 'zoom' ? 'Zoom' : 'Google Meet'} meeting created
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5 text-[var(--text-ter)] shrink-0" />
                  <span className="text-xs text-[var(--text-sec)] truncate flex-1">{session.meetingLink}</span>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={handleCopyMeetingLink}
                    className="flex items-center gap-1 text-xs text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    {meetingCopied ? 'Copied!' : 'Copy link'}
                  </button>
                  <a
                    href={session.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Open
                  </a>
                  <button
                    onClick={handleRemoveMeeting}
                    className="flex items-center gap-1 text-xs text-[var(--coral)] hover:text-[var(--coral)] transition-colors ml-auto"
                  >
                    <X className="h-3.5 w-3.5" />
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {connectedVideoProviders.length === 0 ? (
                  <p className="text-xs text-[var(--text-ter)] bg-[var(--surface)] border border-[var(--rule)] px-3 py-2">
                    Connect <Link to="/prioritization/settings/connectors" className="text-primary-600 hover:underline">Zoom or Google Meet</Link> in Connectors to generate a video link for this session.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {connectedVideoProviders.includes('zoom') && (
                      <button
                        onClick={() => handleCreateMeeting('zoom')}
                        disabled={meetingLoading}
                        className="flex items-center gap-2 px-3 py-2 bg-[#2D8CFF] text-white text-sm hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <svg viewBox="0 0 20 20" className="w-4 h-4" fill="none">
                          <rect width="20" height="20" rx="3" fill="white" fillOpacity="0.2" />
                          <path d="M3 7C3 6.45 3.45 6 4 6h7c.55 0 1 .45 1 1v6c0 .55-.45 1-1 1H4c-.55 0-1-.45-1-1V7Z" fill="white" />
                          <path d="M13 8.5l3.2-1.9c.37-.22.8.05.8.48v5.84c0 .43-.43.7-.8.48L13 11.5V8.5Z" fill="white" />
                        </svg>
                        {meetingLoading ? 'Creating…' : 'Create Zoom meeting'}
                      </button>
                    )}
                    {connectedVideoProviders.includes('google-meet') && (
                      <button
                        onClick={() => handleCreateMeeting('google-meet')}
                        disabled={meetingLoading}
                        className="flex items-center gap-2 px-3 py-2 border border-[var(--rule)] text-[var(--text-sec)] text-sm hover:bg-[var(--surface)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <svg viewBox="0 0 20 20" className="w-4 h-4">
                          <path d="M11.25 10l3.75 2.72V7.28L11.25 10z" fill="#00832D" />
                          <path d="M5 12.92h6.25V7.08H5a.83.83 0 0 0-.83.83v4.18c0 .46.37.83.83.83z" fill="#0066DA" />
                          <path d="M11.25 7.08v2.92l-2.5-2.17V7.08h2.5z" fill="#E94235" />
                          <path d="M11.25 10l-2.5 2.17v.75h2.5V10z" fill="#2684FC" />
                          <path d="M5 7.08h3.75l2.5 2.17V7.08H5z" fill="#00AC47" />
                          <path d="M5 12.92h3.75l2.5-2.17v2.17H5z" fill="#FFBA00" />
                        </svg>
                        {meetingLoading ? 'Creating…' : 'Create Google Meet'}
                      </button>
                    )}
                  </div>
                )}
                {meetingError && (
                  <p className="text-xs text-[var(--coral)] flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {meetingError}
                  </p>
                )}
              </div>
            )}
          </div>
          
          {/* Entry Mode */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Entry Mode
            </label>
            <p className="text-xs text-[var(--text-ter)] mb-3">
              Who enters the data into the system?
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleModeChange('entryMode', 'facilitator')}
                className={`p-4  border-2 text-left transition-all ${
                  modeConfig.entryMode === 'facilitator'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <UserCheck className="h-4 w-4 text-[var(--text-ter)]" />
                  <span className="font-medium text-[var(--text-pri)]">Facilitator Entry</span>
                </div>
                <p className="text-xs text-[var(--text-ter)]">
                  Facilitator enters on behalf of the group
                </p>
              </button>
              <button
                onClick={() => handleModeChange('entryMode', 'participants')}
                className={`p-4  border-2 text-left transition-all ${
                  modeConfig.entryMode === 'participants'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Users className="h-4 w-4 text-[var(--green)]" />
                  <span className="font-medium text-[var(--text-pri)]">Participant Entry</span>
                </div>
                <p className="text-xs text-[var(--text-ter)]">
                  Each participant enters their own responses
                </p>
              </button>
            </div>
          </div>
          
          {/* Participant entry options */}
          {modeConfig.entryMode === 'participants' && (
            <div className="p-4 bg-[var(--green-dim)] border border-[var(--green)] space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={modeConfig.allowViewOtherResponses || false}
                  onChange={(e) => handleModeChange('allowViewOtherResponses', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-[var(--rule)] rounded focus:ring-primary-500"
                />
                <div>
                  <span className="text-sm font-medium text-[var(--text-sec)]">
                    Allow viewing other responses
                  </span>
                  <p className="text-xs text-[var(--text-ter)]">
                    Participants can see each other's responses before submitting
                  </p>
                </div>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={modeConfig.allowResponseChanges !== false}
                  onChange={(e) => handleModeChange('allowResponseChanges', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-[var(--rule)] rounded focus:ring-primary-500"
                />
                <div>
                  <span className="text-sm font-medium text-[var(--text-sec)]">
                    Allow response changes
                  </span>
                  <p className="text-xs text-[var(--text-ter)]">
                    Participants can modify their responses after submission
                  </p>
                </div>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={modeConfig.requireAllResponses || false}
                  onChange={(e) => handleModeChange('requireAllResponses', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-[var(--rule)] rounded focus:ring-primary-500"
                />
                <div>
                  <span className="text-sm font-medium text-[var(--text-sec)]">
                    Require all responses
                  </span>
                  <p className="text-xs text-[var(--text-ter)]">
                    All participants must respond before proceeding
                  </p>
                </div>
              </label>
            </div>
          )}
        </div>
        )}
      </div>

    </div>
  );
}

// ─── Floating dropdown (replaces native <select> to avoid transform anchor bug) ─

interface SelectOption { value: string; label: string }

function FloatingSelect({
  value, onChange, options, placeholder = 'Select…', className = '',
}: {
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [pos, setPos] = React.useState({ top: 0, left: 0, width: 0 });

  const selected = options.find(o => o.value === value);

  const openDropdown = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX, width: r.width });
    }
    setOpen(true);
  };

  React.useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openDropdown}
        className={`flex items-center justify-between gap-2 px-3 py-2 text-sm border border-[var(--rule)]  bg-[var(--bg-raised)] hover:bg-[var(--surface)] transition-colors text-left ${className}`}
      >
        <span className={selected ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)]'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)] shrink-0" />
      </button>

      {open && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
          className="bg-[var(--bg-raised)] border border-[var(--rule)] shadow-lg overflow-hidden max-h-56 overflow-y-auto"
          onMouseDown={e => e.stopPropagation()}
        >
          {options.length === 0 && (
            <p className="px-3 py-2 text-sm text-[var(--text-ter)]">No options available</p>
          )}
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 transition-colors ${
                opt.value === value ? 'bg-primary-50 text-primary-700 font-medium' : 'text-[var(--text-pri)]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

// Participants Tab — mirrors Track Participation tab
export function ParticipantsTab({ session }: TabProps) {
  const fetchSessionParticipants = useSessionParticipantsStore(s => s.fetchParticipantsForSession);
  const fetchSessionParticipantGroups = useSessionParticipantGroupsStore(s => s.fetchParticipantGroupsForSession);
  const allSessionParticipants = useSessionParticipantsStore(s => s.participants);
  const sessionParticipants = useMemo(() => allSessionParticipants.filter(p => p.sessionId === session.id), [allSessionParticipants, session.id]);
  const allSessionParticipantGroups = useSessionParticipantGroupsStore(s => s.participantGroups);
  const sessionParticipantGroups = useMemo(() => allSessionParticipantGroups.filter(g => g.sessionId === session.id && g.status === 'active'), [allSessionParticipantGroups, session.id]);
  const { steps: sessionSteps } = useTrackStepLocks(session.trackId);
  const peopleLocked = sessionSteps?.S1.disposition === 'locked';
  const addSessionParticipant = useSessionParticipantsStore(s => s.addParticipant);
  const removeSessionParticipantById = useSessionParticipantsStore(s => s.removeParticipant);
  const addSessionParticipantGroup = useSessionParticipantGroupsStore(s => s.addParticipantGroup);
  const removeSessionParticipantGroup = useSessionParticipantGroupsStore(s => s.removeParticipantGroup);
  const groupsRaw = useGroupsStore(s => s.groups);
  const groups = useMemo(() => groupsRaw ?? [], [groupsRaw]);
  const fetchGroups = useGroupsStore(s => s.fetchFromSupabase);
  const userId = useUserStore(state => state.userId) ?? 'system';
  const updateSession = useSessionStore(state => state.updateSession);
  const { effectiveUserIds, loading: effectiveLoading } = useEffectiveSessionParticipants(session);
  const { users } = useSystemUsers({ workspaceId: session.workspaceId ?? null });
  const voicesEnabled = useAgentsAsUsersEnabled();
  const voices = useVoicesStore((s) => s.voices);
  const fetchVoices = useVoicesStore((s) => s.fetchVoices);
  const fetchSeats = useVoicesStore((s) => s.fetchSeats);
  const seatsBySession = useVoicesStore((s) => s.seatsBySession);

  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [addKind, setAddKind] = useState<'person' | 'voice'>('person');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [pickerSearch, setPickerSearch] = useState('');
  const [overrideCap, setOverrideCap] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [addParticipantError, setAddParticipantError] = useState<string | null>(null);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const voicesApiRef = useRef<SessionVoicesPanelApi | null>(null);

  useEffect(() => {
    fetchSessionParticipants(session.id);
    fetchSessionParticipantGroups(session.id);
  }, [session.id, fetchSessionParticipants, fetchSessionParticipantGroups]);
  useEffect(() => {
    if (showAddGroup) void fetchGroups();
  }, [showAddGroup, fetchGroups]);
  useEffect(() => {
    if (!voicesEnabled || !session.workspaceId) return;
    void fetchVoices(session.workspaceId);
    void fetchSeats(session.id);
  }, [voicesEnabled, session.workspaceId, session.id, fetchVoices, fetchSeats]);

  const sessionParticipantIds = useMemo(
    () => sessionParticipants.filter(p => p.status === 'active').map(p => p.userId),
    [sessionParticipants]
  );

  const isTrackLinked = session.sessionContextMode === 'cycle_linked';
  const displayParticipantIds = useMemo(() => {
    if (isTrackLinked && effectiveUserIds.length > 0) return effectiveUserIds;
    if (sessionParticipantIds.length > 0) return sessionParticipantIds;
    return session.participantIds;
  }, [isTrackLinked, effectiveUserIds, sessionParticipantIds, session.participantIds]);

  const sessionDirectIds = new Set(sessionParticipantIds);
  const availableUsers = users.filter(user => !displayParticipantIds.includes(user.id));
  const voiceSeats = useMemo(
    () => (seatsBySession[session.id] ?? []).filter((s) => s.status === 'active'),
    [seatsBySession, session.id],
  );
  const seatedVoiceIds = useMemo(() => new Set(voiceSeats.map((s) => s.voiceId)), [voiceSeats]);
  const availableVoices = useMemo(
    () => voices.filter((v) => isVoiceInvitable(v) && !seatedVoiceIds.has(v.id)),
    [voices, seatedVoiceIds],
  );
  const voiceCap = voiceSeatCap(displayParticipantIds.length);
  const needsVoiceOverride = voiceSeats.length >= voiceCap;

  const filteredUsers = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return availableUsers;
    return availableUsers.filter(
      (u) =>
        (u.name ?? '').toLowerCase().includes(q) ||
        (u.email ?? '').toLowerCase().includes(q),
    );
  }, [availableUsers, pickerSearch]);

  const filteredVoices = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return availableVoices;
    return availableVoices.filter(
      (v) =>
        v.displayName.toLowerCase().includes(q) ||
        (v.corpusKind ?? '').toLowerCase().includes(q),
    );
  }, [availableVoices, pickerSearch]);

  const pickerListCount =
    addKind === 'person' ? availableUsers.length : availableVoices.length;
  const pickerCountState = getItemCountState(pickerListCount);

  const facilitatorOptions = effectiveUserIds.length > 0
    ? users.filter(u => effectiveUserIds.includes(u.id))
    : users;
  const availableGroupsForAdd = useMemo(() => {
    const addedGroupIds = new Set(sessionParticipantGroups.map(g => g.groupId));
    return groups.filter(g => !addedGroupIds.has(g.id));
  }, [groups, sessionParticipantGroups]);

  const groupDisplayName = (groupId: string) => {
    const g = groups.find(gr => gr.id === groupId);
    return g?.displayName ?? g?.name ?? groupId.slice(0, 8) + '…';
  };

  const resetAddPicker = () => {
    setShowAddParticipant(false);
    setSelectedUserId('');
    setSelectedVoiceId('');
    setAddParticipantError(null);
    setOverrideCap(false);
    setOverrideReason('');
    setPickerSearch('');
    setAddKind('person');
  };

  const handleAddParticipant = async () => {
    setAddParticipantError(null);
    if (addKind === 'voice' && voicesEnabled) {
      if (!selectedVoiceId) return;
      const api = voicesApiRef.current;
      if (!api) {
        setAddParticipantError('Voices panel is still loading. Try again.');
        return;
      }
      const ok = await api.inviteVoice(selectedVoiceId, {
        overrideCap,
        overrideReason: overrideReason.trim() || null,
      });
      if (!ok) {
        setAddParticipantError(useVoicesStore.getState().error ?? 'Failed to invite Voice.');
        return;
      }
      resetAddPicker();
      return;
    }
    if (!selectedUserId) return;
    const result = await addSessionParticipant({
      sessionId: session.id,
      userId: selectedUserId,
      role: 'contributor',
      addedBy: userId,
    });
    if (!result) {
      const storeError = useSessionParticipantsStore.getState().error;
      setAddParticipantError(storeError ?? 'Failed to add participant. Check your permissions.');
      return;
    }
    resetAddPicker();
  };

  const handleRemoveParticipant = async (participantUserId: string) => {
    const record = sessionParticipants.find(p => p.userId === participantUserId && p.status === 'active');
    if (record) {
      await removeSessionParticipantById(record.id);
    }
  };

  const itemInputs = (session.itemIds ?? []).map((id) => {
    const item = useItemsStore.getState().getItemById(id);
    return {
      id,
      title: item?.title ?? id.slice(0, 8),
      description: item?.description,
    };
  });

  return (
    <div className="space-y-6">
      {/* Facilitator */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-3">Facilitator</h3>
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-sm font-medium">F</div>
          {facilitatorOptions.length > 0 ? (
            <FloatingSelect
              value={session.facilitatorId}
              onChange={(v) => updateSession(session.id, { facilitatorId: v })}
              options={facilitatorOptions.map(u => ({
                value: u.id,
                label: `${u.name ?? u.email} (${u.email})`,
              }))}
              className="min-w-[220px]"
            />
          ) : (
            <span className="text-sm text-[var(--text-ter)]">{session.facilitatorId.slice(0, 8)}…</span>
          )}
        </div>
      </div>

      {/* Inherited participants (from track/cycle) */}
      {isTrackLinked && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[var(--text-pri)]">From track &amp; cycle (inherited)</h3>
            {effectiveLoading ? (
              <span className="text-xs text-[var(--text-ter)]">Loading…</span>
            ) : (
              <span className="text-xs text-[var(--text-ter)]">{effectiveUserIds.length} people</span>
            )}
          </div>
          {!effectiveLoading && (
            <div className="border border-[var(--rule)] overflow-hidden">
              <table className="min-w-full divide-y divide-[var(--rule)]">
                <thead className="bg-[var(--surface)]">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Email</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Source</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--rule)]">
                  {displayParticipantIds.filter(id => !sessionDirectIds.has(id)).map((pid) => {
                    const user = users.find(u => u.id === pid);
                    return (
                      <tr key={pid} className="hover:bg-[var(--surface)]">
                        <td className="px-4 py-2 text-sm text-[var(--text-pri)]">{user?.name ?? pid.slice(0, 8) + '…'}</td>
                        <td className="px-4 py-2 text-sm text-[var(--text-ter)]">{user?.email ?? '—'}</td>
                        <td className="px-4 py-2">
                          <span className="text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] px-2 py-0.5 rounded-full">Inherited</span>
                        </td>
                      </tr>
                    );
                  })}
                  {displayParticipantIds.filter(id => !sessionDirectIds.has(id)).length === 0 && (
                    <tr><td colSpan={3} className="px-4 py-4 text-sm text-[var(--text-ter)] text-center">No inherited participants</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Session-specific participants */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[var(--text-pri)]">
            {isTrackLinked ? 'Session-specific additions' : 'Participants'}
          </h3>
          {!peopleLocked && (
            <button
              type="button"
              onClick={() => setShowAddParticipant(true)}
              className="btn btn-primary btn-sm flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              {voicesEnabled ? 'Add people or Voices' : 'Add participant'}
            </button>
          )}
        </div>
        <TrackStepNote step={sessionSteps?.S1} />

        {showAddParticipant && !peopleLocked && (
          <div className="mb-4 space-y-3 p-3 border-2 border-primary-200 bg-primary-50">
            {voicesEnabled && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setAddKind('person');
                    setSelectedVoiceId('');
                    setAddParticipantError(null);
                  }}
                  className={`px-3 py-1.5 text-xs font-medium  border transition-colors ${
                    addKind === 'person'
                      ? 'bg-[var(--bg-raised)] border-primary-400 text-primary-800'
                      : 'bg-transparent border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--bg-raised)]/60'
                  }`}
                >
                  People
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAddKind('voice');
                    setSelectedUserId('');
                    setAddParticipantError(null);
                  }}
                  className={`px-3 py-1.5 text-xs font-medium  border transition-colors inline-flex items-center gap-1.5 ${
                    addKind === 'voice'
                      ? 'bg-electric-mustard-50 border-electric-mustard-400 text-electric-mustard-900'
                      : 'bg-transparent border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--bg-raised)]/60'
                  }`}
                >
                  Voices
                  <VoiceBadge />
                </button>
              </div>
            )}

            {pickerCountState === 'many' && (
              <div className="relative">
                <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
                <input
                  type="search"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder={addKind === 'voice' ? 'Search Voices…' : 'Search people…'}
                  className="w-full border border-[var(--rule)] pl-8 pr-3 py-2 text-sm bg-[var(--bg-raised)]"
                />
              </div>
            )}

            {addKind === 'voice' && voicesEnabled ? (
              <>
                <FloatingSelect
                  value={selectedVoiceId}
                  onChange={(v) => { setSelectedVoiceId(v); setAddParticipantError(null); }}
                  placeholder="Select a Voice for context…"
                  options={filteredVoices.map((v) => ({
                    value: v.id,
                    label: `${v.displayName} (${v.corpusKind ?? 'no corpus'})`,
                  }))}
                  className="w-full"
                />
                <p className="text-[11px] text-[var(--text-ter)]">
                  Voices provide constituency context — not votes. Invite cap {voiceCap} of {displayParticipantIds.length} humans.
                </p>
                {needsVoiceOverride && (
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-xs text-[var(--text-sec)]">
                      <input
                        type="checkbox"
                        checked={overrideCap}
                        onChange={(e) => setOverrideCap(e.target.checked)}
                      />
                      Override invite cap (logged deliberate action)
                    </label>
                    {overrideCap && (
                      <input
                        type="text"
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                        placeholder="Reason for override…"
                        className="w-full border border-[var(--rule)] px-3 py-2 text-sm bg-[var(--bg-raised)]"
                      />
                    )}
                  </div>
                )}
              </>
            ) : (
              <FloatingSelect
                value={selectedUserId}
                onChange={(v) => { setSelectedUserId(v); setAddParticipantError(null); }}
                placeholder="Select a person…"
                options={filteredUsers.map(u => ({ value: u.id, label: u.name ?? u.email }))}
                className="w-full"
              />
            )}

            {addParticipantError && (
              <p className="text-xs text-[var(--coral)] flex items-center gap-1">
                <AlertCircle className="h-3 w-3 flex-shrink-0" />
                {addParticipantError}
              </p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => void handleAddParticipant()}
                disabled={addKind === 'voice' ? !selectedVoiceId : !selectedUserId}
                className="px-3 py-2 bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {addKind === 'voice' ? 'Invite for context' : 'Add'}
              </button>
              <button onClick={resetAddPicker} className="px-3 py-2 border border-[var(--rule)] hover:bg-[var(--surface)]">Cancel</button>
            </div>
          </div>
        )}

        <div className="border border-[var(--rule)] overflow-hidden">
          <table className="min-w-full divide-y divide-[var(--rule)]">
            <thead className="bg-[var(--surface)]">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Email</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Role</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-[var(--text-ter)] uppercase"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--rule)]">
              {displayParticipantIds.filter(id => sessionDirectIds.has(id)).map((pid) => {
                const user = users.find(u => u.id === pid);
                const isFacilitator = pid === session.facilitatorId;
                return (
                  <tr key={pid} className="hover:bg-[var(--surface)]">
                    <td className="px-4 py-2 text-sm text-[var(--text-pri)]">{user?.name ?? pid.slice(0, 8) + '…'}</td>
                    <td className="px-4 py-2 text-sm text-[var(--text-ter)]">{user?.email ?? '—'}</td>
                    <td className="px-4 py-2">
                      {isFacilitator && <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full mr-1">Facilitator</span>}
                      <span className="text-xs bg-[var(--hover-tint)] text-[var(--text-sec)] px-2 py-0.5 rounded-full">Session</span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      {!peopleLocked && (
                        <button onClick={() => handleRemoveParticipant(pid)} className="text-[var(--coral)] hover:text-[var(--coral)]">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {displayParticipantIds.filter(id => sessionDirectIds.has(id)).length === 0 && !isTrackLinked && (
                <tr><td colSpan={4} className="px-4 py-4 text-sm text-[var(--text-ter)] text-center">No participants yet. Add people above.</td></tr>
              )}
              {displayParticipantIds.filter(id => sessionDirectIds.has(id)).length === 0 && isTrackLinked && (
                <tr><td colSpan={4} className="px-4 py-4 text-sm text-[var(--text-ter)] text-center">All participants are inherited from the track. Add session-specific people above if needed.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Groups */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-[var(--text-sec)]">Groups</h4>
            {!peopleLocked && (
              <button type="button" onClick={() => setShowAddGroup(true)} className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1">
                <Plus className="h-3 w-3" /> Add group
              </button>
            )}
          </div>

          {showAddGroup && !peopleLocked && (
            <div className="mb-3 space-y-2 p-3 border-2 border-[var(--mustard)] bg-[var(--mustard-dim)]">
              <FloatingSelect
                value={selectedGroupId}
                onChange={setSelectedGroupId}
                placeholder="Select a group…"
                options={availableGroupsForAdd.map(g => ({ value: g.id, label: g.displayName ?? g.name }))}
                className="w-full"
              />
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    if (selectedGroupId) {
                      await addSessionParticipantGroup({ sessionId: session.id, groupId: selectedGroupId, role: 'contributor', addedBy: userId });
                      setSelectedGroupId(''); setShowAddGroup(false);
                    }
                  }}
                  disabled={!selectedGroupId}
                  className="px-3 py-2 bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >Add</button>
                <button onClick={() => { setShowAddGroup(false); setSelectedGroupId(''); }} className="px-3 py-2 border border-[var(--rule)] hover:bg-[var(--surface)]">Cancel</button>
              </div>
            </div>
          )}

          {sessionParticipantGroups.length > 0 ? (
            <div className="border border-[var(--rule)] overflow-hidden">
              <table className="min-w-full divide-y divide-[var(--rule)]">
                <tbody className="divide-y divide-[var(--rule)]">
                  {sessionParticipantGroups.map((pg) => (
                    <tr key={pg.id} className="hover:bg-[var(--surface)]">
                      <td className="px-4 py-2 text-sm text-[var(--text-pri)]">{groupDisplayName(pg.groupId)}</td>
                      <td className="px-4 py-2 text-right">
                        {!peopleLocked && (
                          <button onClick={() => removeSessionParticipantGroup(pg.id)} className="text-[var(--coral)] hover:text-[var(--coral)]">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-[var(--text-ter)]">No groups added.</p>
          )}
        </div>
      </div>

      <SessionVoicesPanel
        session={session}
        humanRosterSize={displayParticipantIds.length}
        userId={userId}
        itemInputs={itemInputs}
        hideAddControls={voicesEnabled}
        onReady={(api) => {
          voicesApiRef.current = api;
        }}
      />
    </div>
  );
}

// Criteria Tab
export function CriteriaTab({ session, effectiveParticipantCount }: TabProps & { effectiveParticipantCount?: number }) {
  const criteriaRecommendationEnabled = useAiCapability('criteria_recommendation');
  const attributes = useAttributesStore(state => state.attributes);
  const addCriterion = useSessionStore(state => state.addCriterion);
  const removeCriterion = useSessionStore(state => state.removeCriterion);
  const updateCriterionWeight = useSessionStore(state => state.updateCriterionWeight);
  const transitionSessionState = useSessionStore(state => state.transitionSessionState);
  const allCycleConfigs = useAttributeGovernanceStore(s => s.cycleConfigs);
  const fetchTrackConfigs = useAttributeGovernanceStore(s => s.fetchTrackConfigs);
  const getItemById = useItemsStore(state => state.getItemById);
  const trackConfigs = useMemo(
    () => session.trackId ? allCycleConfigs?.filter(c => c.trackId === session.trackId) ?? [] : [],
    [session.trackId, allCycleConfigs]
  );

  useEffect(() => {
    if (session.trackId) fetchTrackConfigs(session.trackId);
  }, [session.trackId, fetchTrackConfigs]);
  
  const selectedCriteria = session.criteriaConfig?.criteriaIds ?? [];
  const weights = session.criteriaConfig?.weights ?? {};
  
  const availableAttributes = attributes.filter(a => !selectedCriteria.includes(a.key));
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [criteriaRecommendation, setCriteriaRecommendation] = useState<RecommendCriteriaResult | null>(null);
  const [isRecommending, setIsRecommending] = useState(false);
  // Why these criteria (985): optional, carried onto the criteria_finalized
  // Decision when the criteria are locked.
  const [lockRationale, setLockRationale] = useState<RationaleValue>(EMPTY_RATIONALE);
  const participantCount = effectiveParticipantCount ?? session.participantIds?.length ?? 0;
  const isTrackLinked = session.sessionContextMode === 'cycle_linked';

  const handleSuggestCriteria = async () => {
    setIsRecommending(true);
    setCriteriaRecommendation(null);
    try {
      const itemIds = session.sessionContextMode === 'isolated'
        ? (session.isolatedItems ?? []).map((text: string) => ({ id: text, title: text }))
        : (session.itemIds ?? []).map((id: string) => {
            const item = getItemById(id);
            return { id, title: item?.title ?? id, description: (item as { description?: string } | undefined)?.description };
          });
      const result = await recommendCriteria(
        itemIds.slice(0, 30),
        availableAttributes.map(a => ({ key: a.key, label: a.label, description: (a as { description?: string }).description })),
        session.selectedToolId
      );
      setCriteriaRecommendation(result);
    } catch {
      // silently fail — the button will re-enable
    } finally {
      setIsRecommending(false);
    }
  };

  const handleFinalize = async () => {
    setTransitionError(null);
    const result = await transitionSessionState(session.id, 'CRITERIA_FINALIZED', session.facilitatorId, {
      participantCount,
      rationale: toRationaleParts(lockRationale),
    });
    if (!result?.success) {
      setTransitionError(result?.error ?? 'Failed to lock criteria');
      return;
    }
    setLockRationale(EMPTY_RATIONALE);
  };
  
  // Reopen targets the one step back the server's state machine allows from
  // the current state (RUN_COMPLETE -> CRITERIA_FINALIZED, CRITERIA_FINALIZED
  // -> DRAFT); PUBLISHED can't be reopened. See lib/sessions/sessionReopen.
  const reopenTarget = getSessionReopenTarget(session.state);
  const handleReopen = async () => {
    setTransitionError(null);
    if (!reopenTarget) {
      setTransitionError(getSessionReopenRefusal(session.state) ?? 'This session cannot be reopened.');
      return;
    }
    const result = await transitionSessionState(session.id, reopenTarget, session.facilitatorId, { participantCount });
    if (!result?.success) setTransitionError(result?.error ?? 'Failed to reopen the session');
  };
  
  const { steps: sessionSteps, trackWeights } = useTrackStepLocks(session.trackId);
  const criteriaLocked = sessionSteps?.S3.disposition === 'locked';
  const isEditable = session.state === 'DRAFT';
  // Shares of this session's whole, whatever scale its record uses (shares of 1,
  // or the Event Designer's points out of 100).
  const weightTotal = selectedCriteria.reduce((s, id) => s + (weights[id] ?? 0), 0);
  const weightPercent = (w: number) => (weightTotal > 0 ? formatWeight((w / weightTotal) * 100) : formatWeight(100 / Math.max(1, selectedCriteria.length)));
  // Finalizing stays open: a session on a locked track still finalizes what it was given.
  const canChangeCriteria = isEditable && !criteriaLocked;
  
  return (
    <div className="space-y-6">
      {transitionError && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-3 text-sm text-[var(--coral)]">
          {transitionError}
        </div>
      )}
      {session.state === 'DRAFT' && criteriaLocked && (
        <div className="bg-[var(--green-dim)] border border-[var(--green)] p-4 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
          <p className="font-medium">Criteria are set by the track and are locked.</p>
          <p className="mt-1">You can run the session from the Run tab—no need to lock criteria here.</p>
        </div>
      )}
      {session.state !== 'DRAFT' && (
        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[var(--mustard)] font-medium">Criteria are finalized</p>
            <p className="text-[var(--mustard)] text-sm mt-1">
              {reopenTarget === 'DRAFT'
                ? 'To modify criteria, you need to reopen the session to draft state.'
                : reopenTarget
                  ? 'To modify criteria, reopen the session one step (to criteria finalized), then reopen it to draft.'
                  : getSessionReopenRefusal(session.state)}
            </p>
            {reopenTarget && (
              <button
                onClick={() => void handleReopen()}
                className="mt-2 text-sm text-[var(--mustard)] hover:text-[var(--mustard)] underline"
              >
                {SESSION_REOPEN_LABEL[reopenTarget] ?? 'Reopen'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Inherited from track */}
      {isTrackLinked && trackConfigs.length > 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[var(--text-pri)]">From track (inherited)</h3>
            <span className="text-xs text-[var(--text-ter)]">{trackConfigs.length} criteria</span>
          </div>
          <div className="border border-[var(--rule)] overflow-hidden">
            <table className="min-w-full divide-y divide-[var(--rule)]">
              <thead className="bg-[var(--surface)]">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Attribute</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Status</th>
                  {trackWeights && (
                    <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Weight</th>
                  )}
                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)]">
                {trackConfigs.map((config) => {
                  const attr = attributes.find(a => a.key === config.attributeKey);
                  return (
                    <tr key={config.id} className="hover:bg-[var(--surface)]">
                      <td className="px-4 py-2 text-sm text-[var(--text-pri)]">{attr?.label ?? config.attributeKey}</td>
                      <td className="px-4 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${config.rangeStatus === 'accepted' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--mustard-dim)] text-[var(--mustard)]'}`}>
                          {config.rangeStatus ?? 'draft'}
                        </span>
                      </td>
                      {trackWeights && (
                        <td className="px-4 py-2 text-sm tabular-nums text-[var(--text-pri)]">
                          {trackWeights[config.attributeKey] != null ? `${formatWeight(trackWeights[config.attributeKey] * 100)}%` : ''}
                        </td>
                      )}
                      <td className="px-4 py-2">
                        <span className="text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] px-2 py-0.5 rounded-full">Inherited</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-4">
          {isTrackLinked ? 'Session-specific criteria' : 'Selected Criteria'}
        </h3>
        {/* Weights follow the track (migration 1048): locked, the server holds this
            session to the track's, so no weight control is drawn (§2.8). */}
        {criteriaLocked && trackWeights && (
          <p className="-mt-2 mb-4 text-sm text-[var(--text-sec)]">
            Weights are set and locked on the track. This session scores with them.
          </p>
        )}
        {!criteriaLocked && trackWeights && selectedCriteria.some((id) => id in trackWeights) && (
          <p className="-mt-2 mb-4 text-sm text-[var(--text-sec)]">
            This session started from the track&apos;s weights ({describeTrackWeights(trackWeights, (key) => attributes.find((a) => a.key === key)?.label ?? key)}).
            {canChangeCriteria ? ' You can change them for this session while the track\'s criteria are open.' : ''}
          </p>
        )}
        
        {selectedCriteria.length === 0 ? (
          <div className="text-center py-8 text-[var(--text-ter)]">
            <Scale className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)]" />
            <p>No criteria selected yet</p>
            <p className="text-sm">Add criteria from the available list below</p>
          </div>
        ) : (
          <div className="space-y-3">
            {selectedCriteria.map((criterionId) => {
              const attr = attributes.find(a => a.key === criterionId);
              const weight = weights[criterionId] ?? (1 / selectedCriteria.length);

              return (
                <div key={criterionId} className="flex items-center gap-4 p-3 bg-[var(--surface)]">
                  <div className="flex-1">
                    <div className="font-medium text-[var(--text-pri)]">
                      {attr?.label || criterionId}
                    </div>
                    {attr?.description && (
                      <div className="text-sm text-[var(--text-ter)]">{attr.description}</div>
                    )}
                  </div>

                  {criteriaLocked ? (
                    <div className="text-sm tabular-nums font-semibold text-[var(--text-pri)]" aria-label={`${attr?.label || criterionId} weight`}>
                      {weightPercent(weight)}%
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-[var(--text-sec)]">Weight:</label>
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.05}
                        value={Math.round(weight * 100) / 100}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val)) updateCriterionWeight(session.id, criterionId, val);
                        }}
                        disabled={!canChangeCriteria}
                        className="w-20 px-2 py-1 text-sm border border-[var(--rule)] rounded disabled:bg-[var(--hover-tint)] disabled:text-[var(--text-ter)]"
                      />
                    </div>
                  )}

                  {canChangeCriteria && (
                    <button
                      onClick={() => removeCriterion(session.id, criterionId)}
                      className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              );
            })}
            {!criteriaLocked && (() => {
              const sum = selectedCriteria.reduce((s, id) => s + (weights[id] ?? 0), 0);
              const rounded = Math.round(sum * 100) / 100;
              return (
                <div className={`text-right text-xs font-medium px-3 py-1 ${rounded === 1 ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'}`}>
                  Total: {rounded} {rounded !== 1 && '(must equal 1.0)'}
                </div>
              );
            })()}
          </div>
        )}
      </div>
      
      {canChangeCriteria && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[var(--text-pri)]">Available Attributes</h3>
            {availableAttributes.length > 0 && criteriaRecommendationEnabled && (
              <button
                onClick={handleSuggestCriteria}
                disabled={isRecommending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] bg-[var(--slate-dim)] border border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors disabled:opacity-60"
                title="Ask AI to suggest which attributes best differentiate your items"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {isRecommending ? 'Thinking…' : 'Suggest Criteria'}
              </button>
            )}
          </div>

          {/* AI recommendations panel */}
          {criteriaRecommendation && criteriaRecommendation.recommendations.length > 0 && (
            <div className="mb-4 p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
                  <span className="text-sm font-semibold text-[var(--text-sec)]">AI recommendations</span>
                </div>
                <button
                  onClick={() => setCriteriaRecommendation(null)}
                  className="p-0.5 hover:bg-[var(--slate-dim)] rounded"
                >
                  <X className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                </button>
              </div>
              {criteriaRecommendation.reasoning && (
                <p className="text-xs text-[var(--text-sec)] mb-3">{criteriaRecommendation.reasoning}</p>
              )}
              <div className="space-y-2">
                {criteriaRecommendation.recommendations.map((rec) => {
                  const alreadySelected = selectedCriteria.includes(rec.attributeKey);
                  const confidenceBadge: Record<string, string> = {
                    high: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
                    medium: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
                    low: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
                  };
                  return (
                    <div key={rec.attributeKey} className="flex items-start gap-3 p-2.5 bg-[var(--bg-raised)] border border-[var(--slate)]">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[var(--text-pri)]">{rec.attributeLabel}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${confidenceBadge[rec.confidence]}`}>
                            {rec.confidence}
                          </span>
                        </div>
                        <p className="text-xs text-[var(--text-sec)] mt-0.5">{rec.rationale}</p>
                      </div>
                      {alreadySelected ? (
                        <span className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] font-medium mt-1 flex-shrink-0">Added</span>
                      ) : (
                        <button
                          onClick={() => addCriterion(session.id, rec.attributeKey)}
                          className="flex-shrink-0 text-xs px-2 py-1 bg-[var(--slate)] text-white rounded hover:bg-[var(--slate)] transition-colors"
                        >
                          Add
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-[var(--text-sec)] mt-2 text-center">Advisory only — you decide which criteria to use.</p>
            </div>
          )}

          {availableAttributes.length === 0 ? (
            <p className="text-[var(--text-ter)] text-center py-4">
              All available attributes have been selected as criteria
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {availableAttributes.map((attr) => (
                <button
                  key={attr.key}
                  onClick={() => addCriterion(session.id, attr.key)}
                  className="flex items-center gap-3 p-3 text-left border border-[var(--rule)] hover:border-primary-300 hover:bg-primary-50 transition-colors"
                >
                  <Plus className="h-4 w-4 text-[var(--text-ter)]" />
                  <div>
                    <div className="font-medium text-[var(--text-pri)]">{attr.label}</div>
                    {attr.description && (
                      <div className="text-sm text-[var(--text-ter)] truncate">{attr.description}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      
      {isEditable && (selectedCriteria.length > 0 || (isTrackLinked && trackConfigs.length > 0)) && (
        <div className="space-y-3">
          <RationaleFields
            value={lockRationale}
            onChange={setLockRationale}
            title="Why these criteria"
          />
          <div className="flex justify-end">
          <button
            onClick={handleFinalize}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            <CheckCircle className="h-4 w-4" />
            {LOCK_CRITERIA_LABEL}
          </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** How many of `incoming` the list gains, and how many it already had. A typed item is its text. */
function addedAndRepeated(current: readonly string[], incoming: readonly string[]) {
  const added = uniqueLines([...current, ...incoming]).length - uniqueLines(current).length;
  return { added, repeated: incoming.length - added };
}

function repeatedLinesNote(repeated: number): string {
  if (repeated === 0) return '';
  return ` ${repeated} repeated line${repeated === 1 ? ' was' : 's were'} left out: each item is listed once.`;
}

// Items Tab — isolated mode: free-form text entry
function IsolatedItemsTab({ session }: TabProps) {
  const liveSession = useSessionStore(state => state.sessions.find(s => s.id === session.id));
  const setIsolatedItems = useSessionStore(state => state.setIsolatedItems);

  const currentItems = useMemo(
    () => liveSession?.isolatedItems ?? session.isolatedItems ?? [],
    [liveSession?.isolatedItems, session.isolatedItems]
  );
  const [draft, setDraft] = useState('');
  const [isEditing, setIsEditing] = useState(currentItems.length === 0);
  const [importExportMessage, setImportExportMessage] = useState<{
    type: 'success' | 'error' | 'info';
    text: string;
  } | null>(null);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && textAreaRef.current) textAreaRef.current.focus();
  }, [isEditing]);

  const exportRows = useMemo(
    () =>
      currentItems.map((label, index) => ({
        position: index + 1,
        label,
      })),
    [currentItems]
  );

  const buildFilename = useCallback(
    (extension: 'csv' | 'xlsx') =>
      `${session.name.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'isolated_session_items'}_${new Date()
        .toISOString()
        .split('T')[0]}.${extension}`,
    [session.name]
  );

  const getImportedLabels = useCallback((rows: Array<Record<string, string | number | undefined>>) => {
    const getValue = (row: Record<string, string | number | undefined>, candidateKeys: string[]) => {
      for (const candidateKey of candidateKeys) {
        const match = Object.keys(row).find((key) => key.toLowerCase().trim() === candidateKey.toLowerCase());
        if (!match) continue;
        const value = row[match];
        if (value != null && String(value).trim() !== '') {
          return String(value).trim();
        }
      }
      return '';
    };

    return rows
      .map((row) => {
        const preferredValue = getValue(row, ['label', 'title', 'name', 'item', 'summary', 'column_0', '0']);
        if (preferredValue) return preferredValue;
        const firstNonEmpty = Object.values(row).find((value) => value != null && String(value).trim() !== '');
        return firstNonEmpty ? String(firstNonEmpty).trim() : '';
      })
      .filter(Boolean);
  }, []);

  const handleFileSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';

      const isExcel = /\.(xlsx|xls)$/i.test(file.name);
      const isCsv = /\.csv$/i.test(file.name);
      if (!isExcel && !isCsv) {
        setImportExportMessage({ type: 'error', text: 'Please choose a .csv, .xlsx, or .xls file.' });
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        try {
          const content = reader.result as string | ArrayBuffer;
          let rows: Array<Record<string, string | number | undefined>>;

          if (isExcel && typeof content !== 'string') {
            const workbook = XLSX.read(content, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            rows = XLSX.utils.sheet_to_json(firstSheet, { defval: '' }) as Array<
              Record<string, string | number | undefined>
            >;
          } else {
            const text = typeof content === 'string' ? content : new TextDecoder().decode(content as ArrayBuffer);
            rows = parseCSV(text, { hasHeaders: true }).rows;
          }

          const importedLabels = getImportedLabels(rows);
          if (importedLabels.length === 0) {
            setImportExportMessage({
              type: 'info',
              text: 'No importable item labels were found. Add a label, title, or name column.',
            });
            return;
          }

          const added = addedAndRepeated(currentItems, importedLabels);
          setIsolatedItems(session.id, [...currentItems, ...importedLabels]);
          setDraft('');
          setIsEditing(false);
          setImportExportMessage({
            type: 'success',
            text: `Imported ${added.added} item${added.added === 1 ? '' : 's'} from ${file.name}.${repeatedLinesNote(added.repeated)}`,
          });
        } catch (error) {
          setImportExportMessage({
            type: 'error',
            text: error instanceof Error ? error.message : 'Could not parse the selected file.',
          });
        }
      };

      if (isExcel) reader.readAsArrayBuffer(file);
      else reader.readAsText(file, 'UTF-8');
    },
    [currentItems, getImportedLabels, session.id, setIsolatedItems]
  );

  const handleExportCsv = useCallback(() => {
    if (exportRows.length === 0) {
      setImportExportMessage({ type: 'info', text: 'Add isolated-session items before exporting.' });
      return;
    }

    const csvContent = generateCSV(exportRows, {
      fields: ['position', 'label'],
      includeHeaders: true,
    });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = buildFilename('csv');
    link.click();
    URL.revokeObjectURL(url);

    setImportExportMessage({
      type: 'success',
      text: `Exported ${exportRows.length} item${exportRows.length === 1 ? '' : 's'} to CSV.`,
    });
  }, [buildFilename, exportRows]);

  const handleExportExcel = useCallback(() => {
    if (exportRows.length === 0) {
      setImportExportMessage({ type: 'info', text: 'Add isolated-session items before exporting.' });
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Items');
    XLSX.writeFile(workbook, buildFilename('xlsx'));

    setImportExportMessage({
      type: 'success',
      text: `Exported ${exportRows.length} item${exportRows.length === 1 ? '' : 's'} to Excel.`,
    });
  }, [buildFilename, exportRows]);

  const handleSave = () => {
    const parsed = draft
      .split(/[\n,]+/)
      .map(s => s.trim())
      .filter(Boolean);
    if (parsed.length > 0) {
      const { repeated } = addedAndRepeated(currentItems, parsed);
      setIsolatedItems(session.id, [...currentItems, ...parsed]);
      setDraft('');
      setIsEditing(false);
      setImportExportMessage(repeated > 0 ? { type: 'info', text: repeatedLinesNote(repeated).trim() } : null);
    }
  };

  const handleRemove = (index: number) => {
    const next = currentItems.filter((_, i) => i !== index);
    setIsolatedItems(session.id, next);
  };

  return (
    <div className="space-y-6">
      {session.sessionContextMode === 'isolated' && (
      <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-[var(--mustard)] font-medium">Isolated session</p>
          <p className="text-[var(--mustard)] text-sm mt-1">
            Items in an isolated session are free-form labels — they are not linked to the system item registry.
          </p>
        </div>
      </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        className="hidden"
        onChange={handleFileSelected}
      />

      {importExportMessage && (
        <div
          className={` border px-4 py-3 text-sm ${
            importExportMessage.type === 'success'
              ? 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]'
              : importExportMessage.type === 'error'
              ? 'bg-[var(--coral-dim)] border-[var(--coral)] text-[var(--coral)]'
              : 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]'
          }`}
        >
          {importExportMessage.text}
        </div>
      )}

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-pri)]">Items to Prioritize</h3>
          <p className="text-sm text-[var(--text-ter)]">
            {currentItems.length} item{currentItems.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Items
            </button>
          )}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] transition-colors"
          >
            <ArrowDownToLine className="h-4 w-4" />
            Import from Excel/CSV
          </button>
          <button
            onClick={handleExportCsv}
            disabled={currentItems.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--bg-raised)] text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)] hover:bg-[var(--green-dim)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowUpFromLine className="h-4 w-4" />
            Export CSV
          </button>
          <button
            onClick={handleExportExcel}
            disabled={currentItems.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--bg-raised)] text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)] hover:bg-[var(--green-dim)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowUpFromLine className="h-4 w-4" />
            Export Excel
          </button>
        </div>
      </div>

      {isEditing && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Enter items to prioritize
            </label>
            <p className="text-xs text-[var(--text-ter)] mb-2">
              One item per line, or separate with commas
            </p>
            <textarea
              ref={textAreaRef}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder={"Beach Trip\nMountain Trip\nCity Trip"}
              rows={6}
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
            />
          </div>
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => { setDraft(''); setIsEditing(currentItems.length === 0); }}
              className="px-4 py-2 text-sm text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={draft.trim().length === 0}
              className="px-4 py-2 text-sm bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {currentItems.length > 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
          <table className="w-full">
            <thead className="bg-[var(--surface)] border-b border-[var(--rule)]">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-[var(--text-sec)] w-10">#</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[var(--text-sec)]">Item</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--rule)]">
              {currentItems.map((label, idx) => (
                <tr key={idx} className="hover:bg-[var(--surface)]">
                  <td className="px-4 py-3 text-sm text-[var(--text-ter)]">{idx + 1}</td>
                  <td className="px-4 py-3 text-sm font-medium text-[var(--text-pri)]">{label}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleRemove(idx)}
                      className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {currentItems.length === 0 && !isEditing && (
        <div className="text-center py-12 bg-[var(--bg-raised)] border border-[var(--rule)]">
          <ListChecks className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[var(--text-pri)] mb-2">No items yet</h3>
          <p className="text-[var(--text-sec)] mb-4">Type in the items you want to prioritize</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Items
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] transition-colors"
            >
              <ArrowDownToLine className="h-4 w-4" />
              Import from Excel/CSV
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Items Tab — cycle_linked and app_linked modes (existing item picker)
function LinkedItemsTab({ session }: TabProps) {
  const itemHref = useItemHref();
  const altitudeText = useAltitudeText();
  const items = useItemsStore(state => state.items);
  const addItem = useSessionStore(state => state.addItem);
  const removeItem = useSessionStore(state => state.removeItem);
  const { steps: sessionSteps } = useTrackStepLocks(session.trackId);
  const itemsLocked = sessionSteps?.S2.disposition === 'locked';
  const recomputeAllEligibility = useSessionStore(state => state.recomputeAllEligibility);
  const updateSession = useSessionStore(state => state.updateSession);
  const liveSession = useSessionStore(state => state.sessions.find(s => s.id === session.id));
  const itemPoolsRaw = useItemPoolsStore(state => state.itemPools);
  const itemPools = useMemo(() => itemPoolsRaw ?? [], [itemPoolsRaw]);

  // Item Filter — grid cell counts for the altitude × category selector
  const gridCellCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const item of items) {
      const level = item.altitudeLevel;
      const cat = item.itemCategory;
      if (level != null && cat) {
        const key = `${level}-${cat}`;
        counts[key] = (counts[key] ?? 0) + 1;
      }
    }
    return counts;
  }, [items]);

  const isTrackLinked = session.sessionContextMode === 'cycle_linked';
  const trackPool = useMemo(() => {
    if (!isTrackLinked || !session.trackId || !itemPools?.length) return null;
    return itemPools.find((p: { trackId?: string }) => p.trackId === session.trackId) ?? null;
  }, [isTrackLinked, session.trackId, itemPools]);
  const trackPoolItemIds = useMemo(() => trackPool?.priorityIds ?? [], [trackPool]);

  // Resolve active cell filter from the live session state (preferred) or the session prop.
  const activeGridCells = useMemo(
    () => liveSession?.selectedGridCells ?? session.selectedGridCells ?? [],
    [liveSession?.selectedGridCells, session.selectedGridCells],
  );

  // Returns true when no cell filter is active, or the item matches at least one selected cell.
  const matchesCellFilter = useCallback(
    (item: { altitudeLevel?: number | string | null; itemCategory?: string | null }) => {
      if (activeGridCells.length === 0) return true;
      const level = item.altitudeLevel != null ? Number(item.altitudeLevel) : null;
      const cat = item.itemCategory ?? null;
      return activeGridCells.some(
        cell => cell.altitudeLevel === level && cell.itemCategory === cat
      );
    },
    [activeGridCells],
  );

  const trackPoolItems = useMemo(
    () => items.filter(c => trackPoolItemIds.includes(c.id) && matchesCellFilter(c)),
    [items, trackPoolItemIds, matchesCellFilter],
  );

  const [showAddModal, setShowAddModal] = useState(false);
  const [justAdded, setJustAdded] = useState<Set<string>>(new Set());
  const [addError, setAddError] = useState<string | null>(null);
  const [showAltitudeTray, setShowAltitudeTray] = useState(false);
  const updateItem = useItemsStore(state => state.updateItem);

  useEffect(() => {
    const onBlocked = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (d?.action === 'addItem') setAddError(d?.message ?? 'Could not add item.');
    };
    const onSyncError = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (d?.action === 'addItem') setAddError(d?.message ?? 'Failed to save. Try again.');
    };
    window.addEventListener('configuration-locked', onBlocked);
    window.addEventListener('authorization-denied', onBlocked);
    window.addEventListener('session-sync-error', onSyncError);
    return () => {
      window.removeEventListener('configuration-locked', onBlocked);
      window.removeEventListener('authorization-denied', onBlocked);
      window.removeEventListener('session-sync-error', onSyncError);
    };
  }, []);

  const currentItemIds = liveSession?.itemIds ?? session.itemIds ?? [];
  const sessionItemIds = new Set(currentItemIds);
  // Filter the picker to items not already in the session, not in terminal states, and — when
  // a grid cell filter is active — only items that match the selected altitude × category cells.
  const availableItems = items.filter(
    c =>
      !sessionItemIds.has(c.id) &&
      c.detailedState !== 'RAW' &&
      c.detailedState !== 'ARCHIVED' &&
      matchesCellFilter(c)
  );
  
  const sessionItemsList = items.filter(c => sessionItemIds.has(c.id));
  
  const altitudeValidation = useMemo(() => {
    const itemsWithAltitude = sessionItemsList.map(c => ({
      id: c.id,
      title: c.title,
      altitude: c.altitude,
      // Pass the current numeric altitudeLevel so items with only this field set
      // are not incorrectly reported as missing altitude.
      altitudeLevel: c.altitudeLevel ? (Number(c.altitudeLevel) as 1|2|3|4|5) : undefined,
    }));
    return validateSessionCandidatesAltitude(itemsWithAltitude);
  }, [sessionItemsList]);
  
  const eligibilityBadge = (status: ItemEligibilityStatus) => {
    const config: Record<ItemEligibilityStatus, { label: string; className: string }> = {
      ELIGIBLE: { label: 'Eligible', className: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
      ELIGIBLE_WITH_GAPS: { label: 'Has Gaps', className: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
      INELIGIBLE_STRUCTURAL: { label: 'Ineligible', className: 'bg-[var(--coral-dim)] text-[var(--coral)]' },
    };
    const { label, className } = config[status];
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${className}`}>
        {label}
      </span>
    );
  };
  
  return (
    <div className="space-y-6">
      {/* Item Filter — moved from Setup tab. Restrict by altitude level × item category. */}
      {/* Always show filter section — was: (session.selectedGridCells?.length > 0 || true) */}
      {(
        <details className="group" open={!!session.selectedGridCells?.length}>
          <summary className="flex cursor-pointer items-center justify-between border border-[var(--rule)] bg-[var(--surface)] px-4 py-3 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors select-none list-none">
            <span className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-[var(--text-ter)]" />
              Item Filter
              {(session.selectedGridCells?.length ?? 0) > 0 && (
                <span className="ml-1 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
                  {session.selectedGridCells?.length} cell{session.selectedGridCells?.length !== 1 ? 's' : ''} active
                </span>
              )}
            </span>
            <ChevronDown className="h-4 w-4 text-[var(--text-ter)] transition-transform group-open:rotate-180" />
          </summary>
          <div className="mt-3 px-1 pb-1">
            <p className="text-xs text-[var(--text-ter)] mb-3">
              Restrict this session to items at specific altitude levels and categories. Only items in selected cells will be eligible.
            </p>
            <GridCellSelector
              value={session.selectedGridCells || []}
              onChange={(cells: GridCellFilter[]) => {
                updateSession(session.id, { selectedGridCells: cells });
              }}
              allowMultiple
              cellCounts={gridCellCounts}
            />
          </div>
        </details>
      )}

      {addError && (
        <div className="flex items-start gap-3 p-4 border border-[var(--coral)] bg-[var(--coral-dim)]">
          <AlertCircle className="h-5 w-5 flex-shrink-0 text-[var(--coral)] mt-0.5" />
          <p className="flex-1 text-sm text-[var(--coral)]">{addError}</p>
          <button
            type="button"
            onClick={() => setAddError(null)}
            className="p-1 text-[var(--coral)] hover:text-[var(--coral)] rounded"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {altitudeValidation.warningLevel !== 'none' && sessionItemsList.length > 0 && (
        <div className={`p-4  border flex items-start gap-3 ${
          altitudeValidation.warningLevel === 'error'
            ? 'bg-[var(--coral-dim)] border-[var(--coral)]'
            : altitudeValidation.warningLevel === 'warning'
            ? 'bg-[var(--mustard-dim)] border-[var(--mustard)]'
            : 'bg-[var(--slate-dim)] border-[var(--slate)]'
        }`}>
          <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
            altitudeValidation.warningLevel === 'error'
              ? 'text-[var(--coral)]'
              : altitudeValidation.warningLevel === 'warning'
              ? 'text-[var(--mustard)]'
              : 'text-[var(--text-ter)]'
          }`} />
          <div className="flex-1">
            <p className={`font-medium ${
              altitudeValidation.warningLevel === 'error'
                ? 'text-[var(--coral)]'
                : altitudeValidation.warningLevel === 'warning'
                ? 'text-[var(--mustard)]'
                : 'text-[var(--text-ter)]'
            }`}>
              {altitudeValidation.message}
            </p>
            <p className={`mt-0.5 text-sm ${
              altitudeValidation.warningLevel === 'error' ? 'text-[var(--coral)]'
                : altitudeValidation.warningLevel === 'warning' ? 'text-[var(--mustard)]'
                : 'text-[var(--text-ter)]'
            }`}>
              All items must have an altitude set before prioritization.
            </p>
          </div>
          {altitudeValidation.warningLevel === 'error' && (
            <button
              onClick={() => setShowAltitudeTray(true)}
              className="shrink-0 text-sm font-medium px-3 py-1.5 bg-[var(--coral)] text-white hover:bg-[var(--coral)] transition-colors"
            >
              Set Altitude
            </button>
          )}
        </div>
      )}

      {/* ── Altitude tray ── */}
      {showAltitudeTray && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="flex-1 bg-black/30"
            onClick={() => setShowAltitudeTray(false)}
          />
          {/* Tray */}
          <div className="w-96 bg-[var(--bg-raised)] shadow-xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rule)]">
              <div>
                <h2 className="text-base font-semibold text-[var(--text-pri)]">Set Altitude</h2>
                <p className="text-xs text-[var(--text-ter)] mt-0.5">
                  Assign an altitude to each item below
                </p>
              </div>
              <button
                onClick={() => setShowAltitudeTray(false)}
                className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {sessionItemsList
                .filter(item => !item.altitude && !item.altitudeLevel)
                .map(item => (
                  <div key={item.id} className="bg-[var(--surface)] border border-[var(--rule)] p-3">
                    <p className="text-sm font-medium text-[var(--text-pri)] mb-2 line-clamp-2">{item.title}</p>
                    <select
                      defaultValue=""
                      onChange={e => {
                        const val = e.target.value;
                        if (val) updateItem(item.id, { altitudeLevel: Number(val) as 1 | 2 | 3 | 4 | 5 });
                      }}
                      className="w-full text-sm border border-[var(--rule)] px-3 py-2 bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    >
                      <option value="" disabled>Select altitude…</option>
                      {([5, 4, 3, 2, 1] as const).map(level => (
                        <option key={level} value={level}>
                          {altitudeText.format(level, null, { short: true })}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              {sessionItemsList.filter(item => !item.altitude && !item.altitudeLevel).length === 0 && (
                <div className="text-center py-8 text-sm text-[var(--text-ter)]">
                  All items have an altitude set.
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-[var(--rule)]">
              <button
                onClick={() => setShowAltitudeTray(false)}
                className="w-full py-2 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      
      {isTrackLinked && trackPoolItems.length === 0 && (
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4 text-sm text-[var(--text-sec)]">
          <p className="font-medium">Items are set by the track's item pool.</p>
          <p className="mt-1 text-[var(--text-sec)]">The track's item pool is currently empty. Add items to the track to populate this session.</p>
        </div>
      )}

      {isTrackLinked && trackPoolItems.length > 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[var(--text-pri)]">From track pool (inherited)</h3>
            <div className="flex items-center gap-2">
              {activeGridCells.length > 0 && (
                <span className="text-xs text-primary-600 bg-primary-50 border border-primary-200 rounded-full px-2 py-0.5">
                  Filtered to {activeGridCells.length} cell{activeGridCells.length !== 1 ? 's' : ''}
                </span>
              )}
              <span className="text-xs text-[var(--text-ter)]">{trackPoolItems.length} item{trackPoolItems.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <div className="border border-[var(--rule)] overflow-hidden">
            <table className="min-w-full divide-y divide-[var(--rule)]">
              <thead className="bg-[var(--surface)]">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Item</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Altitude</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">State</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)]">
                {trackPoolItems.map((item) => (
                  <tr key={item.id} className="hover:bg-[var(--surface)]">
                    <td className="px-4 py-2 text-sm text-[var(--text-pri)]">{item.title}</td>
                    <td className="px-4 py-2 text-sm text-[var(--text-ter)]">
                      {altitudeText.format(item.altitudeLevel ?? item.altitude, (item as unknown as { itemCategory?: string }).itemCategory) ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-sm text-[var(--text-ter)]">{item.detailedState?.replace(/_/g, ' ') ?? '—'}</td>
                    <td className="px-4 py-2">
                      <span className="text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] px-2 py-0.5 rounded-full">Inherited</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-pri)]">
            {isTrackLinked ? 'Session-specific items' : 'Session Items'}
          </h3>
          <p className="text-sm text-[var(--text-ter)]">
            {currentItemIds.length} item{currentItemIds.length !== 1 ? 's' : ''} in this session
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => recomputeAllEligibility(session.id)}
            className="inline-flex items-center gap-2 px-3 py-2 text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh Eligibility
          </button>
          {!itemsLocked && (
            <button
              onClick={() => { setShowAddModal(true); setAddError(null); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Items
            </button>
          )}
        </div>
      </div>
      <TrackStepNote step={sessionSteps?.S2} />
      
      {currentItemIds.length === 0 ? (
        <div className="text-center py-12 bg-[var(--bg-raised)] border border-[var(--rule)]">
          <ListChecks className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[var(--text-pri)] mb-2">No items yet</h3>
          <p className="text-[var(--text-sec)] mb-4">
            {isTrackLinked
              ? 'Session-specific items can be added here. Items from the track pool are shown above.'
              : 'Add items to this session to begin prioritization'}
          </p>
          <button
            onClick={() => { setShowAddModal(true); setAddError(null); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Items
          </button>
        </div>
      ) : (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
          <table className="w-full">
            <thead className="bg-[var(--surface)] border-b border-[var(--rule)]">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-[var(--text-sec)]">Item</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[var(--text-sec)]">State</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[var(--text-sec)]">Eligibility</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[var(--text-sec)]">Missing</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--rule)]">
              {currentItemIds.map((itemId) => {
                const item = items.find(c => c.id === itemId);
                const eligibility = (liveSession ?? session).itemEligibility?.[itemId];
                
                if (!item) return (
                  <tr key={itemId} className="hover:bg-[var(--surface)] opacity-50">
                    <td colSpan={5} className="px-4 py-3 text-sm text-[var(--text-ter)] italic">Loading item…</td>
                  </tr>
                );
                
                return (
                  <tr key={itemId} className="hover:bg-[var(--surface)]">
                    <td className="px-4 py-3">
                      <Link 
                        to={itemHref(itemId)}
                        className="font-medium text-[var(--text-pri)] hover:text-primary-600"
                      >
                        {item.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text-sec)]">
                      {item.detailedState || item.lifecycle_state}
                    </td>
                    <td className="px-4 py-3">
                      {eligibility && eligibilityBadge(eligibility.status)}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text-ter)]">
                      {eligibility && (
                        eligibility.missingAttributes.length > 0 || eligibility.missingAcceptedValues.length > 0
                          ? [...eligibility.missingAttributes, ...eligibility.missingAcceptedValues].join(', ')
                          : '-'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {!itemsLocked && (
                        <button
                          onClick={() => removeItem(session.id, itemId)}
                          className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setShowAddModal(false); setJustAdded(new Set()); }} />
          <div className="relative bg-[var(--bg-raised)] shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-[var(--rule)]">
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">Add Items</h2>
              <p className="text-sm text-[var(--text-ter)] mt-1">
                {activeGridCells.length > 0
                  ? `Showing items matching the active cell filter (${activeGridCells.length} cell${activeGridCells.length !== 1 ? 's' : ''} selected)`
                  : 'Select items to add to this session'}
              </p>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {availableItems.length === 0 && justAdded.size === 0 ? (
                <p className="text-center text-[var(--text-ter)] py-8">
                  No more items available to add
                </p>
              ) : availableItems.length === 0 && justAdded.size > 0 ? (
                <p className="text-center text-[var(--text-ter)] py-8">
                  All available items have been added
                </p>
              ) : (
                <div className="space-y-2">
                  {availableItems.map((item) => {
                    const wasAdded = justAdded.has(item.id);
                    return (
                      <button
                        key={item.id}
                        onClick={async () => {
                          await addItem(session.id, item.id);
                          const updated = useSessionStore.getState().getSession(session.id);
                          if (updated?.itemIds.includes(item.id)) {
                            setJustAdded(prev => new Set(prev).add(item.id));
                          }
                        }}
                        disabled={wasAdded}
                        className={`w-full flex items-center justify-between p-3 text-left border  transition-colors ${
                          wasAdded
                            ? 'border-[var(--green)] bg-[var(--green-dim)]'
                            : 'border-[var(--rule)] hover:border-primary-300 hover:bg-primary-50'
                        }`}
                      >
                        <div>
                          <div className={`font-medium ${wasAdded ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>{item.title}</div>
                          <div className="text-sm text-[var(--text-ter)]">
                            {item.detailedState || item.lifecycle_state}
                          </div>
                        </div>
                        {wasAdded ? (
                          <CheckCircle className="h-5 w-5 text-[var(--green)]" />
                        ) : (
                          <Plus className="h-5 w-5 text-[var(--text-ter)]" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-[var(--rule)] flex justify-end">
              <button
                onClick={() => { setShowAddModal(false); setJustAdded(new Set()); }}
                className="px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Items Tab — routes to the correct sub-component based on session context mode.
// cycle_linked and app_linked use the workspace item picker; isolated uses free-text entry.
export function SessionItemsTab({ session }: TabProps) {
  if (session.sessionContextMode === 'isolated') {
    return <IsolatedItemsTab session={session} />;
  }
  return <LinkedItemsTab session={session} />;
}

// ============================================================================
// Session Inline Scoring Component
// ============================================================================

interface ScoringItem {
  id: string;
  name: string;
  description?: string;
  sourceId?: string;
}

interface SessionInlineScoringProps {
  items: ScoringItem[];
  criteria: string[];
  onComplete: (results: ToolResultItem[]) => void;
}

// Not wired into any tab yet — kept available for a future inline-scoring flow.
export function SessionInlineScoring({ items, criteria, onComplete }: SessionInlineScoringProps) {
  const attributes = useAttributesStore(state => state.attributes);
  
  // Track scores for each item
  const [scores, setScores] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    items.forEach((item, idx) => {
      initial[item.id] = items.length - idx; // Default: reverse order (first = highest)
    });
    return initial;
  });
  
  // Track order for drag-and-drop ranking
  const [orderedItems, setOrderedItems] = useState<ScoringItem[]>(items);
  
  // Get criterion labels
  const getCriterionLabel = (key: string) => {
    const attr = attributes.find(a => a.key === key);
    return attr?.label || key;
  };
  
  // Update score for an item
  const handleScoreChange = (itemId: string, score: number) => {
    setScores(prev => ({ ...prev, [itemId]: score }));
  };
  
  // Move item up in ranking
  const moveUp = (index: number) => {
    if (index === 0) return;
    const newOrder = [...orderedItems];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setOrderedItems(newOrder);
    // Update scores based on new order
    const newScores: Record<string, number> = {};
    newOrder.forEach((item, idx) => {
      newScores[item.id] = newOrder.length - idx;
    });
    setScores(newScores);
  };
  
  // Move item down in ranking
  const moveDown = (index: number) => {
    if (index === orderedItems.length - 1) return;
    const newOrder = [...orderedItems];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setOrderedItems(newOrder);
    // Update scores based on new order
    const newScores: Record<string, number> = {};
    newOrder.forEach((item, idx) => {
      newScores[item.id] = newOrder.length - idx;
    });
    setScores(newScores);
  };
  
  // Generate results
  const handleComplete = () => {
    const results: ToolResultItem[] = orderedItems.map((item, idx) => ({
      id: item.id,
      name: item.name,
      rank: idx + 1,
      score: scores[item.id] || (orderedItems.length - idx),
    }));
    onComplete(results);
  };
  
  return (
    <div className="space-y-4">
      {/* Criteria being used */}
      {criteria.length > 0 && (
        <div className="bg-[var(--surface)] p-4 mb-4">
          <p className="text-sm text-[var(--text-sec)] mb-2">
            <strong>Criteria:</strong> {criteria.map(c => getCriterionLabel(c)).join(', ')}
          </p>
        </div>
      )}
      
      {/* Ranking list */}
      <div className="space-y-2">
        {orderedItems.map((item, index) => (
          <div
            key={item.id}
            className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 flex items-center gap-4"
          >
            {/* Rank number */}
            <div className="w-8 h-8 flex items-center justify-center bg-primary-100 text-primary-700 rounded-full font-semibold text-sm">
              {index + 1}
            </div>
            
            {/* Item info */}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[var(--text-pri)] truncate">{item.name}</div>
              {item.description && (
                <div className="text-sm text-[var(--text-ter)] truncate">{item.description}</div>
              )}
            </div>
            
            {/* Score input */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-[var(--text-ter)]">Score:</label>
              <input
                type="number"
                min="1"
                max="100"
                value={scores[item.id] || 0}
                onChange={(e) => handleScoreChange(item.id, parseInt(e.target.value) || 0)}
                className="w-16 px-2 py-1 border border-[var(--rule)] rounded text-center text-sm"
              />
            </div>
            
            {/* Move buttons */}
            <div className="flex flex-col gap-1">
              <button
                onClick={() => moveUp(index)}
                disabled={index === 0}
                className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move up"
              >
                <ChevronRight className="h-4 w-4 rotate-[-90deg]" />
              </button>
              <button
                onClick={() => moveDown(index)}
                disabled={index === orderedItems.length - 1}
                className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move down"
              >
                <ChevronRight className="h-4 w-4 rotate-90" />
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {/* Complete button */}
      <div className="pt-4">
        <button
          onClick={handleComplete}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[var(--green)] text-white hover:bg-[var(--green)] transition-colors"
        >
          <CheckCircle className="h-5 w-5" />
          Save Rankings & Complete
        </button>
      </div>
    </div>
  );
}

// Run Tab
function RunTab({ session, onSwitchTab: _onSwitchTab, effectiveParticipantCount, effectiveParticipantIds, autoRun, onAutoRunConsumed }: TabProps & { onSwitchTab?: (tab: Tab) => void; effectiveParticipantCount?: number; effectiveParticipantIds?: string[]; autoRun?: boolean; onAutoRunConsumed?: () => void }) {
  const toolAdvisorEnabled = useAiCapability('tool_advisor');
  const navigate = useNavigate();
  const writeBackOnClose = useWriteBackOnClose();
  const participantCount = effectiveParticipantCount ?? session.participantIds.length;

  // Seed from effectiveParticipantIds prop (includes group-inherited members) if available,
  // otherwise fall back to session.participantIds while the async DB query runs.
  const [asyncParticipantIds, setAsyncParticipantIds] = useState<string[]>(
    (effectiveParticipantIds && effectiveParticipantIds.length > 0)
      ? effectiveParticipantIds
      : (session.participantIds ?? [])
  );
  const [resolvedParticipantProfiles, setResolvedParticipantProfiles] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const { getSafeSupabaseClient } = await import('../../../lib/supabaseSync');
      const client = getSafeSupabaseClient();
      if (!client || cancelled) return;

      const ids = new Set<string>(session.participantIds ?? []);

      if (session.cycleId) {
        const { data } = await client
          .from('cycle_participants')
          .select('user_id')
          .eq('cycle_id', session.cycleId)
          .eq('status', 'active');
        if (data) (data as Array<{ user_id: string }>).forEach(r => ids.add(r.user_id));
      }
      if (session.trackId) {
        const { data } = await client
          .from('track_participants')
          .select('user_id')
          .eq('track_id', session.trackId)
          .eq('status', 'active');
        if (data) (data as Array<{ user_id: string }>).forEach(r => ids.add(r.user_id));
      }

      if (cancelled) return;
      const allIds = [...ids];
      setAsyncParticipantIds(allIds);

      if (allIds.length > 0) {
        const { data: users } = await client
          .from('users')
          .select('id, name, email')
          .in('id', allIds);
        if (!cancelled && users) {
          setResolvedParticipantProfiles(
            (users as Array<{ id: string; name: string | null; email: string | null }>).map(u => ({
              id: u.id,
              name: u.name || u.email || u.id,
            }))
          );
        }
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [session.id, session.cycleId, session.trackId, session.participantIds?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const getCurrentWorkspace = useTenantStore(s => s.getCurrentWorkspace);
  const setSelectedTool = useSessionStore(state => state.setSelectedTool);
  const transitionSessionState = useSessionStore(state => state.transitionSessionState);
  const getEligibleItems = useSessionStore(state => state.getEligibleItems);
  const acceptedValues = useValueProposalStore(state => state.acceptedValues);
  const acceptedExceptions = useValueProposalStore(state => state.exceptions);
  const items = useItemsStore(state => state.items);
  const allItemPools = useItemPoolsStore(state => state.itemPools);
  const trackPool = useMemo(
    () => session.trackId ? allItemPools.find(p => p.trackId === session.trackId) ?? null : null,
    [session.trackId, allItemPools]
  );
  const trackPoolItemIds = useMemo(() => trackPool?.priorityIds ?? [], [trackPool]);
  const trackPoolItems = useMemo(() => items.filter(c => trackPoolItemIds.includes(c.id)), [items, trackPoolItemIds]);
  const attributes = useAttributesStore(state => state.attributes);
  const allCycleConfigs = useAttributeGovernanceStore(s => s.cycleConfigs);
  const fetchTrackConfigs = useAttributeGovernanceStore(s => s.fetchTrackConfigs);
  const isTrackLinked = session.sessionContextMode === 'cycle_linked';
  const trackConfigs = useMemo(
    () => session.trackId ? allCycleConfigs.filter(c => c.trackId === session.trackId) : [],
    [session.trackId, allCycleConfigs]
  );
  
  useEffect(() => {
    if (session.trackId) fetchTrackConfigs(session.trackId);
  }, [session.trackId, fetchTrackConfigs]);

  // Collaborative session context
  const allParticipants = useSessionParticipantsStore(s => s.participants);
  const userId = useUserStore(s => s.userId);
  const userName = useUserStore(s => s.userName);
  const { users: systemUsers } = useSystemUsers({ workspaceId: session.workspaceId ?? null });

  const sessionParticipantsForContext = useMemo(() => {
    const active = allParticipants.filter(p => p.sessionId === session.id && p.status === 'active');
    return active.map(p => {
      const sysUser = systemUsers.find(u => u.id === p.userId);
      return { id: p.userId, name: sysUser?.name ?? p.userId };
    });
  }, [allParticipants, session.id, systemUsers]);

  const isFacilitatorInSession = useMemo(
    () =>
      session.facilitatorId === userId ||
      allParticipants.some(p => p.sessionId === session.id && p.userId === userId && p.role === 'facilitator'),
    [allParticipants, session.id, session.facilitatorId, userId]
  );

  // Presence — needed inside renderTool for situationalContext
  const presentUsers = usePresenceStore(s => s.presentUsers);

  // §2.3/§2.4 Build Contract: reconstruct active tool session from DB on mount so
  // participants who arrive after the facilitator started the session see it correctly.
  const [activeToolSessionIdFromDB, setActiveToolSessionIdFromDB] = useState<string | undefined>(undefined);
  const subscribeToToolSession = useToolSessionStore(s => s.subscribeToToolSession);

  useEffect(() => {
    let cancelled = false;
    // Capture synchronously BEFORE the async DB fetch. The autoRun effect fires in the
    // same batch and calls onAutoRunConsumed() which strips ?live=1 from the URL before
    // our fetch resolves — reading window.location.search inside the async callback would
    // always see the cleaned-up URL and incorrectly redirect back to /live.
    const isLiveRankingMode = new URLSearchParams(window.location.search).get('live') === '1';
    const loadActiveToolSession = async () => {
      const { getSafeSupabaseClient } = await import('../../../lib/supabaseSync');
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;
      const { data } = await supabase
        .from('tool_sessions')
        .select('id')
        .eq('session_id', session.id)
        .in('phase', ['collecting', 'revealed'])
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      if (cancelled || !data) return;
      const id = (data as { id: string }).id;
      setActiveToolSessionIdFromDB(id);
      void subscribeToToolSession(id);
      const sessionIsLive = session.modeConfig?.timingMode !== 'async';
      if (sessionIsLive && session.state === 'CRITERIA_FINALIZED') {
        setAsyncToolSessionId(id);
        // If the user ended up on the Run tab without the live=1 ranking flag
        // (e.g., refreshed the page, or navigated here directly), send them back
        // to the shared /live board.
        if (!isLiveRankingMode) {
          navigate(
            withCatchballQuery(
              `/prioritization/sessions/${session.id}/live`,
              catchballQueryFromSearch(new URLSearchParams(window.location.search)),
            ),
            { replace: true },
          );
        }
      }
    };
    void loadActiveToolSession();
    return () => { cancelled = true; };
  }, [session.id, session.state, session.modeConfig?.timingMode, subscribeToToolSession, navigate]);

  // ── Live session open state ─────────────────────────────────────────────
  // Tracks whether the facilitator has clicked "Open for participants" for a live session.
  // Because the state machine has no intermediate "OPEN" state, we use local state to
  // advance the FacilitatorControls from open_session → collecting while the session
  // waits in CRITERIA_FINALIZED until the facilitator closes it.
  const [liveSessionOpen, setLiveSessionOpen] = useState(false);

  // ── Async session state ───────────────────────────────────────────────────
  const isAsync = session.modeConfig?.timingMode === 'async';
  const createToolSessionFn = useToolSessionStore(s => s.createToolSession);
  const participantResponses = useToolSessionStore(s => s.participantResponses);
  const computeAggregatesInRun = useToolSessionStore(s => s.computeAggregates);
  const createTask = usePrioritizationTaskStore(s => s.createTask);
  const openTaskFn = usePrioritizationTaskStore(s => s.openTask);
  const getCurrentWorkspaceForAsync = useTenantStore(s => s.getCurrentWorkspace);

  const [asyncToolSessionId, setAsyncToolSessionId] = useState<string | null>(null);
  const [isOpeningAsync, setIsOpeningAsync] = useState(false);
  const [asyncOpenError, setAsyncOpenError] = useState<string | null>(null);
  // Why these criteria (985), written beside the facilitator strip's Lock criteria.
  const [stripLockRationale, setStripLockRationale] = useState<RationaleValue>(EMPTY_RATIONALE);
  const [asyncMySubmitted, setAsyncMySubmitted] = useState(false);
  const [reNotifyState, setReNotifyState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [reNotifyCount, setReNotifyCount] = useState<number | null>(null);
  const reNotifyCooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load any existing async tool session for this prioritization session on mount.
  // Query by session_id only — the mode column may not be stored on older rows.
  useEffect(() => {
    if (!isAsync) return;
    let cancelled = false;
    const load = async () => {
      const { getSafeSupabaseClient } = await import('../../../lib/supabaseSync');
      const supabase = getSafeSupabaseClient();
      if (!supabase || cancelled) return;
      const { data } = await supabase
        .from('tool_sessions')
        .select('id')
        .eq('session_id', session.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled && data) {
        const tsId = (data as { id: string }).id;
        setAsyncToolSessionId(tsId);
        // Await so toolSessions store is populated before the user can submit.
        await subscribeToToolSession(tsId);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [isAsync, session.id, subscribeToToolSession]);

  // Track whether the current user has already submitted their async response
  useEffect(() => {
    if (!asyncToolSessionId || !userId) return;
    const submitted = participantResponses.some(
      r => r.toolSessionId === asyncToolSessionId && r.participantId === userId && r.isComplete
    );
    if (submitted) setAsyncMySubmitted(true);
  }, [participantResponses, asyncToolSessionId, userId]);

  // Use activeToolSessionIdFromDB as fallback so responses are visible even after a
  // page refresh when asyncToolSessionId (local state) hasn't been set yet.
  const effectiveToolSessionId = asyncToolSessionId ?? activeToolSessionIdFromDB ?? null;
  const asyncResponses = useMemo(
    () => participantResponses.filter(r => r.toolSessionId === effectiveToolSessionId && r.isComplete),
    [participantResponses, effectiveToolSessionId]
  );

  // Polling fallback: fetch all responses every 4s so the inline live board shows
  // every participant, not just the current user. Mirrors LiveSessionPage polling.
  const _upsertParticipantResponse = useToolSessionStore(s => s._upsertParticipantResponse);
  useEffect(() => {
    if (!effectiveToolSessionId || isAsync || session.state !== 'CRITERIA_FINALIZED') return;
    let cancelled = false;
    const poll = async () => {
      const supabase = getSafeSupabaseClient();
      if (!supabase || cancelled) return;
      const { data: respRows } = await supabase
        .from('tool_session_responses')
        .select('*')
        .eq('tool_session_id', effectiveToolSessionId);
      if (!respRows || cancelled) return;
      const toolSession = useToolSessionStore.getState().toolSessions.find(s => s.id === effectiveToolSessionId);
      respRows.forEach((row, idx) => {
        const colorIdx = toolSession
          ? toolSession.participantIds.indexOf(row.participant_id as string)
          : idx;
        _upsertParticipantResponse(
          {
            id: row.id as string,
            toolSessionId: row.tool_session_id as string,
            participantId: row.participant_id as string,
            participantName: (row.participant_name as string) || '',
            participantInitials: (row.participant_name as string || 'U').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase(),
            participantColor: '',
            submittedAt: (row.submitted_at as string) ?? (row.created_at as string),
            results: ((row.results as Array<{ id: string; name?: string; rank?: number; score?: number }>) ?? [])
              .map(r => ({ ...r, name: r.name ?? r.id })),
            isComplete: Boolean(row.is_complete),
          },
          colorIdx >= 0 ? colorIdx : idx
        );
      });
    };
    void poll();
    const interval = setInterval(() => { void poll(); }, 4000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [effectiveToolSessionId, isAsync, session.state, _upsertParticipantResponse]);

  // State for tool execution
  const [isRunning, setIsRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [toolAdvice, setToolAdvice] = useState<ToolAdvisorResult | null>(null);
  const [isAdvising, setIsAdvising] = useState(false);
  const [toolAdviceError, setToolAdviceError] = useState<string | null>(null);

  const handleToolAdvice = async () => {
    setIsAdvising(true);
    setToolAdvice(null);
    setToolAdviceError(null);
    try {
      const toolOptions = OFFERED_SESSION_TOOL_DEFINITIONS.map((tool) => {
        const compat = toolCompatibility[tool.compatibilityId];
        return {
          toolId: tool.compatibilityId,
          name: tool.name,
          description: tool.description,
          status: (compat?.status ?? 'compatible') as 'compatible' | 'compatible_with_warnings' | 'incompatible',
          warnings: compat?.warnings.map((w) => w.title) ?? [],
        };
      });
      // Use effective item count — includes track-pool inherited items, not just session.itemIds
      const effectiveItemCount = isTrackLinked && trackPoolItemIds.length > 0
        ? new Set([...eligibleIds, ...trackPoolItemIds]).size
        : eligibleIds.length;
      const result = await adviseToolSelection(toolOptions, {
        itemCount: effectiveItemCount,
        criteriaCount: effectiveCriteriaIds.length,
        participantCount,
        valuesSufficient: valueSufficiency.valuesSufficient,
        intent: session.intent,
      });
      if (result) {
        setToolAdvice(result);
      } else {
        setToolAdviceError('No compatible tools found or AI advisor is unavailable. Make sure an API key is configured in Settings → Connectors.');
      }
    } catch (err) {
      console.warn('[AI Advisor] failed:', err);
      setToolAdviceError('AI Advisor encountered an error. Please try again.');
    } finally {
      setIsAdvising(false);
    }
  };
  
  const eligibleIds = getEligibleItems(session.id);

  /** Same item universe as the run-bar count: session eligibility ∪ track pool when linked. */
  const runItemIds = useMemo(() => {
    if (isTrackLinked && trackPoolItemIds.length > 0) {
      return [...new Set([...eligibleIds, ...trackPoolItemIds])];
    }
    return eligibleIds;
  }, [isTrackLinked, trackPoolItemIds, eligibleIds]);

  // Items passed into the tool overlay — must match runItemIds so Run opens preloaded, not zero-state.
  const eligibleItems = useMemo(() => {
    return items.filter(c => runItemIds.includes(c.id)).map(c => ({
      id: c.id,
      name: c.title,
      description: c.description,
      sourceId: c.id,
    }));
  }, [items, runItemIds]);
  const sessionToolItems = useMemo<ToolItem[]>(
    () => {
      // Isolated sessions store free-form text labels rather than workspace item IDs.
      // Convert them to synthetic ToolItems so the tool opens pre-loaded instead of blank.
      if (session.sessionContextMode === 'isolated') {
        return (session.isolatedItems ?? [])
          .filter(text => text.trim())
          .map((text) => ({
            id: text.trim(),
            name: text.trim(),
            sourceType: 'item' as const,
          }));
      }
      return eligibleItems.map((item) => {
        // Build a map of accepted attribute values for this item so tools that are
        // data-driven (e.g. Impact/Effort Matrix) can read pre-existing scores instead
        // of asking users to rate them manually.
        const itemValues = acceptedValues.filter(av => av.itemId === item.id);
        const attributes: Record<string, unknown> = {};
        for (const av of itemValues) {
          if (av.value !== undefined && av.value !== null) {
            const numeric = typeof av.value === 'number' ? av.value : Number(av.value);
            attributes[av.attributeKey] = Number.isFinite(numeric) ? numeric : av.value;
          }
        }

        return {
          id: item.id,
          name: item.name,
          description: item.description,
          sourceId: item.sourceId,
          sourceType: 'item' as const,
          attributes: Object.keys(attributes).length > 0 ? attributes : undefined,
        };
      });
    },
    [session.sessionContextMode, session.isolatedItems, eligibleItems, acceptedValues]
  );
  
  // Effective criteria: session's criteriaIds, or track's accepted criteria when track-linked and session has none
  const effectiveCriteriaIds = useMemo(() => {
    if ((session.criteriaConfig.criteriaIds?.length ?? 0) > 0) return session.criteriaConfig.criteriaIds;
    if (isTrackLinked && session.trackId) {
      return trackConfigs.filter(c => c.rangeStatus === 'accepted').map(c => c.attributeKey);
    }
    return [];
  }, [session.criteriaConfig.criteriaIds, isTrackLinked, session.trackId, trackConfigs]);
  
  const sessionCriteria = useMemo(() => {
    return attributes.filter(attr => effectiveCriteriaIds.includes(attr.key));
  }, [attributes, effectiveCriteriaIds]);
  const selectedToolDefinition = useMemo(
    () => resolveSessionToolDefinition(session.selectedToolId),
    [session.selectedToolId]
  );
  const selectedCanonicalToolId = selectedToolDefinition?.compatibilityId;

  // Opens the async session for participants: creates a shared tool_session + tasks
  // Declared after sessionToolItems and selectedToolDefinition to avoid TDZ errors.
  const openForParticipants = useCallback(async () => {
    // Live session path: create a shared tool_session so all participants (including
    // the facilitator) see the same collaborative panel in real-time, then notify them.
    if (!isAsync) {
      setIsOpeningAsync(true);
      setAsyncOpenError(null);
      try {
        const { getCurrentWorkspaceId: getWsId } = await import('../../../lib/supabaseSync');
        const workspace = getCurrentWorkspaceForAsync();
        const workspaceId = workspace?.id ?? session.workspaceId ?? getWsId() ?? '';
        if (!workspaceId) {
          throw new Error('No workspace found. Make sure you are in a workspace before opening a live session.');
        }
        const participantIds = (effectiveParticipantIds && effectiveParticipantIds.length > 0)
          ? effectiveParticipantIds
          : asyncParticipantIds;
        const participantNames: Record<string, string> = {};
        for (const p of resolvedParticipantProfiles) participantNames[p.id] = p.name;

        const toolSessionId = await createToolSessionFn({
          sessionId: session.id,
          workspaceId,
          toolType: session.selectedToolId ?? 'pairwise_ranking',
          toolName: selectedToolDefinition?.name ?? 'Prioritization',
          outputType: 'ranking',
          mode: 'sync',
          participantIds,
          participantNames,
          facilitatorId: userId ?? '',
          facilitatorName: userName ?? '',
          items: sessionToolItems.map(i => ({ id: i.id, title: i.name })),
        });
        setAsyncToolSessionId(toolSessionId);

        // GAP-4: one go-live notification → C Mode /participate (brief-X §5).
        // Tasks still open for My Tasks, but skip duplicate task_assigned bells.
        //
        // The row has to say "live" before the urgent "Live now · Join now"
        // goes out — opening for participants used to leave the session in
        // DRAFT, so the alert landed people on "the facilitator is still
        // setting up this session."
        const goLive = await ensureSessionJoinable(session.id, userId ?? undefined, {
          participantCount: Math.max(1, participantIds.length),
        });
        if (!goLive.wentLive) {
          console.warn('[SessionDetail] session not announced as live:', goLive.error);
        }
        const nonFacilitatorIds = participantIds.filter(pid => pid !== userId);
        if (nonFacilitatorIds.length > 0 && goLive.wentLive) {
          void notifySessionLiveNow({
            sessionId: session.id,
            sessionName: session.name,
            recipientIds: nonFacilitatorIds,
            cycleId: session.cycleId,
            senderId: userId ?? undefined,
            assumeLive: true,
          });
        }
        for (const participantId of nonFacilitatorIds) {
          const task = createTask({
            name: `Join live session: ${session.name}`,
            intent: 'Join the live session now and submit your ranking.',
            category: 'prioritization',
            mode: 'live',
            participantIds: [participantId],
            facilitatorId: userId ?? 'system',
            createdBy: userId ?? 'system',
            opensAt: new Date(),
            sessionId: session.id,
            cycleId: session.cycleId ?? undefined,
            // session.selectedToolId uses the session tool catalog's underscore
            // ids (e.g. 'pairwise_ranking'); PrioritizationToolId is a narrower,
            // hyphenated task-routing enum with no 1:1 mapping for every session
            // tool — cast at this boundary rather than widen the task enum.
            toolId: (session.selectedToolId ?? 'pairwise-comparison') as PrioritizationToolId,
            itemIds: sessionToolItems.map(i => i.id),
            autoOpen: true,
          });
          openTaskFn(task.id, { skipNotification: true });
        }

        // Gate open: emit waiting_room_started so participants advance from WaitingRoom.
        void import('../../../lib/sessionEvents').then(({ emitSessionEvent }) =>
          emitSessionEvent(session.id, userId ?? '', userName ?? 'Facilitator', 'waiting_room_started', { toolSessionId })
        );

        setLiveSessionOpen(true);
        // Redirect facilitator to the live board — same screen everyone else sees.
        // Keep Catchball Inbox ?loopId= so ← Back to Catchball Inbox stays available.
        navigate(
          withCatchballQuery(
            `/prioritization/sessions/${session.id}/live`,
            catchballQueryFromSearch(new URLSearchParams(window.location.search)),
          ),
        );
      } catch (err) {
        setAsyncOpenError(err instanceof Error ? err.message : 'Failed to open session');
      } finally {
        setIsOpeningAsync(false);
      }
      return;
    }
    setIsOpeningAsync(true);
    setAsyncOpenError(null);
    try {
      const { getCurrentWorkspaceId: getWsId } = await import('../../../lib/supabaseSync');
      const workspace = getCurrentWorkspaceForAsync();
      // Mirror the live-session resolution: the tenantStore workspace list may not
      // be hydrated yet, so fall back to the session's workspace and the synced
      // workspace id before giving up. Avoids inserting an empty-string uuid.
      const workspaceId = workspace?.id ?? session.workspaceId ?? getWsId() ?? '';
      if (!workspaceId) {
        throw new Error('No workspace found. Make sure you are in a workspace before opening a session.');
      }
      // Prefer the fully-resolved list (includes group members) passed from the
      // parent component; fall back to the locally-queried set only if not provided.
      const participantIds = (effectiveParticipantIds && effectiveParticipantIds.length > 0)
        ? effectiveParticipantIds
        : asyncParticipantIds;
      const participantNames: Record<string, string> = {};
      for (const p of resolvedParticipantProfiles) participantNames[p.id] = p.name;

      const toolSessionId = await createToolSessionFn({
        sessionId: session.id,
        workspaceId,
        toolType: session.selectedToolId ?? 'pairwise_ranking',
        toolName: selectedToolDefinition?.name ?? 'Prioritization',
        outputType: 'ranking',
        mode: 'async',
        participantIds,
        participantNames,
        facilitatorId: userId ?? '',
        facilitatorName: userName ?? '',
        items: sessionToolItems.map(i => ({ id: i.id, title: i.name })),
        deadline: session.modeConfig?.closesAt
          ? new Date(session.modeConfig.closesAt).toISOString()
          : undefined,
      });
      setAsyncToolSessionId(toolSessionId);

      // Create + open a task for each non-facilitator participant so they get notified
      for (const participantId of participantIds) {
        if (participantId === userId) continue;
        const task = createTask({
          name: `Complete your ranking: ${session.name}`,
          category: 'prioritization',
          mode: 'async',
          participantIds: [participantId],
          facilitatorId: userId ?? 'system',
          createdBy: userId ?? 'system',
          opensAt: session.modeConfig?.opensAt ? new Date(session.modeConfig.opensAt) : new Date(),
          closesAt: session.modeConfig?.closesAt ? new Date(session.modeConfig.closesAt) : undefined,
          sessionId: session.id,
          cycleId: session.cycleId ?? undefined,
          toolId: (session.selectedToolId ?? 'pairwise-comparison') as PrioritizationToolId,
          itemIds: sessionToolItems.map(i => i.id),
        });
        openTaskFn(task.id);
      }
    } catch (err) {
      setAsyncOpenError(err instanceof Error ? err.message : 'Failed to open session');
    } finally {
      setIsOpeningAsync(false);
    }
  }, [isAsync, session, asyncParticipantIds, effectiveParticipantIds, resolvedParticipantProfiles, selectedToolDefinition, sessionToolItems, userId, userName, getCurrentWorkspaceForAsync, createToolSessionFn, createTask, openTaskFn, navigate]);

  const handleReNotify = useCallback(async () => {
    if (reNotifyState === 'sending' || reNotifyState === 'sent') return;
    setReNotifyState('sending');
    setReNotifyCount(null);
    try {
      // Only notify participants who haven't submitted yet
      const pendingIds = asyncParticipantIds.filter(pid => {
        if (pid === userId) return false;
        return !asyncResponses.some(r => r.participantId === pid);
      });
      for (const participantId of pendingIds) {
        const task = createTask({
          name: `Reminder: Complete your ranking — ${session.name}`,
          category: 'prioritization',
          mode: 'async',
          participantIds: [participantId],
          facilitatorId: userId ?? 'system',
          createdBy: userId ?? 'system',
          opensAt: new Date(),
          closesAt: session.modeConfig?.closesAt ? new Date(session.modeConfig.closesAt) : undefined,
          sessionId: session.id,
          cycleId: session.cycleId ?? undefined,
          toolId: (session.selectedToolId ?? 'pairwise-comparison') as PrioritizationToolId,
          itemIds: sessionToolItems.map(i => i.id),
        });
        openTaskFn(task.id);
      }
      setReNotifyCount(pendingIds.length);
      setReNotifyState('sent');
      if (reNotifyCooldownRef.current) clearTimeout(reNotifyCooldownRef.current);
      reNotifyCooldownRef.current = setTimeout(() => setReNotifyState('idle'), 30_000);
    } catch (err) {
      console.error('[ReNotify] Failed:', err);
      setReNotifyState('error');
      setTimeout(() => setReNotifyState('idle'), 4_000);
    }
  }, [reNotifyState, asyncParticipantIds, asyncResponses, userId, session, sessionToolItems, createTask, openTaskFn]);

  const valueSufficiency = useMemo(
    () =>
      computeValueSufficiency({
        itemIds: eligibleIds,
        attributeKeys: effectiveCriteriaIds,
        acceptedValues,
        exceptions: acceptedExceptions,
        sessionId: session.id,
      }),
    [acceptedExceptions, acceptedValues, effectiveCriteriaIds, eligibleIds, session.id]
  );
  
  // Compute tool compatibility for each tool (use effective participant count)
  const toolCompatibility = useMemo(() => {
    const results: Record<string, ToolCompatibilityResult> = {};
    const criteriaLocked = session.state !== 'DRAFT' || (isTrackLinked && trackConfigs.some(c => c.rangeStatus === 'accepted'));
    
    // Pool is locked if the session has advanced past DRAFT, OR if the track pool
    // has been explicitly locked while still in DRAFT (the common case for pairwise ranking).
    const poolLocked =
      session.state !== 'DRAFT' ||
      !!(trackPool?.poolLockedAt);

    // For track-linked sessions, items are inherited from the track pool rather than
    // explicitly added to session.itemIds. Union both so inherited items are counted.
    const sessionEligibleItems = items.filter(c => eligibleIds.includes(c.id));
    const effectiveItems = isTrackLinked && trackPoolItems.length > 0
      ? items.filter(c => {
          const inSession = eligibleIds.includes(c.id);
          const inTrackPool = trackPoolItemIds.includes(c.id);
          return inSession || inTrackPool;
        })
      : sessionEligibleItems;

    for (const tool of SESSION_TOOL_DEFINITIONS) {
      const context: ToolCompatibilityContext = {
        toolId: tool.compatibilityId,
        items: effectiveItems,
        criteria: sessionCriteria,
        poolLocked,
        valuesSufficient: valueSufficiency.valuesSufficient,
        criteriaLocked,
        valueCompletionByAttribute: valueSufficiency.valueCompletionByAttribute,
        minimumValueCompletionPercent: valueSufficiency.minimumValueCompletionPercent,
        participationLocked: session.state !== 'DRAFT',
        participantCount,
      };
      results[tool.compatibilityId] = evaluateToolCompatibility(context);
    }

    return results;
  }, [items, eligibleIds, sessionCriteria, session.state, participantCount, isTrackLinked, trackConfigs, valueSufficiency, trackPool, trackPoolItems, trackPoolItemIds]);

  // Situation rules (advisory). Precedence: compatibility (hard) > situation rules > catalog order.
  // In DRAFT every card is selectable (intent only), so the whole catalog is "available" there;
  // once running, only tools compatibility lets through. Rules reorder and annotate — never gate.
  const situationRecommendation = useMemo(() => {
    const available = OFFERED_SESSION_TOOL_DEFINITIONS
      .filter((tool) => {
        const compat = toolCompatibility[tool.compatibilityId];
        return session.state === 'DRAFT' || !compat || compat.canUse;
      })
      .map((tool) => tool.compatibilityId);
    return evaluateSituationRules({
      vector: session.situationVector,
      availableToolIds: registryIdsToMatrixToolIds(available),
      defaultToolIds: [],
    });
  }, [session.state, session.situationVector, toolCompatibility]);

  const orderedToolDefinitions = useMemo(() => {
    if (!situationRecommendation.applied) return OFFERED_SESSION_TOOL_DEFINITIONS;
    const order = matrixToolIdsToRegistryIds(situationRecommendation.orderedToolIds);
    const rank = new Map(order.map((id, i) => [id, i]));
    return [...OFFERED_SESSION_TOOL_DEFINITIONS].sort(
      (a, b) => (rank.get(a.compatibilityId) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.compatibilityId) ?? Number.MAX_SAFE_INTEGER)
    );
  }, [situationRecommendation]);

  // Every choice made while a rule was in effect is recorded — followed or overridden —
  // as the evidence the rule table is revised against (method_recommendation_overrides).
  const recordToolChoice = useCallback((registryToolId: string) => {
    if (!situationRecommendation.applied || !session.workspaceId) return;
    const chosenToolId = registryIdToMatrixToolId(registryToolId);
    if (!chosenToolId) return;
    void recordMethodChoice({
      workspaceId: session.workspaceId,
      sessionId: session.id,
      chosenToolId,
      recommendation: situationRecommendation,
      vector: session.situationVector,
    });
  }, [situationRecommendation, session.workspaceId, session.id, session.situationVector]);
  
  // Criteria are finalized if state is past DRAFT, we have a lock timestamp, or track has locked criteria (no extra finalize step)
  const trackCriteriaReady = isTrackLinked && trackConfigs.some(c => c.rangeStatus === 'accepted');
  // Non-track-linked sessions have no criteria to finalize — treat as ready.
  const criteriaFinalized =
    !isTrackLinked ||
    session.state !== 'DRAFT' ||
    (session as { criteriaFinalizedAt?: Date | string }).criteriaFinalizedAt != null ||
    trackCriteriaReady;
  const hasSelectedTool = !!selectedToolDefinition;
  const selectedToolCompatibility = selectedCanonicalToolId
    ? toolCompatibility[selectedCanonicalToolId]
    : undefined;
  
  // Check if selected tool is compatible (no blockers)
  const selectedToolCompatible = hasSelectedTool 
    ? selectedToolCompatibility?.canUse ?? true
    : true;
  const canRun = criteriaFinalized && hasSelectedTool && selectedToolCompatible;

  // Auto-start the tool when arriving from the launcher with ?autorun=1
  useEffect(() => {
    if (!autoRun || isRunning) return;
    if (canRun) {
      setIsRunning(true);
      onAutoRunConsumed?.();
    }
  }, [autoRun, canRun, isRunning, onAutoRunConsumed]);
  
  // Handle tool completion - convert ToolResultItem[] to SessionRankingOutput
  // Now uses the run_session RPC with fallback to store method (v3.0)
  const handleToolComplete = useCallback(async (results: ToolResultItem[]) => {
    console.log('[RunTab] Tool completed with results:', results);
    
    // Sort by rank if available, otherwise by score
    const sortedResults = [...results].sort((a, b) => {
      if (a.rank !== undefined && b.rank !== undefined) {
        return a.rank - b.rank;
      }
      if (a.score !== undefined && b.score !== undefined) {
        return b.score - a.score; // Higher score = better
      }
      return 0;
    });
    
    // Convert to SessionRankingOutput format
    const rankedItemIds = sortedResults.map(r => r.id);
    const scores = buildSessionScores(sortedResults);
    const classifications = buildSessionClassifications(sortedResults);

    // Derive output_form from method registry for audit/routing metadata
    const toolVariants = selectedCanonicalToolId ? getVariantsForTool(selectedCanonicalToolId) : [];
    const primaryOutputForm = toolVariants[0]?.outputForms[0];
    // Map session intent to the appropriate DecisionClass for routing
    const intentToSClass: Record<string, import('../../../types/prioritization/session').DecisionClass> = {
      select_attributes: 'S3_ATTRIBUTE_SELECTION',
      converge_values: 'S5_VALUE_SELECTION',
      select_item_pool: 'S2_ITEM_POOL_SELECTION',
      rank_priorities: 'S6_ITEM_PRIORITIZATION',
      calibration: 'S6_ITEM_PRIORITIZATION',
      review: 'S6_ITEM_PRIORITIZATION',
    };
    const decisionClass = intentToSClass[session.intent] ?? 'S6_ITEM_PRIORITIZATION';
    // Compute routed destinations from output form — used both for logging and for
    // post-persist side-effects (item pool update, criteria artifact, narrative artifact).
    const routed = primaryOutputForm
      ? routeToolResults(sortedResults, primaryOutputForm, decisionClass)
      : [];
    console.log('[RunTab] Result routing:', routed.map(r => r.destination));
    
    const rankingOutput = {
      toolId: selectedCanonicalToolId!,
      toolName: selectedToolDefinition?.name ?? 'Prioritization',
      toolConfig: {},
      rankedItemIds,
      scores,
      classifications,
      normalizedScores: {},
      tiers: {},
      generatedAt: new Date().toISOString(),
      ...(primaryOutputForm ? { outputForm: primaryOutputForm } : {}),
    };
    
    // When a tool_session exists (live or async), everyone — including the facilitator —
    // submits their individual response here. Session finalization is a separate action:
    // the facilitator clicks "Close participation" in the FacilitatorControls strip, which
    // calls transitionSessionState → RUN_COMPLETE and triggers aggregation independently.
    if (asyncToolSessionId) {
      const { useToolSessionStore: tsStore } = await import('@/stores/prioritization/toolSessionStore');
      const { getCurrentWorkspaceId } = await import('@/lib/supabaseSync');
      await tsStore.getState().submitParticipantResponse({
        toolSessionId: asyncToolSessionId,
        workspaceId: getCurrentWorkspaceId() ?? '',
        participantId: userId ?? '',
        participantName: userName ?? '',
        results: sortedResults,
      });
      setAsyncMySubmitted(true);
      setIsRunning(false);
      {
        const inboxCtx = catchballQueryFromSearch(new URLSearchParams(window.location.search));
        if (isAsync) {
          navigate(withCatchballQuery(`/prioritization/reports/session/${session.id}`, inboxCtx));
        } else {
          // Live session: return everyone (facilitator + participants) to the shared board
          navigate(withCatchballQuery(`/prioritization/sessions/${session.id}/live`, inboxCtx));
        }
      }
      return;
    }

    // The run_session RPC is the whole act (760) and the ONLY way this app
    // runs a session: it writes ranking_output, the RUN_COMPLETE state and the
    // session_results rows in one transaction. There is no client-side
    // fallback — 771 closed the direct session_results write, and a second
    // lane is exactly the "same button, different behaviour" defect.
    {
      const { runSessionViaRpc, useSessionStore } = await import('@/stores/prioritization/sessionStore');
      const rpcResult = await runSessionViaRpc(session.id, rankingOutput);

      if (rpcResult.success) {
        console.log('[RunTab] Session run via RPC successfully');
        // V-5: the workflow's write-back setting decides what the per-criterion scores become.
        await writeBackOnClose({ session, results: sortedResults });
        setIsRunning(false);
        navigate(`/prioritization/reports/session/${session.id}`);
        return;
      }

      const err = rpcResult.error ?? '';
      if (err.includes('Session must be in DRAFT or CRITERIA_FINALIZED')) {
        // Session already RUN_COMPLETE in DB (e.g. from a previous run); refetch and show results.
        console.warn('[RunTab] run_session rejected (session already complete?), refetching');
        await useSessionStore.getState().fetchFromSupabase();
        setIsRunning(false);
        navigate(`/prioritization/reports/session/${session.id}`);
        return;
      }

      // Server refusal (or no server) — nothing was written and nothing is
      // written here. Say why and stop.
      console.error('[RunTab] RPC run_session refused:', err, rpcResult.readiness);
      const blockers = collectReadinessBlockers(rpcResult.readiness);
      setRunError(
        `This session was not run: ${err || 'the server refused it'}.` +
          (blockers.length > 0 ? ` Still to do: ${blockers.join('; ')}.` : '') +
          ' Nothing was saved — fix this and run the session again.',
      );
      setIsRunning(false);
      return;
    }
  }, [isAsync, asyncToolSessionId, userId, userName, selectedCanonicalToolId, selectedToolDefinition?.name, navigate, session, writeBackOnClose]);
  
  // Handle cancel - close tool without saving
  const handleCancelTool = useCallback(() => {
    setIsRunning(false);
  }, []);
  
  // Start running the tool
  const handleRunTool = () => {
    if (!canRun) {
      return;
    }
    setIsRunning(true);
  };

  /** Live + participant entry: ranking happens on the shared /live board, not the Run tab overlay */
  const goToLiveRanking = () => {
    navigate(
      withCatchballQuery(
        `/prioritization/sessions/${session.id}/live?startRanking=1`,
        catchballQueryFromSearch(new URLSearchParams(window.location.search)),
      ),
    );
  };
  
  // Render tool in full-screen overlay when running
  const renderTool = () => {
    const selectedTool = selectedToolDefinition;
    if (!selectedTool) return null;

    const SelectedToolComponent = selectedTool.component;

    // Build situational context: items come preloaded from the session;
    // completion label and destination hint come from the method registry.
    const toolVariants = selectedCanonicalToolId ? getVariantsForTool(selectedCanonicalToolId) : [];
    const primaryVariant = toolVariants[0];
    const intentToSClass: Record<string, import('../../../types/prioritization/session').DecisionClass> = {
      select_attributes: 'S3_ATTRIBUTE_SELECTION',
      converge_values: 'S5_VALUE_SELECTION',
      select_item_pool: 'S2_ITEM_POOL_SELECTION',
      rank_priorities: 'S6_ITEM_PRIORITIZATION',
      calibration: 'S6_ITEM_PRIORITIZATION',
      review: 'S6_ITEM_PRIORITIZATION',
    };
    const decisionClass = intentToSClass[session.intent] ?? 'S6_ITEM_PRIORITIZATION';
    const outputForms = primaryVariant?.outputForms ?? [];
    const destinationHint = outputForms.length > 0
      ? `→ ${describeResultDestinations(outputForms, decisionClass)}`
      : undefined;

    const facilitatorUser = systemUsers.find(u => u.id === session.facilitatorId);
    const situationalContext: ToolSituationalContext = {
      inputMode: sessionToolItems.length > 0 ? 'preloaded' : 'workspace',
      completion: {
        label: 'Complete Session Run',
        destinationHint,
        outputForm: primaryVariant?.outputForms[0],
        decisionClass,
        navigateOnComplete: true,
      },
      sessionId: session.id,
      cycleId: session.cycleId ?? undefined,
      trackId: session.trackId ?? undefined,
      // Async sessions use per-participant independent ranking; the live CollaborativeSessionPanel
      // is a sync-only feature. Pass empty participants list to suppress it for async mode.
      sessionParticipants: isAsync ? [] : sessionParticipantsForContext,
      currentUserId: userId ?? undefined,
      currentUserName: userName || 'You',
      isFacilitator: isFacilitatorInSession,
      workspaceId: getCurrentWorkspace()?.id ?? '',
      facilitatorId: session.facilitatorId ?? '',
      facilitatorName: facilitatorUser?.name ?? session.facilitatorId ?? '',
      presentUsers,
      // §2.3/§2.4: pass DB-loaded active tool session so participants who arrive
      // after the facilitator started the session see the collecting state.
      activeToolSessionId: activeToolSessionIdFromDB,
      toolConfig: session.toolConfig,
    };

    return (
      <ToolItemsProvider
        items={sessionToolItems}
        hostContext="session"
        situationalContext={situationalContext}
      >
        <SelectedToolComponent
          displayMode="page"
          hostContext="session"
          isOpen
          onClose={handleCancelTool}
          onCompleteResults={handleToolComplete}
          completeActionLabel={(asyncToolSessionId || activeToolSessionIdFromDB) && !isFacilitatorInSession ? 'Submit my ranking' : 'Complete Session Run'}
          importSourceLabel="Session"
          backTo={{ label: 'Back to Session', onClick: handleCancelTool }}
        />
      </ToolItemsProvider>
    );
  };
  
  // Show tool overlay if running
  if (isRunning) {
    return renderTool();
  }

  // When arriving via autorun, wait silently for eligibility to be computed
  // rather than flashing "Cannot run yet" at the user for a brief moment.
  if (autoRun && !canRun && !runError) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 text-[var(--text-ter)]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
        <p className="text-sm">Preparing session…</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {runError && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-3 text-sm text-[var(--coral)]">
          {runError}
        </div>
      )}

      {/* ── Async Session Panel ─────────────────────────────────────────── */}
      {isAsync && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-[var(--mustard)]" />
            <h3 className="text-lg font-semibold text-[var(--text-pri)]">Async Session</h3>
            {asyncToolSessionId && (
              <span className="ml-auto text-xs bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] px-2 py-0.5 rounded-full font-medium">
                Open for Participants
              </span>
            )}
          </div>

          {/* How it works */}
          <div className="text-sm text-[var(--text-sec)] space-y-1">
            <p>Each participant completes their ranking independently during the open window, then you aggregate all responses into a combined result.</p>
            {session.modeConfig?.opensAt && (
              <p className="text-[var(--text-ter)]">
                Window: {new Date(session.modeConfig.opensAt).toLocaleString()}
                {session.modeConfig?.closesAt && ` → ${new Date(session.modeConfig.closesAt).toLocaleString()}`}
              </p>
            )}
          </div>

          {asyncOpenError && (
            <p className="text-sm text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] px-3 py-2">{asyncOpenError}</p>
          )}

          {/* Participant response status */}
          {asyncToolSessionId && asyncParticipantIds.length > 0 && (
            <div>
              <p className="text-sm font-medium text-[var(--text-sec)] mb-2">Participant Status</p>
              <div className="space-y-1.5">
                {asyncParticipantIds.map(pid => {
                  const user = systemUsers.find(u => u.id === pid);
                  const responded = asyncResponses.some(r => r.participantId === pid);
                  const isMe = pid === userId;
                  return (
                    <div key={pid} className="flex items-center gap-2 text-sm">
                      <span className={`h-2 w-2 rounded-full flex-shrink-0 ${responded ? 'bg-[var(--green)]' : 'bg-[var(--rule-bright)]'}`} />
                      <span className="text-[var(--text-pri)]">{user?.name ?? pid}{isMe ? ' (you)' : ''}</span>
                      <span className={`ml-auto text-xs ${responded ? 'text-[var(--green)] dark:text-[var(--text-ter)] font-medium' : 'text-[var(--text-ter)]'}`}>
                        {responded ? 'Submitted' : isMe ? '—' : 'Pending'}
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-[var(--text-ter)] mt-2">
                {asyncResponses.length} of {asyncParticipantIds.length} submitted
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-1">
            {!asyncToolSessionId ? (
              <button
                onClick={openForParticipants}
                disabled={isOpeningAsync || !canRun}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isOpeningAsync ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {isOpeningAsync ? 'Opening…' : 'Open for Participants'}
              </button>
            ) : (
              <div className="flex flex-col gap-1.5">
                <button
                  onClick={handleReNotify}
                  disabled={reNotifyState === 'sending' || reNotifyState === 'sent'}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm  border transition-colors disabled:cursor-not-allowed ${
                    reNotifyState === 'sent'
                      ? 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]'
                      : reNotifyState === 'error'
                        ? 'bg-[var(--coral-dim)] border-[var(--coral)] text-[var(--coral)]'
                        : 'text-[var(--text-sec)] border-[var(--rule)] hover:bg-[var(--surface)] disabled:opacity-50'
                  }`}
                  title={reNotifyState === 'sent' ? 'Notifications sent — button unlocks in 30s' : 'Re-send notifications to participants who haven\'t submitted yet'}
                >
                  {reNotifyState === 'sending' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {reNotifyState === 'sent' && <CheckCircle className="h-3.5 w-3.5" />}
                  {reNotifyState === 'error' && <AlertCircle className="h-3.5 w-3.5" />}
                  {reNotifyState === 'idle' && <RefreshCw className="h-3.5 w-3.5" />}
                  {reNotifyState === 'sending' ? 'Sending…' :
                   reNotifyState === 'sent' ? `Sent to ${reNotifyCount ?? 0} participant${reNotifyCount !== 1 ? 's' : ''}` :
                   reNotifyState === 'error' ? 'Failed — try again' :
                   'Re-notify Pending'}
                </button>
                {reNotifyState === 'sent' && (
                  <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)]">Button will re-enable in 30 seconds.</p>
                )}
              </div>
            )}
          </div>

          {asyncMySubmitted && asyncResponses.length < asyncParticipantIds.length && (
            <div className="flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] px-3 py-2">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              You've submitted your ranking. Waiting for other participants.
            </div>
          )}
        </div>
      )}

      {/* Error from openForParticipants — shown for both live and async sessions */}
      {asyncOpenError && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-3 text-sm text-[var(--coral)] flex items-center gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {asyncOpenError}
        </div>
      )}

      {/* ── Facilitator controls — elevated action strip for the session facilitator ── */}
      {isFacilitatorInSession && (
        <FacilitatorControls
          session={session}
          participants={allParticipants.filter((p) => p.sessionId === session.id)}
          submittedCount={asyncResponses?.length ?? 0}
          forceStep={(!isAsync && liveSessionOpen && session.state === 'CRITERIA_FINALIZED') ? 'collecting' : undefined}
          onLockConfig={session.state === 'DRAFT' ? async () => {
            const locked = await transitionSessionState(session.id, 'CRITERIA_FINALIZED', session.facilitatorId, {
              participantCount,
              rationale: toRationaleParts(stripLockRationale),
            });
            if (locked?.success) setStripLockRationale(EMPTY_RATIONALE);
          } : undefined}
          stepExtras={session.state === 'DRAFT' ? {
            lock_config: (
              <RationaleFields
                value={stripLockRationale}
                onChange={setStripLockRationale}
                title="Why these criteria"
              />
            ),
          } : undefined}
          onOpenSession={session.state === 'CRITERIA_FINALIZED' ? openForParticipants : undefined}
          onCloseSession={async () => {
            setLiveSessionOpen(false);
            // §2.1 Build Contract: results must persist from DB truth, not in-memory state.
            // Use activeToolSessionIdFromDB (always populated on mount from DB) so a page
            // refresh between Open and Close does not lose the tool session reference.
            const toolSessionId = activeToolSessionIdFromDB ?? asyncToolSessionId;
            if (toolSessionId) {
              try {
                const { runSessionViaRpc } = await import('@/stores/prioritization/sessionStore');
                // The one counting rule — re-reads the answers from the database
                // (§2.1) and counts them the same way the app and the live board do.
                const toolId = session.selectedToolId ?? 'pairwise_ranking';
                const rankingOutput = await rankingOutputForToolSession(toolSessionId, {
                  toolId,
                  toolName: resolveSessionToolDefinition(toolId)?.name ?? 'Prioritization',
                });
                if (rankingOutput) {
                  // One act, one write path (771): run_session writes
                  // ranking_output, RUN_COMPLETE and the session_results rows
                  // in one transaction. A refusal leaves the session in
                  // CRITERIA_FINALIZED with nothing saved.
                  const runResult = await runSessionViaRpc(session.id, rankingOutput);
                  if (!runResult.success) {
                    console.error('[LiveSession] run_session refused — session stays open:', runResult.error);
                    setLiveSessionOpen(true); // restore open state so facilitator can retry
                    return;
                  }
                  await writeBackOnClose({ session, toolSessionId });
                  await useSessionStore.getState().fetchFromSupabase();
                  navigate('/prioritization/reports/sessions');
                  return;
                }
              } catch (err) {
                console.error('[LiveSession] Failed to aggregate results on close:', err);
                setLiveSessionOpen(true); // restore open state so facilitator can retry
                return;
              }
            }
            await transitionSessionState(session.id, 'RUN_COMPLETE', session.facilitatorId, { participantCount });
            await useSessionStore.getState().fetchFromSupabase();
            navigate('/prioritization/reports/sessions');
          }}
          onPublish={session.state === 'RUN_COMPLETE' ? async () => {
            // One lane (739) — same RPC, label, confirmation and records as the
            // Results and Ranking tabs.
            const { publishSessionResults } = await import('@/stores/prioritization/sessionStore');
            const publishResult = await publishSessionResults(session.id, {
              actorId: userId || session.facilitatorId,
            });
            if (!publishResult.success) {
              if (publishResult.code === 'cancelled') return;
              // Stay here and say why — never navigate as if it published.
              setRunError(
                `Results were not published: ${publishResult.error ?? 'the server refused'}. The session is still unpublished — try again.`,
              );
              return;
            }
            setRunError(null);
            await useSessionStore.getState().fetchFromSupabase();
            navigate('/prioritization/reports/sessions');
          } : undefined}
        />
      )}

      {/* ── Live Session Board — shown instead of the admin run bar when a live session is collecting ── */}
      {(() => {
        const liveToolId = effectiveToolSessionId;
        const isLiveActive = !isAsync && !!liveToolId && session.state === 'CRITERIA_FINALIZED';
        if (!isLiveActive) return null;

        const liveAggregates = computeAggregatesInRun(liveToolId!);
        const sortedAgg = [...liveAggregates].sort((a, b) =>
          (b.totalScore ?? b.averageScore ?? 0) - (a.totalScore ?? a.averageScore ?? 0)
        );
        const mySubmitted = asyncMySubmitted || asyncResponses.some(r => r.participantId === userId);
        const livePtps = allParticipants.filter(p => p.sessionId === session.id);

        return (
          <div className="border border-[var(--slate)] bg-[var(--slate-dim)] overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-3 bg-[var(--slate)] text-white">
              <span className="h-2 w-2 rounded-full bg-[var(--green)] animate-pulse" />
              <span className="font-semibold text-sm">Live Session</span>
              <span className="ml-auto text-xs opacity-80">
                {asyncResponses.length} / {livePtps.length || participantCount} submitted
              </span>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Left: Participant status */}
              <div>
                <p className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide mb-3">Participants</p>
                <div className="space-y-2">
                  {(livePtps.length > 0 ? livePtps : allParticipants).map(p => {
                    const submitted = asyncResponses.some(r => r.participantId === p.userId);
                    const user = systemUsers.find(u => u.id === p.userId);
                    const name = user?.name ?? p.userId;
                    const isMe = p.userId === userId;
                    const isOnline = presentUsers.some(u => u.userId === p.userId);
                    return (
                      <div key={p.userId} className="flex items-center gap-2 text-sm">
                        <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${isOnline ? 'bg-[var(--green)]' : 'bg-[var(--rule-bright)]'}`} title={isOnline ? 'Online' : 'Offline'} />
                        <span className="text-[var(--text-pri)]">{name}{isMe ? ' (you)' : ''}</span>
                        {submitted
                          ? <span className="ml-auto text-xs text-[var(--green)] dark:text-[var(--text-ter)] font-medium flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5" />Submitted</span>
                          : <span className="ml-auto text-xs text-[var(--text-sec)]">Pending…</span>
                        }
                      </div>
                    );
                  })}
                </div>

                {/* My action */}
                <div className="mt-4">
                  {mySubmitted ? (
                    <div className="flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border border-[var(--green)] px-3 py-2">
                      <CheckCircle className="h-4 w-4 flex-shrink-0" />
                      Your ranking is submitted.
                      <button onClick={goToLiveRanking} className="ml-auto text-xs text-[var(--text-ter)] underline">Revise</button>
                    </div>
                  ) : (
                    <button
                      onClick={goToLiveRanking}
                      className="flex items-center gap-2 px-4 py-2.5 bg-[var(--slate)] text-white text-sm font-semibold hover:bg-[var(--slate)] transition-colors w-full justify-center"
                    >
                      <Play className="h-4 w-4" />
                      Start my ranking
                    </button>
                  )}
                </div>
              </div>

              {/* Right: Live aggregate results */}
              <div>
                <p className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide mb-3">
                  {sortedAgg.length > 0 ? 'Live Results' : 'Results (waiting for responses)'}
                </p>
                {sortedAgg.length === 0 ? (
                  <p className="text-sm text-[var(--text-sec)] italic">Results will appear as participants submit.</p>
                ) : (
                  <div className="space-y-2">
                    {sortedAgg.map((agg, idx) => {
                      const total = agg.totalScore ?? agg.averageScore ?? 0;
                      const average = agg.averageScore ?? agg.totalScore ?? 0;
                      const maxScore = (sortedAgg[0]?.totalScore ?? sortedAgg[0]?.averageScore ?? 1) || 1;
                      const pct = Math.round((total / maxScore) * 100);
                      return (
                        <div key={agg.itemId} className="flex items-center gap-2 text-sm">
                          <span className="w-5 text-center text-xs font-bold text-[var(--text-sec)]">{idx + 1}</span>
                          <span className="flex-1 truncate text-[var(--text-pri)] font-medium">{agg.itemTitle}</span>
                          <div className="w-20 h-2 rounded-full bg-[var(--slate-dim)] overflow-hidden flex-shrink-0">
                            <div className="h-full rounded-full bg-[var(--slate)] transition-all duration-500" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="flex flex-col items-end flex-shrink-0">
                            <span className="text-xs font-semibold text-[var(--text-sec)] tabular-nums leading-none">{Math.round(total)}</span>
                            <span className="text-[10px] text-[var(--text-sec)] tabular-nums leading-none mt-0.5">avg {average.toFixed(2)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Run bar — shown only when live session is NOT active (setup / solo mode) ── */}
      {(() => {
        const liveActive = !isAsync && !!effectiveToolSessionId && session.state === 'CRITERIA_FINALIZED';
        if (liveActive) return null; // Live Board handles this
        const effectiveItemCount = isTrackLinked && trackPoolItemIds.length > 0
          ? new Set([...eligibleIds, ...trackPoolItemIds]).size
          : eligibleIds.length;
        return (
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4 flex flex-wrap items-center gap-4">
            {/* Stats */}
            <div className="flex items-center gap-6 flex-1 min-w-0">
              <div className="text-center">
                <div className={`text-xl font-bold ${effectiveItemCount === 0 ? 'text-[var(--coral)]' : 'text-[var(--text-pri)]'}`}>{effectiveItemCount}</div>
                <div className="text-xs text-[var(--text-ter)]">Items</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-[var(--text-pri)]">{effectiveCriteriaIds.length}</div>
                <div className="text-xs text-[var(--text-ter)]">Criteria</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-[var(--text-pri)]">{participantCount}</div>
                <div className="text-xs text-[var(--text-ter)]">Participants</div>
              </div>
              {selectedToolDefinition && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 border-2 border-primary-500 text-xs font-semibold text-primary-900 shadow-sm">
                  <CheckCircle className="h-4 w-4 text-primary-600 shrink-0" aria-hidden />
                  <span className="truncate max-w-[10rem] sm:max-w-[14rem]">{selectedToolDefinition.name}</span>
                </div>
              )}
            </div>
            {/* Blockers summary */}
            {!canRun && (
              <div className="flex items-center gap-1.5 text-xs text-[var(--mustard)]">
                <AlertCircle className="h-3.5 w-3.5 text-[var(--mustard)] shrink-0" />
                <span>
                  {!session.selectedToolId ? 'Select a method below' :
                   !criteriaFinalized ? 'Lock criteria first' :
                   selectedToolCompatibility?.blockers[0]?.title ?? 'Cannot run yet'}
                </span>
              </div>
            )}
            {/* Run button */}
            <button
              onClick={handleRunTool}
              disabled={!canRun || (isAsync && !asyncToolSessionId)}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
              title={isAsync && !asyncToolSessionId ? 'Open the session for participants first' : undefined}
            >
              <Play className="h-4 w-4" />
              {isAsync ? (asyncMySubmitted ? REVISE_MY_RANKING_LABEL : SUBMIT_MY_RANKING_LABEL) : RUN_SESSION_LABEL}
            </button>
          </div>
        );
      })()}

      {/* Hide session config panels when live session is collecting — participants don't need them */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6" style={{ display: (!isAsync && !!effectiveToolSessionId && session.state === 'CRITERIA_FINALIZED') ? 'none' : undefined }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[var(--text-pri)]">Select Prioritization Method</h3>
          {toolAdvisorEnabled && (
          <button
            onClick={handleToolAdvice}
            disabled={isAdvising}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] bg-[var(--slate-dim)] border border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors disabled:opacity-60"
            title="Ask AI to recommend the best method for this session"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {isAdvising ? 'Advising…' : 'AI Advisor'}
          </button>
          )}
        </div>

        {/* AI Advisor error */}
        {toolAdviceError && (
          <div className="mb-4 flex items-start gap-2 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-xs text-[var(--mustard)]">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[var(--mustard)]" />
            <span>{toolAdviceError}</span>
            <button onClick={() => setToolAdviceError(null)} className="ml-auto shrink-0 p-0.5 hover:bg-[var(--mustard-dim)] rounded">
              <X className="h-3 w-3 text-[var(--mustard)]" />
            </button>
          </div>
        )}

        {/* AI Tool Advice Panel */}
        {toolAdvice && (
          <div className="mb-5 p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
                <span className="text-sm font-semibold text-[var(--text-sec)]">AI Tool Recommendation</span>
              </div>
              <button
                onClick={() => setToolAdvice(null)}
                className="p-0.5 hover:bg-[var(--slate-dim)] rounded"
              >
                <X className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              </button>
            </div>
            {toolAdvice.overallGuidance && (
              <p className="text-xs text-[var(--text-sec)] mb-3">{toolAdvice.overallGuidance}</p>
            )}
            {/* Top pick */}
            <div className="bg-[var(--bg-raised)] border border-[var(--slate)] p-3 mb-2">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs bg-[var(--slate)] text-white px-2 py-0.5 rounded-full font-semibold">Top Pick</span>
                <span className="text-sm font-semibold text-[var(--text-pri)]">{toolAdvice.topPick.toolName}</span>
              </div>
              <p className="text-xs text-[var(--text-sec)] font-medium mb-0.5">{toolAdvice.topPick.headline}</p>
              <p className="text-xs text-[var(--text-sec)]">{toolAdvice.topPick.rationale}</p>
              {toolAdvice.topPick.tradeoffs && (
                <p className="text-xs text-[var(--mustard)] mt-1 italic">Trade-off: {toolAdvice.topPick.tradeoffs}</p>
              )}
            </div>
            {/* Alternatives */}
            {toolAdvice.alternatives.length > 0 && (
              <div className="space-y-1.5">
                {toolAdvice.alternatives.map((alt) => (
                  <div key={alt.toolId} className="bg-[var(--bg-raised)] border border-[var(--slate)] p-2.5">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs text-[var(--text-ter)] font-medium">#{alt.rank}</span>
                      <span className="text-sm font-medium text-[var(--text-pri)]">{alt.toolName}</span>
                    </div>
                    <p className="text-xs text-[var(--text-sec)]">{alt.headline}</p>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-[var(--text-sec)] mt-3 text-center">Advisory only — you choose the final method.</p>
          </div>
        )}

        <SituationEscapeNotice recommendation={situationRecommendation} />
        <SituationBasisNote recommendation={situationRecommendation} />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {orderedToolDefinitions.map((tool) => {
            const compat = toolCompatibility[tool.compatibilityId];
            // In DRAFT, picking a method is intent only — full compatibility is enforced when running
            // (sessionStore setSelectedTool + canTransitionSessionState). Keep cards clickable so setup
            // is not blocked by pool/criteria/values gates that apply at run time.
            const blockSelection = session.state !== 'DRAFT' && compat && !compat.canUse;
            const isIncompatible = compat && !compat.canUse;
            const hasWarnings = compat && compat.warnings.length > 0 && compat.canUse;
            const draftSetupHints = session.state === 'DRAFT' && isIncompatible;

            const isToolSelected = selectedCanonicalToolId === tool.compatibilityId;
            return (
              <button
                key={tool.id}
                type="button"
                aria-pressed={isToolSelected}
                onClick={() => {
                  if (blockSelection) return;
                  setSelectedTool(session.id, tool.compatibilityId, { participantCount });
                  recordToolChoice(tool.compatibilityId);
                }}
                disabled={blockSelection}
                className={`
                  p-4 text-left border-2  transition-all
                  ${isToolSelected
                    ? 'border-primary-600 bg-primary-100 shadow-sm'
                    : draftSetupHints
                      ? 'border-[var(--mustard)] bg-[var(--mustard-dim)]/50 hover:border-[var(--mustard)]'
                      : isIncompatible
                        ? 'border-[var(--coral)] bg-[var(--coral-dim)] opacity-60 cursor-not-allowed'
                        : hasWarnings
                          ? 'border-[var(--mustard)] hover:border-[var(--mustard)]'
                          : 'border-[var(--rule)] hover:border-primary-300'
                  }
                `}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className={`font-medium ${isToolSelected ? 'text-primary-950' : 'text-[var(--text-pri)]'}`}>{tool.name}</div>
                  {compat && (
                    <span className={`
                      inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                      ${draftSetupHints ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' : ''}
                      ${!draftSetupHints && compat.status === 'compatible' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : ''}
                      ${!draftSetupHints && compat.status === 'compatible_with_warnings' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' : ''}
                      ${!draftSetupHints && compat.status === 'incompatible' ? 'bg-[var(--coral-dim)] text-[var(--coral)]' : ''}
                    `}>
                      {draftSetupHints && <AlertCircle className="h-3 w-3 mr-1" />}
                      {!draftSetupHints && compat.status === 'compatible' && <CheckCircle className="h-3 w-3 mr-1" />}
                      {!draftSetupHints && compat.status === 'compatible_with_warnings' && <AlertCircle className="h-3 w-3 mr-1" />}
                      {!draftSetupHints && compat.status === 'incompatible' && <X className="h-3 w-3 mr-1" />}
                      {draftSetupHints
                        ? 'Finish setup to run'
                        : compat.status === 'compatible'
                          ? 'Ready'
                          : compat.status === 'compatible_with_warnings'
                            ? 'Warning'
                            : 'Blocked'}
                    </span>
                  )}
                </div>
                <div
                  className={`text-sm mt-1 ${isToolSelected ? 'text-primary-800' : 'text-[var(--text-ter)]'}`}
                >
                  {tool.description}
                </div>
                <ToolSuitabilityDots
                  intent={session.intent}
                  toolId={tool.compatibilityId}
                  selected={isToolSelected}
                  className="mt-2"
                />
                <SituationToolNote
                  recommendation={situationRecommendation}
                  toolId={registryIdToMatrixToolId(tool.compatibilityId)}
                />
                {compat && (compat.blockers.length > 0 || compat.warnings.length > 0) && (
                  <div className="mt-2 text-xs space-y-1">
                    {compat.blockers.map((issue, i) => (
                      <div key={`blocker-${i}`} className="text-[var(--coral)] flex items-start gap-1">
                        <X className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span>{issue.title}</span>
                      </div>
                    ))}
                    {compat.warnings.slice(0, 2).map((issue, i) => (
                      <div key={`warning-${i}`} className="text-[var(--mustard)] flex items-start gap-1">
                        <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span>{issue.title}</span>
                      </div>
                    ))}
                    {compat.warnings.length > 2 && (
                      <div className="text-[var(--mustard)] text-xs">+{compat.warnings.length - 2} more warnings</div>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
      
      {isAsync && !asyncToolSessionId && (
        <p className="text-xs text-center text-[var(--text-ter)]">
          Click "Open for Participants" above to enable submissions
        </p>
      )}
    </div>
  );
}

// Results Tab
function ResultsTab({ session, effectiveParticipantCount, onGoToReview }: TabProps & { effectiveParticipantCount?: number; onGoToReview?: () => void }) {
  const itemHref = useItemHref();
  const items = useItemsStore(state => state.items);
  // S-3: when names are hidden, the record names people only to workspace admins.
  const { rules: roomRules } = useSessionRoomRules(session.id);
  const hideRecordNames = namesHidden(roomRules, 'record');
  const recordName = (name: string, index: number) => (hideRecordNames ? anonymousLabel(index) : name);
  const writeBackOnClose = useWriteBackOnClose();
  const userId = useUserStore(state => state.userId) ?? 'system';
  const userName = useUserStore(state => state.userName) ?? 'User';
  // Brief K slice 1 — the Deliberation Record's rationale notes + thread live on this tab.
  const deliberationWorkspaceId = session.workspaceId ?? getCurrentWorkspaceId() ?? '';
  const deliberationLoaded = useDeliberationStore((s) => s.loadedSessions[session.id]);
  const fetchDeliberation = useDeliberationStore((s) => s.fetchForSession);
  useEffect(() => {
    if (!deliberationLoaded) void fetchDeliberation(session.id);
  }, [deliberationLoaded, fetchDeliberation, session.id]);

  // Async aggregation
  const isAsync = session.modeConfig?.timingMode === 'async';
  const participantResponses = useToolSessionStore(s => s.participantResponses);
  const toolSessions = useToolSessionStore(s => s.toolSessions);
  const computeAggregates = useToolSessionStore(s => s.computeAggregates);
  const subscribeToToolSessionResults = useToolSessionStore(s => s.subscribeToToolSession);
  const promoteSessionItems = useItemsStore(s => s.promoteSessionItems);
  const getEffectiveConfigForCycle = useWorkflowStore(state => state.getEffectiveConfigForCycle);
  const allCycleParticipants = useCycleParticipantsStore(s => s.participants);
  const cycles = useDegapProcessStore(state => state.cycles);

  // Catchball loop for proposal formation
  const workspaceModules = useWorkspaceModules();
  const catchballEnabled = isModuleEnabled(workspaceModules, 'catchball');
  const catchballLoops = useCatchballLoopStore(s => s.loops);
  const fetchLoopsFromSupabase = useCatchballLoopStore(s => s.fetchFromSupabase);
  const getTrackForProposal = useTrackStore(s => s.getTrack);
  const activeLoop = session.trackId
    ? catchballLoops.find(l => l.trackId === session.trackId && normalizeLoopState(l.state) === 'OPEN')
    : null;
  const loopAlreadyProposed = session.trackId
    ? catchballLoops.some(l => l.trackId === session.trackId && normalizeLoopState(l.state) === 'PROPOSED' && l.proposalId)
    : false;
  const proposalTrack = session.trackId ? getTrackForProposal(session.trackId) : null;

  // result_status for proposal entry-point gating (Brief B correction §5)
  const [proposalResultStatus, setProposalResultStatus] = useState<string | null>(null);
  const [proposalResultStatusLoaded, setProposalResultStatusLoaded] = useState(false);

  // Proposal formation drawer state — must be declared before canShowProposalButton calc
  const [showProposalDrawer, setShowProposalDrawer] = useState(false);
  const [proposalSubmittedAt, setProposalSubmittedAt] = useState<string | null>(null);
  const { users: proposalSystemUsers } = useSystemUsers({ workspaceId: session.workspaceId ?? null });
  const facilitatorUser = proposalSystemUsers.find(u => u.id === session.facilitatorId);
  const proposalFacilitatorName = facilitatorUser?.name ?? session.facilitatorId ?? null;

  useEffect(() => {
    if (!session.rankingOutput) { setProposalResultStatusLoaded(true); return; }
    fetchSessionResultStatus(session.id).then(({ resultStatus: rs }) => {
      setProposalResultStatus(rs ?? null);
      setProposalResultStatusLoaded(true);
    });
  }, [session.id, session.rankingOutput]);

  // Named gating reason for "Form Proposal" (§5 — never silently hide)
  const proposalGatingReason: string | null = (() => {
    if (session.state !== 'RUN_COMPLETE' && session.state !== 'PUBLISHED') {
      return 'Run the session to produce a result before forming a proposal';
    }
    if (proposalResultStatusLoaded && proposalResultStatus === 'draft') {
      return 'Publish the result before forming a proposal';
    }
    if (proposalResultStatusLoaded && proposalResultStatus === null && !session.rankingOutput) {
      return null; // no results at all — hide affordance entirely per §4 of correction
    }
    if (!session.trackId) {
      return 'This session is not part of a Catchball loop — proposals require a cycle-linked session';
    }
    const trackLoop = catchballLoops.find(l => l.trackId === session.trackId);
    if (!trackLoop) {
      return 'This session is not part of a Catchball loop — proposals require a cycle-linked session';
    }
    const loopState = normalizeLoopState(trackLoop.state);
    if (loopState !== 'OPEN') {
      return `This loop is not accepting proposals right now: ${(LOOP_STATE_LABELS[trackLoop.state] ?? "closed").toLowerCase()}`;
    }
    return null; // all clear — show button
  })();

  const canShowProposalButton =
    catchballEnabled &&
    proposalResultStatusLoaded &&
    session.rankingOutput != null &&
    (proposalResultStatus === 'complete' || proposalResultStatus === 'published') &&
    proposalGatingReason === null &&
    !loopAlreadyProposed &&
    !proposalSubmittedAt;

  // Publish / provenance state
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [isPromoting, setIsPromoting] = useState(false);
  const [promoteMessage, setPromoteMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showLiftModal, setShowLiftModal] = useState(false);
  const [liftLoopId, setLiftLoopId] = useState<string | undefined>(undefined);
  const [liftTosserId, setLiftTosserId] = useState<string | undefined>(undefined);
  const [publishDisabledByAuth, setPublishDisabledByAuth] = useState(false);
  const [workflowConfig, setWorkflowConfig] = useState<{ resultWriteBack: string } | null>(null);
  // 1001: the cycle's decision right, as publish_session_outcome will judge it —
  // asked only when the session can produce a Priority List.
  const publishRight = useSessionPublishRight(session.cycleId && session.trackId ? session.id : null);


  useEffect(() => {
    if (!session.cycleId) { setWorkflowConfig(null); return; }
    const cycle = cycles.find(c => c.id === session.cycleId);
    if (!cycle) { setWorkflowConfig(null); return; }
    let cancelled = false;
    getEffectiveConfigForCycle(cycle).then(config => {
      if (!cancelled) setWorkflowConfig({ resultWriteBack: config.resultWriteBack });
    });
    return () => { cancelled = true; };
  }, [session.cycleId, cycles, getEffectiveConfigForCycle]);

  useEffect(() => {
    if (!session.cycleId) { setPublishDisabledByAuth(false); return; }
    const cycle = cycles.find(c => c.id === session.cycleId);
    if (!cycle) { setPublishDisabledByAuth(false); return; }
    let cancelled = false;
    import('@/lib/authorization/storeEnforcement').then(({ checkPolicyForCycle }) => {
      checkPolicyForCycle('session.publish', session.cycleId!, cycle as unknown as Record<string, unknown>).then(decision => {
        if (!cancelled) setPublishDisabledByAuth(!decision.allowed);
      });
    });
    return () => { cancelled = true; };
  }, [session.cycleId, cycles]);

  useEffect(() => {
    if (!session.cycleId) return;
    const me = allCycleParticipants.find(p => p.cycleId === session.cycleId && p.userId === userId);
    if (me) {
      setLiftTosserId(me.id);
      // CycleParticipant (stores/prioritization/cycleParticipantsStore) has no
      // loop/level id — this participant's own Catchball loop isn't derivable
      // here yet, so LiftToProposalModal falls back to its own loop resolution.
      setLiftLoopId(undefined);
    }
  }, [session.cycleId, allCycleParticipants, userId]);

  const publishDisabledByWorkflow = workflowConfig?.resultWriteBack === 'none';

  const handlePublish = async () => {
    setIsPublishing(true);
    setPublishError(null);
    try {
      // One lane (739). The old client-side fallback to transitionSessionState
      // is gone: it was the third write lane, and it left a different record
      // set behind than the RPC it was standing in for.
      const { publishSessionResults } = await import('@/stores/prioritization/sessionStore');
      const rpcResult = await publishSessionResults(session.id, {
        actorId: userId || session.facilitatorId,
      });
      if (rpcResult.success) {
        if (session.cycleId && session.rankingOutput?.rankedItemIds?.length) {
          promoteSessionItems(session.id, session.rankingOutput.rankedItemIds, userId).catch(() => null);
        }
        return;
      }
      if (rpcResult.code === 'cancelled') return;
      // Stay on the page and show the server's own reason.
      setPublishError(rpcResult.error || 'Failed to publish');
    } catch (err) {
      setPublishError(err instanceof Error ? err.message : 'Failed to publish');
    } finally {
      // Every path — success included — must release the button.
      setIsPublishing(false);
    }
  };

  const handlePromoteToItems = async () => {
    setPromoteMessage(null);
    setIsPromoting(true);
    try {
      const rankedIds = session.rankingOutput?.rankedItemIds ?? [];
      const result = await promoteSessionItems(session.id, rankedIds, userId);
      if (result.success) {
        const ok = result.succeededIds?.length ?? 0;
        const skipped = result.skippedIds?.length ?? 0;
        if (skipped > 0) {
          setPromoteMessage({ type: 'success', text: `Provenance recorded for ${ok} item${ok !== 1 ? 's' : ''} (${skipped} already had provenance).` });
        } else {
          setPromoteMessage({ type: 'success', text: `Provenance recorded for ${ok} item${ok !== 1 ? 's' : ''}.` });
        }
      } else {
        setPromoteMessage({ type: 'error', text: result.error ?? 'Failed to record provenance.' });
      }
    } catch (err) {
      setPromoteMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to record provenance.' });
    } finally {
      setIsPromoting(false);
    }
  };

  const [asyncToolSessionIdForResults, setAsyncToolSessionIdForResults] = useState<string | null>(null);
  const [isAggregating, setIsAggregating] = useState(false);
  const [aggregateError, setAggregateError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAsync) return;
    let cancelled = false;
    const load = async () => {
      const { getSafeSupabaseClient } = await import('../../../lib/supabaseSync');
      const supabase = getSafeSupabaseClient();
      if (!supabase || cancelled) return;
      const { data } = await supabase
        .from('tool_sessions')
        .select('id')
        .eq('session_id', session.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled && data) {
        setAsyncToolSessionIdForResults((data as { id: string }).id);
        void subscribeToToolSessionResults((data as { id: string }).id);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [isAsync, session.id, subscribeToToolSessionResults]);

  // Human submissions only — Voices never enter "X of Y submitted" vote counts.
  const asyncResponseCount = asyncToolSessionIdForResults
    ? participantResponses.filter(
        (r) =>
          r.toolSessionId === asyncToolSessionIdForResults &&
          r.isComplete &&
          (r.principalType ?? 'user') !== 'voice' &&
          !r.voiceId,
      ).length
    : 0;
  // Prefer the widest honest total: tool-session roster, session roster, effective
  // participants, and unique complete responders (share-link joiners can submit
  // before the roster catches up).
  const asyncToolSession = asyncToolSessionIdForResults
    ? toolSessions.find(ts => ts.id === asyncToolSessionIdForResults)
    : null;
  const totalParticipants = Math.max(
    asyncToolSession?.participantIds.length ?? 0,
    (session.participantIds ?? []).length,
    effectiveParticipantCount ?? 0,
    asyncResponseCount,
  );

  // Divergence surfacing (Gap 1) for the async review surface — derived from the
  // same aggregate computation, no new data.
  const divergenceAggregates = useMemo(
    () => (asyncToolSessionIdForResults ? computeAggregates(asyncToolSessionIdForResults) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [asyncToolSessionIdForResults, asyncResponseCount, participantResponses]
  );
  const divergenceOutputType = asyncToolSession?.outputType ?? 'ranking';

  // Noise audit — computed from per-participant responses when results are available.
  // Uses the tool session responses (which carry individual scores + submission timestamps).
  const noiseAudit = useMemo(() => {
    const rankedItemIds = session.rankingOutput?.rankedItemIds;
    if (!rankedItemIds?.length) return null;

    // Prefer responses from the async tool session; fall back to any complete responses
    const toolSessionId = asyncToolSessionIdForResults ?? '';
    const responses = toolSessionId
      ? participantResponses.filter(r => r.toolSessionId === toolSessionId && r.isComplete)
      : participantResponses.filter(r => r.isComplete);

    if (responses.length < 2) return null;

    const rankedItemTitles: Record<string, string> = {};
    for (const id of rankedItemIds) {
      const item = items.find(c => c.id === id);
      if (item) rankedItemTitles[id] = item.title;
    }

    return computeNoiseAudit({
      sessionId: session.id,
      toolSessionId,
      sessionName: session.name ?? 'Session',
      rankedItemIds,
      rankedItemTitles,
      participantResponses: hideRecordNames
        ? responses.map((r, i) => ({ ...r, participantName: anonymousLabel(i), participantInitials: '' }))
        : responses,
    });
  }, [session.rankingOutput, session.id, session.name, asyncToolSessionIdForResults, participantResponses, items, hideRecordNames]);

  const handleAggregateResponses = async () => {
    if (!asyncToolSessionIdForResults) return;
    setIsAggregating(true);
    setAggregateError(null);
    try {
      // The one counting rule — the same order the app and the live board save.
      const toolId = session.selectedToolId ?? 'pairwise_ranking';
      const rankingOutput = await rankingOutputForToolSession(asyncToolSessionIdForResults, {
        toolId,
        toolName: resolveSessionToolDefinition(toolId)?.name ?? 'Prioritization',
      });
      if (!rankingOutput) {
        setAggregateError('No participant responses to aggregate yet.');
        return;
      }
      // One act, one write path (771): run_session writes the session's
      // ranking_output, the RUN_COMPLETE state and the session_results rows in
      // one transaction.
      const { runSessionViaRpc } = await import('@/stores/prioritization/sessionStore');
      const result = await runSessionViaRpc(session.id, rankingOutput);
      if (!result.success) {
        setAggregateError(result.error ?? 'Failed to save aggregated results');
        return;
      }
      await writeBackOnClose({ session, toolSessionId: asyncToolSessionIdForResults });
    } catch (err) {
      setAggregateError(err instanceof Error ? err.message : 'Aggregation failed');
    } finally {
      setIsAggregating(false);
    }
  };
  // Report generation state
  const [reportTemplates, setReportTemplates] = useState<ReportTemplate[]>(SYSTEM_REPORT_TEMPLATES);
  const [showReportPicker, setShowReportPicker] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [generatedArtifacts, setGeneratedArtifacts] = useState<ReportArtifact[]>([]);

  // Item relationships for context
  const edges = useItemRelationshipsStore(s => s.edges);
  const tracks = useTrackStore(state => state.tracks);



  const buildExplainerContext = (
    rankedItemIds: string[],
    scores: Record<string, Record<string, number>>
  ): ResultsExplainerContext => {
    const rankMap = new Map<string, number>(rankedItemIds.map((id, i) => [id, i + 1]));
    const sessionItemIds = new Set(rankedItemIds);

    const relevantEdges = edges.filter(
      e => sessionItemIds.has(e.sourceItemId) && sessionItemIds.has(e.targetItemId)
    );

    const rankedItems = rankedItemIds.map((id, idx) => {
      const itemData = items.find(c => c.id === id);
      const itemScores = scores[id] ?? {};
      const score = itemScores.total ?? Object.entries(itemScores).reduce(
        (sum, [k, v]) => (k === 'total' ? sum : sum + v), 0
      );

      const itemEdges = relevantEdges.filter(
        e => e.sourceItemId === id || e.targetItemId === id
      );

      const relationships = itemEdges.map(edge => {
        const isSource = edge.sourceItemId === id;
        const otherItemId = isSource ? edge.targetItemId : edge.sourceItemId;
        const otherItem = items.find(c => c.id === otherItemId);
        type RelType = 'blocks' | 'blocked_by' | 'depends_on' | 'dependency_of' | 'aggregates' | 'part_of';
        let relType: RelType;
        if (edge.type === 'dependency') {
          relType = isSource ? 'depends_on' : 'dependency_of';
        } else if (edge.type === 'aggregation') {
          relType = isSource ? 'aggregates' : 'part_of';
        } else {
          relType = isSource ? 'blocks' : 'blocked_by';
        }
        return {
          type: relType,
          otherItemTitle: otherItem?.title ?? otherItemId,
          otherItemRank: rankMap.get(otherItemId),
          status: edge.status,
        };
      });

      return {
        rank: idx + 1,
        itemId: id,
        title: itemData?.title ?? id,
        score,
        relationships: relationships.length ? relationships : undefined,
      };
    });

    const activeRelationships = relevantEdges.map(edge => {
      const sourceItem = items.find(c => c.id === edge.sourceItemId);
      const targetItem = items.find(c => c.id === edge.targetItemId);
      return {
        sourceTitle: sourceItem?.title ?? edge.sourceItemId,
        sourceRank: rankMap.get(edge.sourceItemId),
        targetTitle: targetItem?.title ?? edge.targetItemId,
        targetRank: rankMap.get(edge.targetItemId),
        relationshipType: edge.type,
        status: edge.status,
      };
    });

    const cycle = cycles.find(c => c.id === session.cycleId);
    const track = tracks.find(t => t.id === session.trackId);

    return {
      sessionName: session.name ?? 'Session',
      sessionIntent: 'rank_priorities',
      toolName: session.selectedToolId ?? 'Unknown method',
      participantCount: session.participantIds?.length ?? 1,
      cycleName: cycle?.name,
      trackName: track?.name,
      criteriaIds: session.criteriaConfig.criteriaIds,
      criteriaWeights: session.criteriaConfig.weights,
      totalItems: rankedItemIds.length,
      cutLinePosition: session.cutLinePosition,
      rankedItems,
      activeRelationships,
    };
  };

  const buildBiasContext = (rankedItemIds: string[]): BiasAnalysisContext => {
    const cycle = cycles.find(c => c.id === session.cycleId);
    return {
      analysisScope: 'session',
      sessionName: session.name ?? 'Session',
      sessionTool: session.selectedToolId ?? undefined,
      cycleName: cycle?.name,
      items: rankedItemIds.map((id, idx) => {
        const item = items.find(c => c.id === id);
        return {
          id,
          title: item?.title ?? id,
          category: (item as unknown as Record<string, string>)?.itemLanguageType,
          bucket: (item as unknown as Record<string, string>)?.strategicBucket,
          rank: idx + 1,
          steward: (item as unknown as Record<string, string>)?.stewardId,
        };
      }),
      participants: (session.participantIds ?? []).map(pid => ({ id: pid })),
      criteriaLabels: session.criteriaConfig?.criteriaIds ?? [],
      cutLinePosition: session.cutLinePosition ?? undefined,
    };
  };

  const handleGenerateReport = async (template: ReportTemplate) => {
    if (!session.rankingOutput) return;
    setShowReportPicker(false);
    setIsGeneratingReport(true);
    setReportError(null);
    try {
      const ctx = buildExplainerContext(
        session.rankingOutput.rankedItemIds,
        session.rankingOutput.scores
      );
      const artifact = await generateReportArtifact(
        session.id,
        template,
        ctx,
        userId,
        session.workspaceId
      );
      setGeneratedArtifacts(prev => [artifact, ...prev]);
    } catch (e) {
      setReportError(e instanceof Error ? e.message : 'Failed to generate report.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Load templates and existing artifacts on mount
  useEffect(() => {
    fetchReportTemplates(session.workspaceId).then(setReportTemplates).catch(() => {
      setReportTemplates(SYSTEM_REPORT_TEMPLATES);
    });
    fetchArtifactsForSession(session.id).then(setGeneratedArtifacts).catch(() => {});
  }, [session.id, session.workspaceId]);

  const [showIndividualResponses, setShowIndividualResponses] = useState(false);
  const [showVoiceContributions, setShowVoiceContributions] = useState(false);
  const asyncSubmittedResponses = asyncToolSessionIdForResults
    ? participantResponses.filter(r => r.toolSessionId === asyncToolSessionIdForResults && r.isComplete)
    : [];
  const asyncHumanResponses = asyncSubmittedResponses.filter(
    (r) => (r.principalType ?? 'user') !== 'voice' && !r.voiceId,
  );
  const asyncVoiceResponses = asyncSubmittedResponses.filter(
    (r) => r.principalType === 'voice' || Boolean(r.voiceId),
  );

  const AsyncParticipantPanel = () => (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5 space-y-4">
      {/* Header row */}
      <div className="flex items-center gap-2">
        <Users className="h-5 w-5 text-primary-500" />
        <h3 className="text-base font-semibold text-[var(--text-pri)]">Participant Responses</h3>
        <span className="ml-auto text-sm text-[var(--text-ter)]">{asyncResponseCount} of {totalParticipants} submitted</span>
      </div>

      {aggregateError && (
        <p className="text-sm text-[var(--coral)] bg-[var(--coral-dim)] rounded px-3 py-2">{aggregateError}</p>
      )}

      {/* Individual responses toggle */}
      {asyncHumanResponses.length > 0 && (
        <div>
          <button
            onClick={() => setShowIndividualResponses(v => !v)}
            className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${showIndividualResponses ? 'rotate-180' : ''}`} />
            {showIndividualResponses ? 'Hide' : 'Show'} individual responses
          </button>

          {showIndividualResponses && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {asyncHumanResponses.map((resp, respIndex) => {
                const sorted = [...resp.results].sort((a, b) => {
                  if (a.rank !== undefined && b.rank !== undefined) return a.rank - b.rank;
                  return (b.score ?? 0) - (a.score ?? 0);
                });
                return (
                  <div key={resp.participantId} className="border border-[var(--rule)] bg-[var(--surface)] p-3">
                    <p className="text-xs font-semibold text-[var(--text-sec)] mb-2 truncate">{recordName(resp.participantName, respIndex)}</p>
                    <ol className="space-y-1">
                      {sorted.map((item, idx) => (
                        <li key={item.id} className="flex items-center gap-2 text-xs text-[var(--text-sec)]">
                          <span className="w-4 text-right text-[var(--text-ter)] flex-shrink-0">{idx + 1}.</span>
                          <span className="flex-1 truncate">{item.name}</span>
                          {item.score !== undefined && (
                            <span className="text-[var(--text-ter)] flex-shrink-0">{item.score}</span>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {asyncVoiceResponses.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowVoiceContributions((v) => !v)}
            className="flex items-center gap-1.5 text-sm text-electric-mustard-800 hover:text-electric-mustard-900 font-medium transition-colors"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${showVoiceContributions ? 'rotate-180' : ''}`} />
            {showVoiceContributions ? 'Hide' : 'Show'} Voice context ({asyncVoiceResponses.length})
          </button>
          {showVoiceContributions && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {asyncVoiceResponses.map((resp) => (
                <VoiceContributionCard key={resp.participantId} response={resp} compact />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action row */}
      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={handleAggregateResponses}
          disabled={isAggregating || asyncHumanResponses.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isAggregating ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart3 className="h-4 w-4" />}
          {isAggregating
            ? 'Aggregating…'
            : `Aggregate ${asyncHumanResponses.length} Response${asyncHumanResponses.length !== 1 ? 's' : ''}`}
        </button>
        {asyncHumanResponses.length === 0 && (
          <p className="text-xs text-[var(--text-ter)]">Waiting for participants to submit their rankings.</p>
        )}
      </div>
    </div>
  );

  if (!sessionHasPrioritizationResults(session)) {
    return (
      <div className="space-y-4">
        <SessionItemChangesNote sessionId={session.id} />
        {isAsync && asyncToolSessionIdForResults && <AsyncParticipantPanel />}
        <FairnessFlagPanel sessionId={session.id} />
        <div className="text-center py-12 bg-[var(--bg-raised)] border border-[var(--rule)]">
          <BarChart3 className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[var(--text-pri)] mb-2">No results yet</h3>
          <p className="text-[var(--text-sec)]">
            {isAsync ? 'Aggregate participant responses above to see combined results' : 'Run a prioritization method to see results'}
          </p>
        </div>
      </div>
    );
  }
  
  const { rankedItemIds, scores } = session.rankingOutput!;

  return (
    <div className="space-y-6">
      <SessionItemChangesNote sessionId={session.id} />

      {/* Async: re-aggregate panel when results already exist */}
      {isAsync && asyncToolSessionIdForResults && asyncResponseCount > 0 && (
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-[var(--text-sec)]">
              {asyncResponseCount} of {totalParticipants} participant{totalParticipants !== 1 ? 's' : ''} have submitted their ranking
            </p>
            {asyncResponseCount < totalParticipants && (
              <p className="text-xs text-[var(--text-sec)] mt-0.5">Re-aggregate when all responses are in to include everyone.</p>
            )}
          </div>
          <button
            onClick={handleAggregateResponses}
            disabled={isAggregating}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50 transition-colors"
          >
            {isAggregating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {isAggregating ? 'Aggregating…' : 'Re-aggregate'}
          </button>
        </div>
      )}
      {aggregateError && (
        <p className="text-sm text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] px-3 py-2">{aggregateError}</p>
      )}

      {/* Divergence + process-fairness — same surfaces as the live review, for async facilitators */}
      {asyncToolSessionIdForResults && divergenceAggregates.length > 0 && (
        <DivergencePanel
          aggregates={divergenceAggregates}
          outputType={divergenceOutputType}
          participantCount={asyncResponseCount}
        />
      )}
      <FairnessFlagPanel sessionId={session.id} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-[var(--text-pri)]">Results</h3>
          <p className="text-sm text-[var(--text-ter)]">
            {rankedItemIds.length} items
            {(session.rankingOutput as { aggregatedFrom?: number })?.aggregatedFrom
              ? ` · aggregated from ${(session.rankingOutput as { aggregatedFrom?: number }).aggregatedFrom} responses`
              : ''}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* The prioritization memo replaced the inline Explain panel (DEP-032). */}
          <SessionMemoAction sessionId={session.id} />

          {/* Generate Report — dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowReportPicker(v => !v)}
              disabled={isGeneratingReport}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors disabled:opacity-60"
            >
              {isGeneratingReport ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileText className="h-3.5 w-3.5" />
              )}
              {isGeneratingReport ? 'Generating…' : 'Generate Report'}
              {!isGeneratingReport && <ChevronDown className="h-3 w-3" />}
            </button>
            {showReportPicker && (
              <div className="absolute right-0 mt-1 w-64 bg-[var(--bg-raised)] border border-[var(--rule)] shadow-xl z-20 overflow-hidden">
                {reportTemplates.map(template => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => handleGenerateReport(template)}
                    className="w-full flex flex-col px-4 py-3 text-left hover:bg-[var(--surface)] transition-colors border-b border-[var(--rule)] last:border-0"
                  >
                    <span className="text-sm font-medium text-[var(--text-pri)]">{template.name}</span>
                    {template.description && (
                      <span className="text-xs text-[var(--text-ter)] mt-0.5">{template.description}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <span className="w-px h-5 bg-[var(--hover-tint)]" />

          {/* Form Proposal — named gating (§5: never silently hide) */}
          {canShowProposalButton && (
            <button
              onClick={() => setShowProposalDrawer(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] border border-[var(--slate)] bg-[var(--slate-dim)] hover:bg-[var(--slate-dim)] transition-colors"
            >
              <ScrollText className="h-3.5 w-3.5" />
              Form Proposal
            </button>
          )}

          {/* Named missing-input message when conditions aren't met (§5) */}
          {!canShowProposalButton && !loopAlreadyProposed && !proposalSubmittedAt &&
           proposalResultStatusLoaded && session.rankingOutput != null &&
           proposalGatingReason && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-[var(--mustard)] border border-[var(--mustard)] bg-[var(--mustard-dim)]">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
              {proposalGatingReason}
            </span>
          )}

          {/* Proposal Submitted badge with provenance (§3) */}
          {(proposalSubmittedAt || loopAlreadyProposed) && (
            <span className="inline-flex flex-col gap-0.5 px-3 py-1.5 text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)] bg-[var(--green-dim)]">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Proposal Submitted
                {proposalSubmittedAt && (
                  <span className="text-xs font-normal text-[var(--green)] dark:text-[var(--text-ter)]">
                    {new Date(proposalSubmittedAt).toLocaleDateString()}
                  </span>
                )}
              </span>
              {activeLoop && (
                <span className="text-xs font-normal text-[var(--green)] dark:text-[var(--text-ter)] pl-5">
                  {userName} · Loop {activeLoop.loopNumber ?? '#'}
                  {activeLoop.cycleId && cycles.find(c => c.id === activeLoop.cycleId)
                    ? ` · ${cycles.find(c => c.id === activeLoop.cycleId)?.name}`
                    : ''}
                </span>
              )}
            </span>
          )}

          <span className="w-px h-5 bg-[var(--hover-tint)]" />
          <button
            onClick={onGoToReview}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 border border-primary-300 bg-primary-50 hover:bg-primary-100 transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" />
            Review →
          </button>
        </div>
      </div>

      {/* Post-session decisions panel — bracket destination + publish/provenance actions */}
      {session.state !== 'PUBLISHED' && (
        <PostSessionActionsPanel
          session={session}
          onPublish={handlePublish}
          onLiftToProposal={() => setShowLiftModal(true)}
          onRecordProvenance={handlePromoteToItems}
          isPublishing={isPublishing}
          isPromoting={isPromoting}
          publishDisabledByAuth={publishDisabledByAuth}
          publishDisabledByWorkflow={publishDisabledByWorkflow}
          publishRightLine={refusedByRight(publishRight) ? (publishRight.line ?? 'You may not publish these results') : null}
          publishError={publishError}
          promoteMessage={promoteMessage}
          rankedItemCount={rankedItemIds.length}
        />
      )}

      {/* This ranking next to another session's, for the items both ranked */}
      <SessionRankComparison session={session} />

      {/* Ranked items — immediate visibility of what was produced */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        <table className="w-full">
          <thead className="bg-[var(--surface)] border-b border-[var(--rule)]">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-[var(--text-ter)] w-12">Rank</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-[var(--text-ter)]">Item</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-[var(--text-ter)] w-28">Why</th>
              <th className="text-right px-4 py-2.5 text-xs font-medium text-[var(--text-ter)] w-20">Score</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--rule)]">
            {rankedItemIds.map((itemId, index) => {
              const item = items.find(c => c.id === itemId);
              const itemScores = scores[itemId] ?? {};
              const total = itemScores.total ?? Object.entries(itemScores).reduce(
                (sum, [k, v]) => (k === 'total' ? sum : sum + v), 0
              );
              const isBelowCutLine = session.cutLinePosition !== undefined && index >= session.cutLinePosition;
              return (
                <React.Fragment key={itemId}>
                  {session.cutLinePosition === index && (
                    <tr key={`cut-${index}`}>
                      <td colSpan={4} className="px-4 py-1.5 bg-[var(--mustard-dim)]">
                        <div className="flex items-center gap-2 text-[var(--mustard)]">
                          <div className="flex-1 h-px bg-[var(--mustard)]" />
                          <span className="text-xs font-medium">Cut Line</span>
                          <div className="flex-1 h-px bg-[var(--mustard)]" />
                        </div>
                      </td>
                    </tr>
                  )}
                  <tr className={`hover:bg-[var(--surface)] ${isBelowCutLine ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-2.5 text-sm font-bold text-[var(--text-ter)]">#{index + 1}</td>
                    <td className="px-4 py-2.5">
                      <Link
                        to={itemHref(itemId)}
                        className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-600"
                      >
                        {item?.title ?? itemId}
                      </Link>
                      <ItemRelationshipFlags itemId={itemId} className="ml-2" />
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <ItemRationaleCell sessionId={session.id} workspaceId={deliberationWorkspaceId} itemId={itemId} canWrite={!!userId && userId !== 'system'} />
                    </td>
                    <td className="px-4 py-2.5 text-right text-sm text-[var(--text-ter)]">
                      {total > 0 ? Number(total.toFixed(1)) : '—'}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        <TranscriptLinksPanel
          sessionId={session.id}
          workspaceId={deliberationWorkspaceId}
          items={rankedItemIds.map((id) => ({ id, title: items.find((c) => c.id === id)?.title ?? 'Untitled item' }))}
          canAct={!!userId && userId !== 'system'}
        />
        <div className="mt-4">
          <SessionDeliberationThread sessionId={session.id} workspaceId={deliberationWorkspaceId} canWrite={!!userId && userId !== 'system'} />
        </div>
      </div>

      {/* Noise Audit — shown when per-participant response data is available */}
      {noiseAudit && <NoiseAuditPanel audit={noiseAudit} />}

      {/* Cognitive Bias Check — shown when results are available */}
      {session.rankingOutput?.rankedItemIds?.length ? (
        <CognitiveBiasPanel
          context={buildBiasContext(session.rankingOutput.rankedItemIds)}
          contextLabel={session.name ?? 'Session Results'}
        />
      ) : null}

      {/* Report generation error */}
      {reportError && (
        <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-3 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[var(--mustard)] flex-1">{reportError}</p>
          <button onClick={() => setReportError(null)} className="p-0.5 hover:bg-[var(--mustard-dim)] rounded">
            <X className="h-3.5 w-3.5 text-[var(--mustard)]" />
          </button>
        </div>
      )}

      {/* Generated Report Artifacts */}
      {generatedArtifacts.length > 0 && (
        <div className="border border-[var(--rule)] bg-[var(--surface)] p-4 space-y-2">
          <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">Generated Reports</p>
          {generatedArtifacts.map(artifact => (
            <div key={artifact.id} className="flex items-center justify-between gap-3 bg-[var(--bg-raised)] border border-[var(--rule)] px-3 py-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                <span className="text-sm font-medium text-[var(--text-pri)] truncate">{artifact.title}</span>
              </div>
              <Link
                to={`/prioritization/sessions/${session.id}/reports/${artifact.id}`}
                className="flex-shrink-0 text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                View →
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* ── Score breakdown table ────────────────────────────────────────────── */}
      {(() => {
        const submittedResponses = asyncToolSessionIdForResults
          ? participantResponses.filter(r => r.toolSessionId === asyncToolSessionIdForResults && r.isComplete)
          : [];
        if (submittedResponses.length === 0) return null;

        return (
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-x-auto">
            <div className="px-4 py-3 border-b border-[var(--rule)] flex items-center justify-between">
              <p className="text-sm font-medium text-[var(--text-pri)]">Score Breakdown</p>
              <p className="text-xs text-[var(--text-ter)]">{submittedResponses.length} participant{submittedResponses.length !== 1 ? 's' : ''}</p>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[var(--surface)] border-b border-[var(--rule)]">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium text-[var(--text-sec)] w-8">#</th>
                  <th className="text-left px-4 py-2.5 font-medium text-[var(--text-sec)]">Item</th>
                  {submittedResponses.map((r, rIndex) => (
                    <th key={r.participantId} className="text-center px-3 py-2.5 font-medium text-[var(--text-ter)] text-xs whitespace-nowrap max-w-[100px]">
                      <span className="block truncate" title={recordName(r.participantName, rIndex)}>{recordName(r.participantName, rIndex).split(' ')[0]}</span>
                    </th>
                  ))}
                  <th className="text-center px-4 py-2.5 font-semibold text-[var(--text-sec)] whitespace-nowrap">Avg</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)]">
                {rankedItemIds.map((itemId, index) => {
                  const item = items.find(c => c.id === itemId);
                  const aggScore = (scores[itemId] as Record<string, number>)?.total;
                  const participantScores = submittedResponses.map(r => {
                    const result = r.results.find(ri => ri.id === itemId);
                    if (!result) return undefined;
                    if (result.score !== undefined) return result.score;
                    if (result.rank !== undefined) {
                      const n = r.results.length;
                      return n - result.rank + 1;
                    }
                    return undefined;
                  });
                  const maxScore = Math.max(
                    ...submittedResponses.flatMap(r =>
                      r.results.map(ri => ri.score ?? (ri.rank !== undefined ? r.results.length - ri.rank + 1 : 0))
                    )
                  );
                  return (
                    <tr key={itemId} className="hover:bg-[var(--surface)]">
                      <td className="px-4 py-2.5 text-xs font-bold text-[var(--text-ter)]">#{index + 1}</td>
                      <td className="px-4 py-2.5 text-[var(--text-pri)] font-medium">{item?.title || itemId}</td>
                      {participantScores.map((score, si) => (
                        <td key={si} className="px-3 py-2.5 text-center">
                          {score !== undefined ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="font-medium text-[var(--text-pri)]">{score}</span>
                              {maxScore > 0 && (
                                <div className="w-8 h-1 bg-[var(--hover-tint)] rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary-400 rounded-full"
                                    style={{ width: `${(score / maxScore) * 100}%` }}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-[var(--text-ter)]">—</span>
                          )}
                        </td>
                      ))}
                      <td className="px-4 py-2.5 text-center">
                        <span className="font-semibold text-[var(--text-pri)]">
                          {aggScore !== undefined ? Math.round(aggScore * 10) / 10 : '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Lift to Proposal modal */}
      {showLiftModal && session.cycleId && (
        <LiftToProposalModal
          isOpen={showLiftModal}
          onClose={() => setShowLiftModal(false)}
          cycleId={session.cycleId}
          cycleName={cycles.find(c => c.id === session.cycleId)?.name}
          loopId={liftLoopId}
          tosserId={liftTosserId}
          defaultSessionId={session.id}
          onLifted={() => { /* stay open — modal shows success state */ }}
        />
      )}

      {/* Proposal Formation Drawer */}
      {showProposalDrawer && activeLoop && (
        <ProposalFormationDrawer
          isOpen={showProposalDrawer}
          onClose={() => setShowProposalDrawer(false)}
          sessionId={session.id}
          sessionName={session.name}
          sessionDate={(session.updatedAt ?? session.createdAt)?.toISOString()}
          facilitatorName={proposalFacilitatorName ?? undefined}
          resultStatus={proposalResultStatus ?? undefined}
          loopId={activeLoop.id}
          loopNumber={activeLoop.loopNumber}
          cycleId={activeLoop.cycleId}
          cycleName={cycles.find(c => c.id === activeLoop.cycleId)?.name}
          trackId={session.trackId ?? undefined}
          trackName={proposalTrack?.name}
          rankedItemIds={rankedItemIds}
          scores={scores}
          actorId={userId}
          actorName={userName}
          onProposalSubmitted={(_proposalId, submittedAt) => {
            setProposalSubmittedAt(submittedAt);
            setShowProposalDrawer(false);
            // Re-fetch loop state from DB — no optimistic update (§2.3a)
            if (session.cycleId) {
              fetchLoopsFromSupabase(session.cycleId).catch(() => null);
            }
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Review Tab — drag-to-reorder and annotate items before finalizing
// ============================================================================
function ReviewTab({ session, onGoToRanking }: TabProps & { onGoToRanking?: () => void }) {
  const items = useItemsStore(state => state.items);
  const updateSession = useSessionStore(s => s.updateSession);

  const rankedItemIds = session.rankingOutput?.rankedItemIds ?? [];
  const [arrangedIds, setArrangedIds] = useState<string[]>([...rankedItemIds]);
  const [itemNotes, setItemNotes] = useState<Record<string, string>>(
    (session.rankingOutput as { itemNotes?: Record<string, string> } | null)?.itemNotes ?? {}
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const dragItemRef = useRef<number | null>(null);
  const dragOverRef = useRef<number | null>(null);

  // Re-sync when rankingOutput changes (e.g. after a fresh aggregate)
  useEffect(() => {
    setArrangedIds([...rankedItemIds]);
    setItemNotes((session.rankingOutput as { itemNotes?: Record<string, string> } | null)?.itemNotes ?? {});
    setHasChanges(false);
    setSaved(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.rankingOutput]);

  if (rankedItemIds.length === 0) {
    return (
      <div className="text-center py-16 bg-[var(--bg-raised)] border border-[var(--rule)]">
        <Pencil className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
        <h3 className="text-lg font-medium text-[var(--text-pri)] mb-2">No results to review</h3>
        <p className="text-[var(--text-ter)] text-sm">Run the prioritization tool first, then come back here to reorder and annotate.</p>
      </div>
    );
  }

  const handleSave = async () => {
    setIsSaving(true);
    const newOutput = {
      ...session.rankingOutput!,
      rankedItemIds: arrangedIds,
      itemNotes,
    };
    await updateSession(session.id, { rankingOutput: newOutput });
    setIsSaving(false);
    setSaved(true);
    setHasChanges(false);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-[var(--text-pri)]">Review & Arrange</h3>
          <p className="text-sm text-[var(--text-ter)]">Drag rows to reorder · add notes · save before moving to Ranking</p>
        </div>
        <div className="flex items-center gap-2">
          {saved && !hasChanges && (
            <span className="flex items-center gap-1.5 text-xs text-[var(--green)] dark:text-[var(--text-ter)] font-medium">
              <CheckCircle className="h-3.5 w-3.5" /> Saved
            </span>
          )}
          {hasChanges && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {isSaving ? 'Saving…' : 'Save arrangement'}
            </button>
          )}
          {onGoToRanking && (
            <button
              onClick={onGoToRanking}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 border border-primary-300 bg-primary-50 hover:bg-primary-100 transition-colors"
            >
              <ListOrdered className="h-3.5 w-3.5" />
              Ranking →
            </button>
          )}
        </div>
      </div>

      {/* Draggable list */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        <div className="divide-y divide-[var(--rule)]">
          {arrangedIds.map((itemId, index) => {
            const item = items.find(c => c.id === itemId);
            const isBelowCutLine = session.cutLinePosition !== undefined && index >= session.cutLinePosition;
            return (
              <React.Fragment key={itemId}>
                {session.cutLinePosition === index && (
                  <div className="px-4 py-2 bg-[var(--mustard-dim)] border-b border-[var(--mustard)]">
                    <div className="flex items-center gap-2 text-[var(--mustard)]">
                      <div className="flex-1 h-px bg-[var(--mustard)]" />
                      <span className="text-xs font-medium">Cut Line</span>
                      <div className="flex-1 h-px bg-[var(--mustard)]" />
                    </div>
                  </div>
                )}
                <div
                  draggable
                  onDragStart={() => { dragItemRef.current = index; }}
                  onDragEnter={() => { dragOverRef.current = index; }}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => {
                    const from = dragItemRef.current;
                    const to = dragOverRef.current;
                    if (from === null || to === null || from === to) return;
                    const next = [...arrangedIds];
                    const [moved] = next.splice(from, 1);
                    next.splice(to, 0, moved);
                    setArrangedIds(next);
                    setHasChanges(true);
                    setSaved(false);
                    dragItemRef.current = null;
                    dragOverRef.current = null;
                  }}
                  onDragEnd={() => {
                    dragItemRef.current = null;
                    dragOverRef.current = null;
                  }}
                  className={`flex items-center gap-3 px-4 py-3 cursor-grab active:cursor-grabbing select-none transition-colors hover:bg-[var(--surface)] ${
                    isBelowCutLine ? 'opacity-50' : ''
                  }`}
                >
                  <GripVertical className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
                  <span className="w-7 text-xs font-bold text-[var(--text-ter)] flex-shrink-0 text-right">
                    #{index + 1}
                  </span>
                  <span className={`flex-1 text-sm font-medium truncate ${isBelowCutLine ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>
                    {item?.title ?? itemId}
                  </span>
                  <input
                    type="text"
                    placeholder="Add note…"
                    value={itemNotes[itemId] ?? ''}
                    onChange={e => {
                      setItemNotes(prev => ({ ...prev, [itemId]: e.target.value }));
                      setHasChanges(true);
                      setSaved(false);
                    }}
                    onClick={e => e.stopPropagation()}
                    onDragStart={e => e.stopPropagation()}
                    className="w-40 flex-shrink-0 text-xs px-2 py-1 rounded border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] placeholder-neutral-300 focus:outline-none focus:ring-1 focus:ring-primary-300 focus:border-primary-300"
                  />
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PostSessionActionsPanel — structured "what do you want to do?" panel
// Replaces the flat button group in RankingTab when session is RUN_COMPLETE.
// Two explicit concerns: (1) session result destination, (2) items provenance.
// ============================================================================

interface PostSessionActionsPanelProps {
  session: PrioritizationSession;
  onPublish: () => void;
  onLiftToProposal: () => void;
  onRecordProvenance: () => void;
  isPublishing: boolean;
  isPromoting: boolean;
  publishDisabledByAuth: boolean;
  publishDisabledByWorkflow: boolean;
  /** 1001: the database's sentence when the cycle's decision right shuts this person out of publishing; the button is not drawn. */
  publishRightLine?: string | null;
  publishError: string | null;
  promoteMessage: { type: 'success' | 'error'; text: string } | null;
  rankedItemCount: number;
}

function PostSessionActionsPanel({
  session,
  onPublish,
  onLiftToProposal,
  onRecordProvenance,
  isPublishing,
  isPromoting,
  publishDisabledByAuth,
  publishDisabledByWorkflow,
  publishRightLine = null,
  publishError,
  promoteMessage,
  rankedItemCount,
}: PostSessionActionsPanelProps) {
  const cycles = useDegapProcessStore(s => s.cycles);
  const getTrack = useTrackStore(s => s.getTrack);

  const cycle = session.cycleId ? cycles.find(c => c.id === session.cycleId) : null;
  const track = session.trackId ? getTrack(session.trackId) : null;
  const hasCycleContext = !!(session.cycleId || session.trackId);

  // One label everywhere (739). This said "Publish to <track>" / "Publish to
  // cycle" / "Publish results" depending on binding — three names for one act,
  // on a button that now always runs the same RPC. The destination belongs in
  // the context header above, not in the verb.
  const publishLabel = PUBLISH_RESULTS_LABEL;

  // Items section: when linked to a cycle, provenance is auto-recorded on publish
  const provenanceAutoRecorded = !!session.cycleId;

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
      {/* Bracket context header — shows which slot in the cycle this result fills */}
      <div className="px-5 py-4 bg-[var(--surface)] border-b border-[var(--rule)]">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-1">
              Result destination
            </p>
            {hasCycleContext ? (
              <div className="flex items-center gap-1.5 flex-wrap">
                {track ? (
                  <span className="text-sm font-semibold text-[var(--text-pri)]">{track.name}</span>
                ) : null}
                {track && cycle ? (
                  <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                ) : null}
                {cycle ? (
                  <span className="text-sm text-[var(--text-sec)]">{cycle.name}</span>
                ) : null}
                {!track && !cycle && (
                  <span className="text-sm text-[var(--text-ter)]">Cycle session</span>
                )}
              </div>
            ) : (
              <p className="text-sm text-[var(--text-ter)]">
                Standalone session — not linked to a cycle or track
              </p>
            )}
          </div>
          <SessionStateBadge state={session.state} />
        </div>
      </div>

      {/* Two-section body: session result | items */}
      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-[var(--rule)]">

        {/* ── Section 1: Session result ─────────────────────────────────────── */}
        <div className="px-5 py-5 space-y-4">
          <div>
            <p className="text-sm font-semibold text-[var(--text-pri)]">Session result</p>
            <p className="text-xs text-[var(--text-ter)] mt-0.5 leading-relaxed">
              {hasCycleContext
                ? 'Publish to record this ranking in the cycle, or lift it into the catchball chain for ratification.'
                : 'Publish to finalize this ranking and record it as a decision.'}
            </p>
          </div>

          <div className="space-y-2">
            {/* 1001: not drawn for a person the cycle's decision right shuts out; the database's own sentence instead */}
            {publishRightLine ? (
              <p className="text-sm text-[var(--text-ter)]">{publishRightLine}</p>
            ) : (
            <button
              type="button"
              onClick={onPublish}
              disabled={isPublishing || publishDisabledByAuth || publishDisabledByWorkflow}
              title={
                publishDisabledByAuth
                  ? 'You do not have permission to publish'
                  : publishDisabledByWorkflow
                    ? 'Result write-back is disabled by the workflow configuration'
                    : undefined
              }
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPublishing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {isPublishing ? 'Publishing…' : publishLabel}
            </button>
            )}

            {session.cycleId && (
              <button
                type="button"
                onClick={onLiftToProposal}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-[var(--text-sec)] border border-[var(--slate)] bg-[var(--slate-dim)] hover:bg-[var(--slate-dim)] transition-colors"
              >
                <ScrollText className="h-4 w-4" />
                Lift to Proposal
              </button>
            )}
          </div>

          {publishError && (
            <p className="text-xs text-[var(--coral)]">{publishError}</p>
          )}
          {publishDisabledByAuth && !publishError && (
            <p className="text-xs text-[var(--mustard)]">You do not have permission to publish</p>
          )}
        </div>

        {/* ── Section 2: Items ──────────────────────────────────────────────── */}
        <div className="px-5 py-5 space-y-4">
          <div>
            <p className="text-sm font-semibold text-[var(--text-pri)]">Items</p>
            <p className="text-xs text-[var(--text-ter)] mt-0.5 leading-relaxed">
              {provenanceAutoRecorded
                ? 'This session will be recorded as the provenance source on each ranked item when you publish.'
                : 'Record this session as the decision source for each ranked item. Items stay in place — this captures the history.'}
            </p>
          </div>

          {!provenanceAutoRecorded && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={onRecordProvenance}
                disabled={isPromoting || rankedItemCount === 0}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors disabled:opacity-50"
              >
                {isPromoting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ClipboardList className="h-4 w-4" />
                )}
                {isPromoting ? 'Recording…' : 'Record provenance'}
              </button>
            </div>
          )}

          {provenanceAutoRecorded && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-[var(--surface)] border border-[var(--rule)]">
              <CheckCircle className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[var(--text-ter)]">
                Happens automatically on publish — no separate step needed.
              </p>
            </div>
          )}

          {promoteMessage && (
            <p className={`text-xs ${promoteMessage.type === 'success' ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]'}`}>
              {promoteMessage.text}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Ranking Tab — clean published view with actions
// ============================================================================

function RankingTab({ session }: TabProps & { effectiveParticipantCount?: number }) {
  const itemHref = useItemHref();
  const setCutLine = useSessionStore(s => s.setCutLine);

  // Cut line: local while dragging, one governed commit on release (setCutLine
  // carries the state gate, the lists.move_cutline policy and the decision record).
  const [cutLineDraft, setCutLineDraft] = useState<number | null>(null);
  const [cutLineError, setCutLineError] = useState<string | null>(null);
  // Why the line goes here (985): optional, carried onto the cut-line
  // Decision by the move it is written before.
  const [cutLineRationale, setCutLineRationale] = useState<RationaleValue>(EMPTY_RATIONALE);
  const cutLineLocked = session.state !== 'RUN_COMPLETE';
  const cutLineCommitting = useRef(false);
  const commitCutLine = async () => {
    // pointerup + blur can both fire for one release — commit once.
    if (cutLineDraft === null || cutLineCommitting.current) return;
    const position = cutLineDraft;
    if (position === session.cutLinePosition) { setCutLineDraft(null); return; }
    cutLineCommitting.current = true;
    const result = await setCutLine(session.id, position, undefined, toRationaleParts(cutLineRationale))
      .finally(() => { cutLineCommitting.current = false; });
    if (result.success) setCutLineRationale(EMPTY_RATIONALE);
    setCutLineError(result.success ? null : (result.error ?? 'The cut line could not be saved.'));
    // On refusal the slider snaps back to the saved position.
    setCutLineDraft((current) => (current === position ? null : current));
  };
  const items = useItemsStore(s => s.items);
  const userId = useUserStore(s => s.userId) ?? '';
  const cycles = useDegapProcessStore(s => s.cycles);
  const { capabilities } = usePermissions();
  // 1001: the cycle's decision right, as publish_session_outcome will judge it —
  // asked only when the session can produce a Priority List.
  const publishRight = useSessionPublishRight(session.cycleId && session.trackId ? session.id : null);
  const publishRefusedByRight = refusedByRight(publishRight);
  // S-33 (mig 891): the switch and the "Change ranking order" permission, as
  // the server will judge them — the controls are drawn only when both hold.
  const { rule: rankRule, reload: reloadRankRule } = useSessionRankChangeRule(session.id, `${session.state}:${session.updatedAt ?? ''}`);
  const { users: systemUsers } = useSystemUsers();

  // ── Brief A: result_status + provenance ────────────────────────────────────
  const [resultStatus, setResultStatus] = useState<SessionResultStatus | null>(null);
  const [provenance, setProvenance] = useState<SessionResultProvenance | null>(null);
  const [isPublishingResult, setIsPublishingResult] = useState(false);
  const [publishResultError, setPublishResultError] = useState<string | null>(null);
  const [resultStatusLoaded, setResultStatusLoaded] = useState(false);
  // 705: what the publish produced, so the confirmation can say more than "done".
  const [publishedSetSummary, setPublishedSetSummary] =
    useState<{ rankedCount?: number; snapshotVersion?: number | null } | null>(null);

  // A priority list is cycle-scoped (canon 03), so only a session bound to both a
  // cycle and a track can produce one. Anything else publishes the result alone.
  const isCycleBound = Boolean(session.cycleId && session.trackId);

  const sessionIsComplete = ['RUN_COMPLETE', 'PUBLISHED'].includes(session.state);

  useEffect(() => {
    if (!session.rankingOutput) return; // no results yet — skip DB probe
    fetchSessionResultStatus(session.id).then(({ resultStatus: rs, provenance: prov }) => {
      setResultStatus(rs);
      setProvenance(prov);
      setResultStatusLoaded(true);
    });
  }, [session.id, session.rankingOutput]);

  /**
   * One lane (739). This tab used to run the two publish RPCs itself and then
   * make a separate client call to move the session state — three round trips,
   * three chances to half-succeed, and a record set that differed from the Run
   * and Results tabs. `publishSessionResults` is now the only publish path:
   * the server picks the outcome lane or the result lane, writes the report and
   * the catchball prerequisite, and moves the state, in one transaction.
   */
  const handlePublishResult = useCallback(async () => {
    setIsPublishingResult(true);
    setPublishResultError(null);

    const outcome = await publishSessionResults(session.id, {
      actorId: userId || undefined,
      kind: isCycleBound ? 'priorities' : 'result',
    });

    setIsPublishingResult(false);

    if (!outcome.success) {
      if (outcome.code === 'cancelled') return;
      // Stay here and show the server's own reason.
      setPublishResultError(outcome.error ?? 'Failed to publish results');
      return;
    }

    setResultStatus('published');
    if (outcome.lane === 'outcome') {
      setPublishedSetSummary({
        rankedCount: outcome.rankedCount,
        snapshotVersion: outcome.snapshotVersion,
      });
    }
    setProvenance(prev => ({
      rankedItemCount: outcome.rankedCount ?? prev?.rankedItemCount ?? 0,
      publishedAt: outcome.publishedAt ?? null,
      publishedBy: userId,
    }));
  }, [session.id, userId, isCycleBound]);

  // Derive names for provenance display
  const facilitatorUser = systemUsers.find(u => u.id === session.facilitatorId);
  const facilitatorName = facilitatorUser?.name ?? session.facilitatorId;
  const publisherUser = provenance?.publishedBy ? systemUsers.find(u => u.id === provenance.publishedBy) : null;
  const publisherName = publisherUser?.name ?? provenance?.publishedBy ?? null;

  // Tool name from the catalog
  const toolDescriptor = session.selectedToolId
    ? SESSION_TOOL_DESCRIPTORS.find(d =>
        d.aliases.includes(normalizeSessionToolId(session.selectedToolId) ?? '') ||
        d.id === normalizeSessionToolId(session.selectedToolId)
      )
    : null;
  const toolName = toolDescriptor?.name ?? session.selectedToolId ?? null;

  // The gate matches `publish_session_outcome` (mig 705) and the iOS gate (#599):
  // facilitator, cycle owner, or a workspace admin. It was facilitator-only, which
  // was narrower than the RPC and narrower than the message below already claimed.
  // 704 is why the cycle-owner branch is worth having: before it, that branch of
  // publish_session_result raised rather than authorizing.
  //
  // This is a best-effort predicate for showing the button. The RPC is the
  // authority and re-checks all three itself, scoped to the session's own
  // workspace — `capabilities.canAdmin` is true for tenant and platform admins
  // too, so a non-member admin sees the button and gets a clean refusal.
  const isFacilitator = session.facilitatorId === userId;
  const sessionCycle = session.cycleId ? cycles.find(c => c.id === session.cycleId) : null;
  const isCycleOwner = Boolean(sessionCycle?.ownerId && sessionCycle.ownerId === userId);
  const isWorkspaceAdmin = capabilities.canAdmin;
  const canPublishAsRole = isFacilitator || isCycleOwner || isWorkspaceAdmin;
  const canPublishResult = canPublishAsRole && !publishRefusedByRight && sessionIsComplete && resultStatus === 'complete';

  // Named missing-input reasons (shown in place of a disabled/hidden button)
  const publishBlockReason: string | null = (() => {
    if (!sessionIsComplete) return 'Session has not been run yet';
    if (resultStatusLoaded && resultStatus === null) return 'No result to publish — run the session first';
    if (resultStatusLoaded && resultStatus === 'draft') return 'No result to publish — run the session first';
    if (!canPublishAsRole) return 'Only the facilitator, the cycle owner, or a workspace admin can publish results';
    // 1001: the cycle's decision right shuts this person out — the database's own sentence.
    if (publishRefusedByRight) return publishRight.line ?? 'You may not publish these results';
    if (resultStatus === 'incorporated') return 'This result is already part of a submitted proposal';
    return null;
  })();
  // ──────────────────────────────────────────────────────────────────────────

  if (!session.rankingOutput) {
    return (
      <div className="text-center py-16 bg-[var(--bg-raised)] border border-[var(--rule)]">
        <ListOrdered className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
        <h3 className="text-lg font-medium text-[var(--text-pri)] mb-2">No ranking yet</h3>
        <p className="text-[var(--text-ter)] text-sm">
          Aggregate participant responses in the <span className="font-medium">Results</span> tab, then arrange and finalize the order before coming here.
        </p>
        {resultStatusLoaded && resultStatus === null && (
          <p className="text-xs text-[var(--mustard)] mt-2">
            No results yet. Run the session to produce a ranking.
          </p>
        )}
      </div>
    );
  }

  const { rankedItemIds, scores, classifications } = session.rankingOutput;
  // Canon 01 Override stage (migration 768). Everyone sees the overrides; the
  // form is shown to decision authority only (the RPC is the real check).
  const rankOverrides = session.rankingOutput.overrides ?? [];
  const canOverrideRank = session.state === 'RUN_COMPLETE' && rankRule?.canChangeOrder === true;
  const canMoveSessionCutLine = !cutLineLocked && rankRule?.canMoveCutLine === true;
  const itemTitle = (id: string) => items.find(c => c.id === id)?.title || id;
  const userName = (id: string) => systemUsers.find(u => u.id === id)?.name;
  const hasClassificationData = rankedItemIds.some(
    (itemId) => getVisibleClassificationEntries(classifications?.[itemId]).length > 0
  );

  return (
    <div className="space-y-6">
      {/* ── Result Status Card (Brief A) ─────────────────────────────────── */}
      {sessionIsComplete && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">

            {/* Left: status badge + provenance line */}
            <div className="space-y-1.5 min-w-0">
              {/* Status badge */}
              {resultStatus === 'complete' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] rounded-full text-sm font-medium">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Result ready
                </span>
              )}
              {resultStatus === 'published' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] rounded-full text-sm font-medium">
                  <CheckCircle className="h-3.5 w-3.5" />
                  Published
                </span>
              )}
              {resultStatus === 'incorporated' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded-full text-sm font-medium">
                  <CheckCircle className="h-3.5 w-3.5" />
                  Incorporated
                </span>
              )}
              {/* Legacy lifecycle badge when result_status not yet loaded */}
              {!resultStatus && session.state === 'PUBLISHED' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] rounded-full text-sm font-medium">
                  <CheckCircle className="h-3.5 w-3.5" />
                  Published
                </span>
              )}

              {/* Provenance line */}
              {(resultStatus === 'complete' || resultStatus === 'published' || resultStatus === 'incorporated') && (
                <p className="text-xs text-[var(--text-ter)] leading-relaxed">
                  {resultStatus === 'complete' && (
                    <>
                      Run by <span className="font-medium text-[var(--text-sec)]">{facilitatorName}</span>
                      {session.runCompletedAt && (
                        <> on <span className="font-medium text-[var(--text-sec)]">{new Date(session.runCompletedAt).toLocaleDateString()}</span></>
                      )}
                      {' · '}<span className="font-medium text-[var(--text-sec)]">{rankedItemIds.length} items</span>
                      {toolName && <> · <span className="font-medium text-[var(--text-sec)]">{toolName}</span></>}
                    </>
                  )}
                  {resultStatus === 'published' && (
                    <>
                      Published
                      {publisherName && <> by <span className="font-medium text-[var(--text-sec)]">{publisherName}</span></>}
                      {provenance?.publishedAt && (
                        <> on <span className="font-medium text-[var(--text-sec)]">{new Date(provenance.publishedAt).toLocaleDateString()}</span></>
                      )}
                      {' · '}Run by <span className="font-medium text-[var(--text-sec)]">{facilitatorName}</span>
                      {session.runCompletedAt && (
                        <> on <span className="font-medium text-[var(--text-sec)]">{new Date(session.runCompletedAt).toLocaleDateString()}</span></>
                      )}
                      {' · '}<span className="font-medium text-[var(--text-sec)]">{rankedItemIds.length} items</span>
                      {toolName && <> · <span className="font-medium text-[var(--text-sec)]">{toolName}</span></>}
                    </>
                  )}
                  {resultStatus === 'incorporated' && (
                    <>
                      Referenced in a submitted proposal
                    </>
                  )}
                </p>
              )}
            </div>

            {/* Right: action area */}
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              {/* The one publish affordance — same label, confirm and RPC everywhere */}
              {canPublishResult && (
                <button
                  type="button"
                  onClick={handlePublishResult}
                  disabled={isPublishingResult}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPublishingResult ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Radio className="h-4 w-4" />
                  )}
                  {isPublishingResult ? 'Publishing…' : PUBLISH_RESULTS_LABEL}
                </button>
              )}

              {/* What the publication produced (705) */}
              {resultStatus === 'published' && publishedSetSummary && (
                <p className="text-xs text-[var(--text-ter)] text-right max-w-[240px]">
                  Priority list published
                  {typeof publishedSetSummary.rankedCount === 'number' && (
                    <> · <span className="font-medium text-[var(--text-sec)]">
                      {publishedSetSummary.rankedCount} items
                    </span></>
                  )}
                  {typeof publishedSetSummary.snapshotVersion === 'number' && (
                    <> · pool frozen at v{publishedSetSummary.snapshotVersion}</>
                  )}
                </p>
              )}

              {/* Retract publication — the reversal of the button above (migration 738/#694) */}
              {(resultStatus === 'published' || (!resultStatus && session.state === 'PUBLISHED')) && canPublishAsRole && (
                <ReverseActionButton
                  label="Retract publication"
                  confirmTitle="Retract publication"
                  explain="This unpublishes the results, reverts the session to run-complete, and voids the session report. Refused once the publication has been referenced by a proposal."
                  run={(reason) => retractSessionPublication(session.id, reason)}
                  onDone={async () => {
                    await useSessionStore.getState().fetchSessionById(session.id);
                    const { resultStatus: rs, provenance: prov } = await fetchSessionResultStatus(session.id);
                    setResultStatus(rs);
                    setProvenance(prov);
                    setPublishedSetSummary(null);
                  }}
                  variant="secondary"
                  size="sm"
                />
              )}

              {/* What "publish" means here, before it is pressed */}
              {canPublishResult && (
                <p className="text-xs text-[var(--text-ter)] text-right max-w-[240px]">
                  {isCycleBound
                    ? 'Publishes the result, ratifies it as a priority list, freezes the pool, and marks the session published.'
                    : 'Publishes the result and marks the session published. This session is not bound to a cycle and track, so it cannot produce a priority list.'}
                </p>
              )}

              {/* Named missing-input message — shown when conditions not met and result is complete */}
              {!canPublishResult && resultStatus !== 'published' && resultStatus !== 'incorporated' && publishBlockReason && (
                <p className="text-xs text-[var(--text-ter)] text-right max-w-[220px]">
                  {publishBlockReason === 'Session has not been run yet' && (
                    <span>Session has not been run yet</span>
                  )}
                  {publishBlockReason === 'No result to publish — run the session first' && (
                    <span>No result to publish — run the session first</span>
                  )}
                  {publishBlockReason === 'Only the facilitator, the cycle owner, or a workspace admin can publish results' && (
                    <span>Only the facilitator, the cycle owner, or a workspace admin can publish results</span>
                  )}
                  {publishRefusedByRight && publishBlockReason === (publishRight.line ?? 'You may not publish these results') && (
                    <span>{publishBlockReason}</span>
                  )}
                  {publishBlockReason === 'This result is already part of a submitted proposal' && (
                    <span>This result is already part of a submitted proposal</span>
                  )}
                </p>
              )}

              {/* The permanently-disabled "Present — coming soon" button is
                  gone. Canon requires nothing of it: Doc 01 §TV-Mode
                  Presentation and Doc 03 §Result surfacing say the Present
                  affordance *appears when* result_status = published — they do
                  not require a dead control in its place while Brief H is
                  unbuilt. A button that can never be pressed is a claim the UI
                  makes about itself that isn't true. */}
            </div>
          </div>

          {/* Publish error */}
          {publishResultError && (
            <div className="mt-3 bg-[var(--coral-dim)] border border-[var(--coral)] px-3 py-2 text-sm text-[var(--coral)]">
              {publishResultError}
            </div>
          )}
        </div>
      )}

      {/* Header: title + item count */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-[var(--text-pri)]">Ranking</h3>
          <p className="text-sm text-[var(--text-ter)]">
            {rankedItemIds.length} items ranked
            {(session.rankingOutput as { aggregatedFrom?: number })?.aggregatedFrom
              ? ` · aggregated from ${(session.rankingOutput as { aggregatedFrom?: number }).aggregatedFrom} responses`
              : ''}
          </p>
        </div>
      </div>

      {/* Ranked items table */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        <table className="w-full">
          <thead className="bg-[var(--surface)] border-b border-[var(--rule)]">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-[var(--text-sec)] w-16">Rank</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-[var(--text-sec)]">Item</th>
              {hasClassificationData && (
                <th className="text-left px-4 py-3 text-sm font-medium text-[var(--text-sec)]">Classification</th>
              )}
              {session.criteriaConfig.criteriaIds.map((criterionId) => (
                <th key={criterionId} className="text-center px-4 py-3 text-sm font-medium text-[var(--text-sec)]">
                  {criterionId}
                </th>
              ))}
              <th className="text-center px-4 py-3 text-sm font-medium text-[var(--text-sec)]">Total</th>
              {Object.keys((session.rankingOutput as { itemNotes?: Record<string, string> })?.itemNotes ?? {}).length > 0 && (
                <th className="text-left px-4 py-3 text-sm font-medium text-[var(--text-sec)]">Notes</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--rule)]">
            {rankedItemIds.map((itemId, index) => {
              const item = items.find(c => c.id === itemId);
              const itemScores = scores[itemId] || {};
              const itemClassifications = getVisibleClassificationEntries(classifications?.[itemId]);
              const total = itemScores.total ?? Object.entries(itemScores).reduce(
                (sum, [key, val]) => (key === 'total' ? sum : sum + val),
                0
              );
              const isBelowCutLine = session.cutLinePosition !== undefined && index >= session.cutLinePosition;
              const note = (session.rankingOutput as { itemNotes?: Record<string, string> })?.itemNotes?.[itemId];

              return (
                <React.Fragment key={itemId}>
                  {session.cutLinePosition === index && (
                    <tr key={`cutline-${index}`}>
                      <td colSpan={session.criteriaConfig.criteriaIds.length + (hasClassificationData ? 4 : 3)} className="px-4 py-2 bg-[var(--mustard-dim)]">
                        <div className="flex items-center gap-2 text-[var(--mustard)]">
                          <div className="flex-1 h-px bg-[var(--mustard)]" />
                          <span className="text-sm font-medium">Cut Line</span>
                          <div className="flex-1 h-px bg-[var(--mustard)]" />
                        </div>
                      </td>
                    </tr>
                  )}
                  <tr className={`hover:bg-[var(--surface)] ${isBelowCutLine ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3 font-bold text-[var(--text-pri)]">#{index + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                          <Link
                            to={itemHref(itemId)}
                            className="font-medium text-[var(--text-pri)] hover:text-primary-600"
                          >
                            {item?.title || itemId}
                          </Link>
                          {item?.isStrategicallyNominated && (
                            <StrategicNominationBadge variant="icon" />
                          )}
                          <ItemRelationshipFlags itemId={itemId} />
                        </div>
                        {note && <p className="text-xs text-[var(--text-ter)] italic">{note}</p>}
                      </div>
                    </td>
                    {hasClassificationData && (
                      <td className="px-4 py-3">
                        {itemClassifications.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {itemClassifications.map((entry) => (
                              <span
                                key={`${itemId}-${entry.key}`}
                                className="inline-flex items-center rounded-full bg-[var(--hover-tint)] px-2 py-0.5 text-xs text-[var(--text-sec)]"
                                title={`${entry.label}: ${entry.value}`}
                              >
                                <span className="font-medium">{entry.label}:</span>&nbsp;{entry.value}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-[var(--text-ter)]">-</span>
                        )}
                      </td>
                    )}
                    {session.criteriaConfig.criteriaIds.map((criterionId) => (
                      <td key={criterionId} className="px-4 py-3 text-center text-sm text-[var(--text-sec)]">
                        {itemScores[criterionId] || '-'}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-center font-semibold text-[var(--text-pri)]">
                      {total}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Rank overrides — visible to everyone who reads the results (ruling #3) */}
      <Panel title={RANK_OVERRIDES_HEADING} count={rankOverrides.length}>
        <RankOverrideList overrides={rankOverrides} getItemTitle={itemTitle} getUserName={userName} />
      </Panel>
      {canOverrideRank && (
        <RankOverrideForm
          sessionId={session.id}
          rankedItemIds={rankedItemIds}
          getItemTitle={itemTitle}
          onDone={async () => { await useSessionStore.getState().fetchSessionById(session.id); }}
        />
      )}
      {session.state === 'RUN_COMPLETE' && rankRule && !rankRule.canChangeOrder && (
        <p className="text-sm text-[var(--text-sec)]" data-testid="rank-order-needs">
          {rankChangeNeedsLine(rankRule, 'order')}
        </p>
      )}

      {/* S-33: this session's own "Change ranking order" switch (until it is published) */}
      {session.state !== 'PUBLISHED' && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)]">
          <RankOrderChangeSetting
            scopeKind="session"
            scopeId={session.id}
            ids={{
              workspaceId: session.workspaceId ?? null,
              workflowId: sessionCycle?.workflowId ?? null,
              cycleId: session.cycleId ?? null,
              trackId: session.trackId ?? null,
              sessionId: session.id,
            }}
            canEdit={isFacilitator || isWorkspaceAdmin}
            onSaved={reloadRankRule}
          />
        </div>
      )}

      {/* Set Cut Line */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <h4 className="font-medium text-[var(--text-pri)] mb-3">Cut line</h4>
        <p className="text-sm text-[var(--text-ter)] mb-4">Items above the cut line will be prioritized for action</p>
        {canMoveSessionCutLine ? (
        <div className="flex items-center gap-4">
          <input
            type="range"
            min={0}
            max={rankedItemIds.length}
            value={cutLineDraft ?? session.cutLinePosition ?? rankedItemIds.length}
            aria-label="Cut line position"
            // Local state while dragging; ONE governed write on release.
            onChange={(e) => setCutLineDraft(parseInt(e.target.value, 10))}
            onPointerUp={() => void commitCutLine()}
            onKeyUp={() => void commitCutLine()}
            onBlur={() => void commitCutLine()}
            className="flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <span className="text-sm font-medium text-[var(--text-sec)] w-32">
            Top {cutLineDraft ?? session.cutLinePosition ?? rankedItemIds.length} items
          </span>
        </div>
        ) : (
          <p className="text-sm font-medium text-[var(--text-sec)]">
            {session.cutLinePosition != null ? `Top ${session.cutLinePosition} items` : 'No cut line set yet.'}
          </p>
        )}
        {!cutLineLocked && rankRule && !rankRule.canMoveCutLine && (
          <p className="mt-2 text-xs text-[var(--text-ter)]" data-testid="cut-line-needs">
            {rankChangeNeedsLine(rankRule, 'cut_line')}
          </p>
        )}
        {cutLineLocked && (
          <p className="mt-2 text-xs text-[var(--text-ter)]">
            {session.state === 'PUBLISHED'
              ? 'This session is published — the cut line is fixed.'
              : 'The cut line can be set once the session has been run.'}
          </p>
        )}
        {cutLineError && (
          <p role="alert" className="mt-2 text-sm text-[var(--coral)]">
            Cut line not moved: {cutLineError}
          </p>
        )}
        {canMoveSessionCutLine && (
          <RationaleFields
            className="mt-4"
            value={cutLineRationale}
            onChange={setCutLineRationale}
            title="Why the line goes here — saved with your next move of the line"
          />
        )}
      </div>

    </div>
  );
}

// ============================================================================
// Overview Tab
// ============================================================================

// ── Session Readiness Card ────────────────────────────────────────────────────
interface SessionReadinessCardProps {
  session: PrioritizationSession;
  effectiveParticipantCount: number;
  effectiveCriteriaCount: number;
  sessionItemCount: number;
}

function SessionReadinessCard({ session, effectiveParticipantCount, effectiveCriteriaCount, sessionItemCount }: SessionReadinessCardProps) {
  const steps = [
    { key: 'participants', label: 'Participants', ready: effectiveParticipantCount > 0 },
    { key: 'items',        label: 'Items',         ready: sessionItemCount > 0 },
    { key: 'criteria',     label: 'Criteria',      ready: effectiveCriteriaCount > 0 },
    { key: 'tool',         label: 'Tool',           ready: !!session.selectedToolId },
  ];

  const readyCount = steps.filter(s => s.ready).length;
  const totalCount = steps.length;

  const stateLabel = sessionStateLabel(session.state);
  const stateClass = session.state === 'RUN_COMPLETE' || session.state === 'PUBLISHED'
    ? 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/30 dark:text-[var(--text-ter)]'
    : session.state === 'CRITERIA_FINALIZED'
      ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/30'
      : 'bg-[var(--hover-tint)] text-[var(--text-sec)]';

  return (
    <div className="shrink-0 border border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50 p-3 min-w-[260px]">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">Readiness</span>
          <span className="text-xs text-[var(--text-ter)]">{readyCount}/{totalCount}</span>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${stateClass}`}>
          {stateLabel}
        </span>
      </div>

      <div className="h-1.5 w-full rounded-full bg-[var(--hover-tint)] mb-3">
        <div
          className="h-1.5 rounded-full bg-[var(--slate)] transition-all"
          style={{ width: `${(readyCount / totalCount) * 100}%` }}
        />
      </div>

      <div className="grid grid-cols-4 gap-x-3 gap-y-1">
        {steps.map(step => (
          <div key={step.key} className="flex items-center gap-1.5">
            {step.ready ? (
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-[var(--green)]" />
            ) : (
              <Circle className="h-3.5 w-3.5 shrink-0 text-[var(--text-ter)]" />
            )}
            <span className={`text-xs truncate ${step.ready ? 'text-[var(--text-sec)]' : 'text-[var(--text-ter)]'}`}>
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Session Overview Tab ──────────────────────────────────────────────────────
interface SessionOverviewTabProps {
  session: PrioritizationSession;
  track?: Track;
  onNavigate: (tab: Tab) => void;
  effectiveParticipantCount: number;
  effectiveCriteriaCount: number;
}

function SessionOverviewTab({ session, track, onNavigate, effectiveParticipantCount, effectiveCriteriaCount }: SessionOverviewTabProps) {
  const allItemsRaw = useItemsStore(s => s.items);
  const allItems = useMemo(() => allItemsRaw || [], [allItemsRaw]);
  const voicesEnabled = useAgentsAsUsersEnabled();
  const voices = useVoicesStore((s) => s.voices);
  const fetchSeats = useVoicesStore((s) => s.fetchSeats);
  const seatsBySession = useVoicesStore((s) => s.seatsBySession);

  useEffect(() => {
    if (!voicesEnabled) return;
    void fetchSeats(session.id);
  }, [voicesEnabled, session.id, fetchSeats]);

  const voiceSeats = useMemo(
    () =>
      voicesEnabled
        ? (seatsBySession[session.id] ?? []).filter((s) => s.status === 'active')
        : [],
    [voicesEnabled, seatsBySession, session.id],
  );
  const seatedVoices = useMemo(() => {
    const byId = new Map(voices.map((v) => [v.id, v]));
    return voiceSeats
      .map((seat) => byId.get(seat.voiceId))
      .filter((v): v is NonNullable<typeof v> => Boolean(v));
  }, [voiceSeats, voices]);

  const sessionItems = useMemo(() => {
    const ids = session.sessionContextMode === 'isolated'
      ? (session.isolatedItems ?? [])
      : (session.itemIds ?? []);
    return allItems.filter(i => ids.includes(i.id));
  }, [allItems, session.sessionContextMode, session.isolatedItems, session.itemIds]);

  const allowSessionOverride = track?.metadata?.allowSessionOverride !== false;

  const cards = [
    {
      tab: 'participants' as Tab,
      label: 'Participants',
      icon: Users,
      value: String(effectiveParticipantCount),
      meta: effectiveParticipantCount === 1 ? 'participant' : 'participants',
    },
    {
      tab: 'items' as Tab,
      label: 'Items',
      icon: ListChecks,
      value: String(sessionItems.length),
      meta: sessionItems.length === 1 ? 'item' : 'items',
    },
    {
      tab: 'criteria' as Tab,
      label: 'Criteria',
      icon: Target,
      value: String(effectiveCriteriaCount),
      meta: effectiveCriteriaCount === 1 ? 'criterion' : 'criteria',
    },
    {
      tab: 'value-ranges' as Tab,
      label: 'Value ranges',
      icon: Sliders,
      value: track ? (allowSessionOverride ? 'Overridable' : 'Inherited') : '—',
      meta: track ? 'from track' : 'no track linked',
    },
    {
      tab: 'values' as Tab,
      label: 'Values',
      icon: BarChart3,
      value: sessionItems.length > 0 ? `${sessionItems.length} items` : '—',
      meta: 'to evaluate',
    },
    {
      tab: 'setup' as Tab,
      label: 'Setup',
      icon: Settings,
      value: session.name,
      meta: session.sessionContextMode === 'isolated' ? 'Isolated' : 'Session setup',
    },
    {
      tab: 'run' as Tab,
      label: 'Run',
      icon: Play,
      value: session.selectedToolId ? 'Ready' : 'Select tool',
      meta: session.selectedToolId ?? 'no tool selected',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">{session.name}</h2>
          {track ? (
            <p className="text-sm text-[var(--text-ter)] mt-1">
              Track:{' '}
              <Link
                to={`/prioritization/tracks/${track.id}`}
                className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
              >
                {track.name}
              </Link>
              {!allowSessionOverride && (
                <span className="ml-2 text-xs bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)] rounded px-1.5 py-0.5">
                  locked — track overrides disabled
                </span>
              )}
            </p>
          ) : (
            <p className="text-sm text-[var(--text-ter)] mt-1">No track linked</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map(card => {
          const Icon = card.icon;
          return (
            <button
              key={card.tab}
              type="button"
              onClick={() => onNavigate(card.tab)}
              className="flex flex-col gap-2 p-4 border border-[var(--rule)] bg-[var(--bg-raised)] hover:border-primary-300 hover:bg-primary-50/30 transition-colors text-left group"
            >
              <div className="flex items-center gap-2 text-[var(--text-ter)] group-hover:text-primary-600 transition-colors">
                <Icon className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wide">{card.label}</span>
              </div>
              <div>
                <p className="text-xl font-semibold text-[var(--text-pri)]">{card.value}</p>
                <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">{card.meta}</p>
              </div>
            </button>
          );
        })}
      </div>

      {voicesEnabled && seatedVoices.length > 0 && (
        <div className="border border-electric-mustard-200 bg-electric-mustard-50/30 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-2">
              Voices on roster
              <VoiceBadge />
            </h3>
            <button
              type="button"
              onClick={() => onNavigate('participants')}
              className="text-xs font-medium text-primary-600 hover:text-primary-700"
            >
              Manage
            </button>
          </div>
          <p className="text-xs text-[var(--text-ter)] mb-3">
            Context providers — not counted in participant vote totals
          </p>
          <ul className="flex flex-wrap gap-2">
            {seatedVoices.map((v) => (
              <li
                key={v.id}
                className="inline-flex items-center gap-2 border border-electric-mustard-200 bg-[var(--bg-raised)] px-3 py-1.5 text-sm text-[var(--text-pri)]"
              >
                <Mic className="h-3.5 w-3.5 text-electric-mustard-600" />
                {v.displayName}
                <VoiceBadge label={v.badgeLabel} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Value Ranges Tab (session-scoped — inherits from track, overridable)
// ============================================================================

interface SessionValueRangesTabProps {
  session: PrioritizationSession;
  track?: Track;
}

// A track's ranges, read-only here (S-39). The per-criterion override this tab
// used to offer saved to a sessions column that does not exist, so nothing it
// showed was ever kept. A session scores every criterion on one range, its own
// tool_config, which comes from the track (migration 1038) and is changed in
// the session's Values step while the track leaves it open.
function SessionValueRangesTab({ session, track }: SessionValueRangesTabProps) {
  const allCycleConfigsRaw = useAttributeGovernanceStore(s => s.cycleConfigs);
  const allCycleConfigs = useMemo(() => allCycleConfigsRaw || [], [allCycleConfigsRaw]);
  const fetchCycleConfigs = useAttributeGovernanceStore(s => s.fetchCycleConfigs);
  const allAttributes = useAttributesStore(s => s.attributes) || [];
  const sessionScale = readToolSettings(session.toolConfig).scoringScale;

  useEffect(() => {
    if (session.cycleId) fetchCycleConfigs(session.cycleId);
  }, [session.cycleId, fetchCycleConfigs]);

  const trackConfigs = useMemo(() =>
    session.trackId
      ? allCycleConfigs.filter(c => c.trackId === session.trackId)
      : [],
    [allCycleConfigs, session.trackId]
  );

  const { trackScale, scaleLocked } = useTrackStepLocks(session.trackId);

  if (!track) {
    return (
      <div className="border border-[var(--rule)] bg-[var(--surface)] p-8 text-center">
        <Sliders className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-3" />
        <p className="text-sm font-medium text-[var(--text-sec)]">No track linked</p>
        <p className="text-xs text-[var(--text-ter)] mt-1">Value ranges are configured at the track level and are not available for sessions without a track.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-[var(--text-pri)]">Value ranges</h3>
          <p className="text-sm text-[var(--text-ter)] mt-0.5">
            Set on <Link to={`/prioritization/tracks/${track.id}?tab=value-ranges`} className="font-medium text-primary-600 hover:underline">{track.name}</Link>.
          </p>
        </div>
      </div>

      <p className="text-sm text-[var(--text-sec)]" data-testid="session-scores-on">
        This session scores every criterion on {scoringScaleLabel(sessionScale)}.
        {trackScale && scaleLocked && !isSameScoringScale(sessionScale, trackScale)
          ? ` The track has since locked ${scoringScaleLabel(trackScale)}; whoever runs the session can move it there in its Values step.`
          : trackScale && scaleLocked
            ? ' The track has locked it.'
            : ''}
      </p>

      {trackConfigs.length === 0 ? (
        <div className="border border-[var(--rule)] bg-[var(--surface)] p-6 text-center">
          <p className="text-sm text-[var(--text-ter)]">No value ranges configured for this track yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {trackConfigs.map(config => {
            const attr = allAttributes.find(a => a.key === config.attributeKey);
            const trackRangeValue = config.rangeValue as { type?: string; min?: number; max?: number; options?: string[] } | null;
            const trackDisplay = trackRangeValue
              ? trackRangeValue.type === 'number'
                ? `${trackRangeValue.min ?? '?'} – ${trackRangeValue.max ?? '?'}`
                : Array.isArray(trackRangeValue.options) ? trackRangeValue.options.join(', ') : '—'
              : '—';
            return (
              <div key={config.id} className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-[var(--text-pri)]">{attr?.label ?? config.attributeKey}</p>
                    {attr?.description && <p className="text-xs text-[var(--text-ter)] mt-0.5">{attr.description}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-[var(--text-sec)]">{trackDisplay}</p>
                    <p className="text-xs text-[var(--text-ter)] mt-0.5">
                      {(config.rangeStatus ?? 'pending').replace(/^./, (c) => c.toUpperCase())} · on the track
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Values Tab (session-scoped — editable when track allows override)
// ============================================================================

interface SessionValuesTabProps {
  session: PrioritizationSession;
  track?: Track;
}

function SessionValuesTab({ session, track }: SessionValuesTabProps) {
  const allItemsRaw2 = useItemsStore(s => s.items);
  const allItems = useMemo(() => allItemsRaw2 || [], [allItemsRaw2]);
  const allAttributes = useAttributesStore(s => s.attributes) || [];
  const allCycleConfigsRaw2 = useAttributeGovernanceStore(s => s.cycleConfigs);
  const allCycleConfigs = useMemo(() => allCycleConfigsRaw2 || [], [allCycleConfigsRaw2]);
  const fetchCycleConfigs = useAttributeGovernanceStore(s => s.fetchCycleConfigs);
  const acceptedValues = useValueProposalStore(s => s.acceptedValues) ?? [];
  // Who decided each official value (SPEC-value-mark-icon). Official values only — never a session's own.
  const valueProposals = useValueProposalStore(s => s.proposals);
  const valueReplacements = useValueProposalStore(s => s.replacements);
  const valueAiSettings = useValueAiSettingsStore(s => s.settings);
  const valueAgentName = useValueAiSettingsStore(s => s.agentName);
  const loadValueAi = useValueAiSettingsStore(s => s.load);
  useEffect(() => { const ws = getCurrentWorkspaceId(); if (ws) void loadValueAi(ws); }, [loadValueAi]);
  const markPeople = useActorDisplayMap([
    ...acceptedValues.filter(isOfficialValue).map(v => v.acceptedBy),
    ...valueReplacements.map(r => r.changedBy),
  ]);
  const officialMark = (v: AcceptedValue) => {
    const setting = valueAiSettings.find(x => x.attributeKey === v.attributeKey) ?? null;
    return valueMarkFor(
      valueStatus(v.itemId, v.attributeKey, [v], valueProposals, valueReplacements),
      setting,
      aiCanJudgeValue(v.itemId, v.attributeKey, setting, valueProposals),
      { nameOf: id => (id ? markPeople[id] ?? null : null), agentName: valueAgentName },
    );
  };
  const fetchFromSupabase = useValueProposalStore(s => s.fetchFromSupabase);
  const currentUserId = useUserStore(s => s.userId) || '';
  const currentUserName = useUserStore(s => s.userName);
  const currentUserRole = useUserStore(s => s.currentRole);
  const canUser = useUserStore(s => s.can);

  const [savingCell, setSavingCell] = useState<{ itemId: string; attr: string } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [localValues, setLocalValues] = useState<Record<string, Record<string, string>>>({});

  useEffect(() => {
    if (session.cycleId) fetchCycleConfigs(session.cycleId);
    fetchFromSupabase();
  }, [session.cycleId, fetchCycleConfigs, fetchFromSupabase]);

  const sessionItemIds = useMemo(() => {
    return session.sessionContextMode === 'isolated'
      ? (session.isolatedItems ?? [])
      : (session.itemIds ?? []);
  }, [session.sessionContextMode, session.isolatedItems, session.itemIds]);

  const sessionItems = useMemo(
    () => allItems.filter(i => sessionItemIds.includes(i.id)),
    [allItems, sessionItemIds]
  );

  const criteriaConfigs = useMemo(() =>
    session.trackId
      ? allCycleConfigs.filter(c => c.trackId === session.trackId)
      : [],
    [allCycleConfigs, session.trackId]
  );

  const { steps: sessionSteps } = useTrackStepLocks(session.trackId);
  const allowOverride = track?.metadata?.allowSessionOverride !== false && sessionSteps?.S4.disposition !== 'locked';

  const getEffectiveRange = useCallback((attributeKey: string) => {
    const config = criteriaConfigs.find(c => c.attributeKey === attributeKey);
    return (config?.rangeValue as { type?: string; min?: number; max?: number; options?: string[] } | null) ?? null;
  }, [criteriaConfigs]);

  // Roadmap 1C (V-1): this tab says "Values entered here apply to this session
  // only", but it saved an official workspace value. It now saves through the
  // one session-entry path: a contribution, plus this session's value when the
  // person may accept values (src/lib/sessionValueEntry.ts).
  const handleSaveValue = useCallback(async (itemId: string, attributeKey: string, value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setSaveError(null);
    setSavingCell({ itemId, attr: attributeKey });
    try {
      const result = await saveSessionValue({
        itemId,
        attributeKey,
        value: trimmed,
        sessionId: session.id,
        sessionName: session.name,
        trackId: session.trackId,
        userId: currentUserId,
        userName: currentUserName,
        role: currentUserRole,
        mayAcceptForSession: canUser('values.accept'),
      });
      if (result.error) {
        setSaveError(result.error);
      } else {
        await fetchFromSupabase();
        setLocalValues(prev => {
          const next = { ...prev };
          if (!next[itemId]) next[itemId] = {};
          next[itemId][attributeKey] = trimmed;
          return next;
        });
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingCell(null);
    }
  }, [currentUserId, currentUserName, currentUserRole, canUser, session.id, session.name, session.trackId, fetchFromSupabase]);

  if (sessionItems.length === 0) {
    return (
      <div className="border border-[var(--rule)] bg-[var(--surface)] p-8 text-center">
        <BarChart3 className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-3" />
        <p className="text-sm font-medium text-[var(--text-sec)]">No items in this session</p>
        <p className="text-xs text-[var(--text-ter)] mt-1">Add items to the session first, then enter values.</p>
      </div>
    );
  }

  if (criteriaConfigs.length === 0) {
    return (
      <div className="border border-[var(--rule)] bg-[var(--surface)] p-8 text-center">
        <BarChart3 className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-3" />
        <p className="text-sm font-medium text-[var(--text-sec)]">No criteria configured</p>
        <p className="text-xs text-[var(--text-ter)] mt-1">
          {track
            ? <>Configure value ranges on track <Link to={`/prioritization/tracks/${track.id}`} className="text-primary-600 hover:underline">{track.name}</Link> first.</>
            : 'No track linked — criteria come from the track.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-[var(--text-pri)]">Values</h3>
          {track && (
            <p className="text-sm text-[var(--text-ter)] mt-0.5">
              {allowOverride
                ? <>Criteria from <Link to={`/prioritization/tracks/${track.id}`} className="text-primary-600 hover:underline">{track.name}</Link>. Values entered here apply to this session only, and are offered up so someone who approves values can make them official.</>
                : <>Criteria from <Link to={`/prioritization/tracks/${track.id}`} className="text-primary-600 hover:underline">{track.name}</Link>.</>
              }
            </p>
          )}
        </div>
        {!allowOverride && (
          <span className="text-xs bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)] rounded px-2 py-1">
            Read-only — overrides disabled
          </span>
        )}
      </div>

      {saveError && (
        <div className="px-3 py-2 rounded bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">{saveError}</div>
      )}

      <SessionValuesWaitingNote sessionId={session.id} itemIds={sessionItemIds} />

      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-[var(--rule)] overflow-hidden">
          <thead>
            <tr className="bg-[var(--surface)] border-b border-[var(--rule)]">
              <th className="text-left px-4 py-2.5 font-medium text-[var(--text-sec)] min-w-[200px]">Item</th>
              {criteriaConfigs.map(config => {
                const attr = allAttributes.find(a => a.key === config.attributeKey);
                const range = getEffectiveRange(config.attributeKey);
                const rangeHint = range
                  ? range.type === 'number' ? `${range.min}–${range.max}` : (range.options ?? []).slice(0, 3).join(', ')
                  : null;
                return (
                  <th key={config.id} className="text-center px-4 py-2.5 font-medium text-[var(--text-sec)] min-w-[120px]">
                    <div>{attr?.label ?? config.attributeKey}</div>
                    {rangeHint && <div className="text-xs font-normal text-[var(--text-ter)]">{rangeHint}</div>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sessionItems.map((item, idx) => (
              <tr key={item.id} className={idx % 2 === 0 ? 'bg-[var(--bg-raised)]' : 'bg-[var(--surface)]/50'}>
                <td className="px-4 py-2.5 text-[var(--text-pri)] font-medium">{item.title ?? item.id.slice(0, 8)}</td>
                {criteriaConfigs.map(config => {
                  // This session's value first, then the official one — never another session's (V-4).
                  const cellValues = acceptedValues.filter(v =>
                    v.itemId === item.id && v.attributeKey === config.attributeKey && valueCountsFor(v, session.id));
                  const accepted = cellValues.find(v => !isOfficialValue(v)) ?? cellValues[0];
                  const acceptedKind = accepted ? valueScopeKind(accepted, session.id) : null;
                  const localVal = localValues[item.id]?.[config.attributeKey];
                  const displayVal = localVal ?? (accepted ? String(accepted.value) : '');
                  const isSaving = savingCell?.itemId === item.id && savingCell?.attr === config.attributeKey;
                  const range = getEffectiveRange(config.attributeKey);
                  const isSelect = range?.type === 'select' && Array.isArray(range.options);

                  if (!allowOverride) {
                    return (
                      <td key={config.id} className="px-4 py-2.5 text-center text-[var(--text-sec)]">
                        {displayVal || <span className="text-[var(--text-ter)]">—</span>}
                        {acceptedKind && localVal === undefined && (
                          <div className="inline-flex items-center justify-center gap-1 text-[10px] text-[var(--text-ter)]">
                            {VALUE_SCOPE_LABEL[acceptedKind]}
                            {acceptedKind === 'official' && accepted && (() => { const m = officialMark(accepted); return m && <ValueMark {...m} size={11} />; })()}
                          </div>
                        )}
                      </td>
                    );
                  }

                  return (
                    <td key={config.id} className="px-3 py-2">
                      {isSaving ? (
                        <div className="flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-[var(--text-ter)]" /></div>
                      ) : isSelect ? (
                        <select
                          value={displayVal}
                          onChange={e => { if (e.target.value) handleSaveValue(item.id, config.attributeKey, e.target.value); }}
                          className="w-full text-xs rounded border border-[var(--rule)] px-2 py-1 bg-[var(--bg-raised)] text-[var(--text-pri)] focus:border-primary-400 focus:outline-none"
                        >
                          <option value="">—</option>
                          {(range.options ?? []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      ) : (
                        <input
                          type="number"
                          defaultValue={displayVal}
                          min={range?.type === 'number' ? range.min : undefined}
                          max={range?.type === 'number' ? range.max : undefined}
                          onBlur={e => { if (e.target.value !== (accepted ? String(accepted.value) : '')) handleSaveValue(item.id, config.attributeKey, e.target.value); }}
                          className="w-full text-xs rounded border border-[var(--rule)] px-2 py-1 text-center bg-[var(--bg-raised)] text-[var(--text-pri)] focus:border-primary-400 focus:outline-none"
                          placeholder="—"
                        />
                      )}
                      {acceptedKind && localVal === undefined && !isSaving && (
                        <div className="mt-0.5 flex items-center justify-center gap-1 text-[10px] text-[var(--text-ter)]">
                          {VALUE_SCOPE_LABEL[acceptedKind]}
                          {acceptedKind === 'official' && accepted && (() => { const m = officialMark(accepted); return m && <ValueMark {...m} size={11} />; })()}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Session Documents + Provenance sub-component
// ============================================================================

function SessionArtifactsAndProvenance({
  sessionId,
  workspaceId,
}: {
  sessionId: string
  workspaceId: string
}) {
  const events = useSessionLineageEvents(sessionId)
  return (
    <div className="space-y-6">
      <SessionArtifactsPanel sessionId={sessionId} workspaceId={workspaceId} />
      <LineageTimeline events={events} title="Session Lineage" />
    </div>
  )
}

// ============================================================================
// Main Session Detail Page
// ============================================================================

/**
 * A retired session (mig 758 retire_session) is hidden from every list but
 * kept whole; its page shows who retired it and offers Take back instead of
 * the working surface. SessionDetailBody loads the row (fetchSessionById),
 * which files a retired one under `retiredSessions`, so this switches over as
 * soon as the row arrives.
 */
export default function SessionDetail() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const retired = useSessionStore((state) =>
    sessionId ? state.retiredSessions.find((s) => s.id === sessionId) : undefined);
  if (retired) return <RetiredSessionPanel session={retired} />;
  return <SessionDetailBody />;
}

function SessionDetailBody() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoRun = searchParams.get('autorun') === '1' || searchParams.get('live') === '1';
  const hasSuggestedInitialTab = useRef(false);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [backMenuOpen, setBackMenuOpen] = useState(false);
  const backMenuRef = useRef<HTMLDivElement>(null);
  const waitingRoomEnabled = useFeatureFlag(FEATURE_FLAGS.SESSION_WAITING_ROOM);
  // Session copilot (Slice 2) — canonical module + flag gate (§2.6/§2.7) AND
  // the AI-assist capability toggle. Both hooks run unconditionally.
  const sessionCopilotModuleOn = useModuleGate('session_copilot');
  const sessionCopilotCapabilityOn = useAiCapability('session_copilot');

  // Link-to-Track modal state
  const [showLinkTrackModal, setShowLinkTrackModal] = useState(false);
  const [linkTrackTargetId, setLinkTrackTargetId] = useState('');
  const [isLinkingTrack, setIsLinkingTrack] = useState(false);
  const [linkTrackError, setLinkTrackError] = useState<string | null>(null);
  const [linkTrackConflicts, setLinkTrackConflicts] = useState<BindingConflict[]>([]);
  const [linkTrackStep, setLinkTrackStep] = useState<'select' | 'review'>('select');
  const linkTrackSelectRef = useRef<HTMLSelectElement>(null);
  /** No-op: reserved if we need to force a read of the select ref; selection is controlled via `linkTrackTargetId`. */
  const bumpLinkTrackSelect = useCallback(() => {}, []);

  // Chat panel
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [_unreadCount, setUnreadCount] = useState(0);
  // Persist the last-seen message count in localStorage so refreshing the page
  // doesn't reset it back to 0 (which made every existing message look unread).
  const chatSeenKey = sessionId ? `chat-seen-${sessionId}` : null;
  const lastSeenCountRef = useRef<number>(
    chatSeenKey ? (parseInt(localStorage.getItem(chatSeenKey) ?? '0', 10) || 0) : 0
  );
  const startPolling = useSessionMessagesStore(s => s.startPolling);
  const stopPolling = useSessionMessagesStore(s => s.stopPolling);
  const currentUserId = useUserStore(s => s.userId) ?? '';
  const currentUserName = useUserStore(s => s.userName) ?? '';

  // Presence
  const joinPresence = usePresenceStore(s => s.join);
  const leavePresence = usePresenceStore(s => s.leave);
  const presentUsers = usePresenceStore(s => s.presentUsers);
  // S-3: who is here is not shown when the session hides names.
  const hidePresenceNames = namesHidden(useSessionRoomRules(sessionId).rules, 'room');
  const getCurrentWorkspace = useTenantStore(s => s.getCurrentWorkspace);
  
  // Get fetch functions from stores
  const fetchSessions = useSessionStore(state => state.fetchFromSupabase);
  const fetchSessionById = useSessionStore(state => state.fetchSessionById);
  const unsubscribeFromSessionRealtime = useSessionStore(state => state.unsubscribeFromSessionRealtime);
  const syncPaused = useSessionStore(state => sessionId ? state._syncPausedSessions.has(sessionId) : false);
  const fetchCandidates = useItemsStore(state => state.fetchFromSupabase);
  const fetchAttributes = useAttributesStore(state => state.fetchFromSupabase);
  const fetchValueProposals = useValueProposalStore(state => state.fetchFromSupabase);
  const recomputeAllEligibility = useSessionStore(state => state.recomputeAllEligibility);
  const fetchCycles = useDegapProcessStore(state => state.fetchFromSupabase);
  const fetchAllTracks = useTrackStore(state => state.fetchAllTracks);
  const fetchItemPools = useItemPoolsStore(state => state.fetchFromSupabase);
  
  const setSessionItemIds = useSessionStore(state => state.setSessionItemIds);
  const itemPoolsRaw2 = useItemPoolsStore(state => state.itemPools);
  const itemPools = useMemo(() => itemPoolsRaw2 ?? [], [itemPoolsRaw2]);

  // Context ID for the Decision Unit panel (resolved from the session's cycle)
  const fetchContextForCycle = useContextsStore(s => s.fetchContextForCycle);
  const [sessionContextId, setSessionContextId] = useState<string | null>(null);
  const sessionCycleId = useSessionStore(state => state.sessions.find(s => s.id === sessionId)?.cycleId);
  useEffect(() => {
    if (!sessionCycleId) return;
    void fetchContextForCycle(sessionCycleId).then((ctx) => {
      setSessionContextId(ctx?.id ?? null);
    });
  }, [sessionCycleId, fetchContextForCycle]);
  
  // Fetch data on mount and when sessionId changes (cycles/tracks for breadcrumb)
  const fetchCycleParticipants = useCycleParticipantsStore(s => s.fetchParticipantsForCycle);
  const fetchCycleParticipantGroups = useCycleParticipantGroupsStore(s => s.fetchParticipantGroupsForCycle);
  const fetchTrackParticipants = useTrackParticipantsStore(s => s.fetchParticipantsForTrack);
  const fetchTrackParticipantGroups = useTrackParticipantGroupsStore(s => s.fetchParticipantGroupsForTrack);
  const fetchSessionParticipantsForDetail = useSessionParticipantsStore(s => s.fetchParticipantsForSession);
  const [sessionLookupDone, setSessionLookupDone] = useState(false);

  useEffect(() => {
    setSessionLookupDone(false);
    const loadData = async () => {
      console.log('[SessionDetail] Loading data from Supabase...');
      await Promise.all([
        fetchSessions(),
        fetchCandidates(),
        fetchAttributes(),
        fetchValueProposals(),
        fetchCycles(),
        fetchAllTracks(),
        fetchItemPools(),
      ]);
      // Always refresh the open session from DB so ranking_output and other fields stay current.
      if (sessionId) {
        await fetchSessionById(sessionId);
        recomputeAllEligibility(sessionId);
        // Load session participants so isFacilitatorInSession resolves without requiring
        // the user to visit the Participants tab first.
        fetchSessionParticipantsForDetail(sessionId);
      }
      // Re-fetch items if the store is empty — handles the race where fetchCandidates ran
      // before the workspace context resolved (getCurrentWorkspaceId() returned null/wrong
      // workspace). By this point the workspace is set, so the retry will succeed.
      if (useItemsStore.getState().items.length === 0) {
        await fetchCandidates();
      }
      setSessionLookupDone(true);
      console.log('[SessionDetail] Data loaded, recomputing eligibility...');
    };
    loadData();
    return () => {
      if (sessionId) {
        unsubscribeFromSessionRealtime(sessionId);
      }
    };
  }, [sessionId, fetchSessions, fetchSessionById, fetchCandidates, fetchAttributes, fetchValueProposals, recomputeAllEligibility, fetchCycles, fetchAllTracks, fetchItemPools, unsubscribeFromSessionRealtime, fetchSessionParticipantsForDetail]);

  const sessionForFetch = useSessionStore(state => state.sessions.find(s => s.id === sessionId));
  useEffect(() => {
    if (!sessionForFetch?.cycleId || !sessionForFetch?.trackId) return;
    fetchCycleParticipants(sessionForFetch.cycleId);
    fetchCycleParticipantGroups(sessionForFetch.cycleId);
    fetchTrackParticipants(sessionForFetch.trackId);
    fetchTrackParticipantGroups(sessionForFetch.trackId);
  }, [sessionForFetch?.cycleId, sessionForFetch?.trackId, fetchCycleParticipants, fetchCycleParticipantGroups, fetchTrackParticipants, fetchTrackParticipantGroups]);
  
  // Select session directly from state - don't call getter functions in selectors
  const session = useSessionStore(state => 
    state.sessions.find(s => s.id === sessionId)
  );
  useItemLinkScope({ cycleId: session?.cycleId });
  const sessionsLoading = useSessionStore(state => state.syncState.isLoading);
  
  // Auto-seed session pool from track when session has trackId and empty itemIds (run once per session)
  const hasAutoSeededTrackPool = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!session?.trackId || session.itemIds.length > 0) return;
    if (hasAutoSeededTrackPool.current.has(session.id)) return;
    const trackPool = itemPools.find((p: { trackId?: string }) => p.trackId === session.trackId);
    const priorityIds = trackPool?.priorityIds ?? [];
    if (priorityIds.length === 0) return;
    hasAutoSeededTrackPool.current.add(session.id);
    setSessionItemIds(session.id, priorityIds);
  }, [session?.id, session?.trackId, session?.itemIds.length, itemPools, setSessionItemIds]);
  
  // Resolve cycle and track for breadcrumb and back navigation
  const cycle = useDegapProcessStore(state =>
    session ? state.cycles.find(c => c.id === session.cycleId) : null
  );
  const track = useTrackStore(state =>
    session ? state.tracks.find(t => t.id === session.trackId) : null
  );
  const allTracks = useTrackStore(state => state.tracks);
  const linkSessionToTrack = useSessionStore(state => state.linkSessionToTrack);

  // Only unlinked tracks (session has no trackId) are candidates for link-to-track
  const linkableToTracks = useMemo(
    () =>
      allTracks.filter(
        (t) => t.status !== 'archived' && typeof t.id === 'string' && t.id.length > 0
      ),
    [allTracks]
  );

  const resolveLinkTrackSelectionId = (): string => {
    const fromState = linkTrackTargetId.trim();
    if (fromState) return fromState;
    return linkTrackSelectRef.current?.value?.trim() ?? '';
  };

  const resetLinkTrackModalState = useCallback(() => {
    setIsLinkingTrack(false);
    setLinkTrackError(null);
    setLinkTrackTargetId('');
    setLinkTrackStep('select');
    setLinkTrackConflicts([]);
  }, []);

  const openLinkTrackModal = useCallback(() => {
    resetLinkTrackModalState();
    setShowLinkTrackModal(true);
  }, [resetLinkTrackModalState]);

  const closeLinkTrackModal = useCallback(() => {
    resetLinkTrackModalState();
    setShowLinkTrackModal(false);
  }, [resetLinkTrackModalState]);

  const resolvedLinkTrackId = resolveLinkTrackSelectionId();

  const handleLinkTrackReview = () => {
    const trackId = resolveLinkTrackSelectionId();
    if (!trackId) {
      setLinkTrackError('Please select a track.');
      return;
    }
    if (!session) {
      setLinkTrackError('Session is not loaded yet. Try again in a moment.');
      return;
    }
    setLinkTrackTargetId(trackId);
    const selectedTrack = linkableToTracks.find((t) => t.id === trackId);
    if (!selectedTrack) {
      setLinkTrackError(
        'That track is not available in this workspace. Refresh the page or pick another track.'
      );
      return;
    }
    const detected = detectSessionTrackConflicts(
      { modeConfig: session.modeConfig, facilitatorId: session.facilitatorId ?? undefined },
      selectedTrack
    );
    if (detected.length > 0) {
      setLinkTrackConflicts(detected);
      setLinkTrackStep('review');
    } else {
      void handleLinkTrackConfirm(trackId);
    }
  };

  const handleLinkTrackConfirm = async (trackIdOverride?: string) => {
    const trackId = (trackIdOverride ?? linkTrackTargetId).trim();
    if (!trackId || isLinkingTrack) return;
    setIsLinkingTrack(true);
    setLinkTrackError(null);
    try {
      const result = await linkSessionToTrack(sessionId!, trackId);
      if (result.success) {
        closeLinkTrackModal();
      } else {
        setLinkTrackError(result.error ?? 'Failed to link session to track.');
      }
    } finally {
      setIsLinkingTrack(false);
    }
  };
  const { effectiveUserIds } = useEffectiveSessionParticipants(session ?? null);
  const effectiveParticipantCount = effectiveUserIds.length;

  // Initial tab from URL or Setup by default (once per session load)
  const prevSessionIdRef = useRef<string | undefined>(undefined);
  if (sessionId !== prevSessionIdRef.current) {
    prevSessionIdRef.current = sessionId;
    hasSuggestedInitialTab.current = false;
  }
  useEffect(() => {
    if (!session || hasSuggestedInitialTab.current) return;
    hasSuggestedInitialTab.current = true;

    const tabParam = searchParams.get('tab');
    const validTabParams: Tab[] = [
      'overview', 'setup', 'participants', 'items', 'criteria', 'value-ranges', 'values', 'run', 'results', 'review', 'ranking', 'prioritization-event', 'transcript',
      ...(waitingRoomEnabled ? ['waiting-room' as Tab] : []),
    ];
    if (tabParam && validTabParams.includes(tabParam as Tab)) {
      setActiveTab(tabParam as Tab);
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      next.delete('autorun');
      setSearchParams(next, { replace: true });
      return;
    }

    setActiveTab('setup');
    if (searchParams.has('autorun')) {
      const next = new URLSearchParams(searchParams);
      next.delete('autorun');
      setSearchParams(next, { replace: true });
    }
  }, [session, searchParams, setSearchParams, waitingRoomEnabled]);

  // Track configs for criteria count and banner (conditions from track)
  const allCycleConfigs = useAttributeGovernanceStore(s => s.cycleConfigs);
  const fetchTrackConfigs = useAttributeGovernanceStore(s => s.fetchTrackConfigs);
  useEffect(() => {
    if (session?.trackId) fetchTrackConfigs(session.trackId);
  }, [session?.trackId, fetchTrackConfigs]);
  const trackConfigs = useMemo(
    () => session?.trackId ? allCycleConfigs.filter(c => c.trackId === session.trackId) : [],
    [session?.trackId, allCycleConfigs]
  );
  const isTrackLinked = session?.sessionContextMode === 'cycle_linked' && session?.trackId;
  const effectiveCriteriaCount = (session?.criteriaConfig?.criteriaIds?.length ?? 0)
    || (isTrackLinked ? trackConfigs.filter(c => c.rangeStatus === 'accepted').length : 0);

  const sessionItemCount = (() => {
    if (!session) return 0;
    if (session.sessionContextMode === 'isolated') return session.isolatedItems?.length ?? 0;
    if (isTrackLinked && session.trackId) {
      const trackPool = itemPools.find(p => p.trackId === session.trackId);
      // Union of session-specific items and track pool (inherited)
      return new Set([...(session.itemIds ?? []), ...(trackPool?.priorityIds ?? [])]).size;
    }
    return session.itemIds?.length ?? 0;
  })();

  // Start chat polling when session is available; stop on unmount
  useEffect(() => {
    if (!sessionId) return;
    startPolling(sessionId);
    return () => { stopPolling(); };
  }, [sessionId, startPolling, stopPolling]);

  // Redirect confirmed participants (non-facilitators) to the focused /participate view.
  // Only triggers when: the session is active (not yet complete), the user is in the
  // explicit participant list, and they are not the facilitator.  Admins who are not
  // in the participant list are NOT redirected so they can review sessions freely.
  // Live sessions (timingMode !== 'async') are exempt: participants stay on SessionDetail
  // so they can access the collaborative tool panel on the Run tab.
  useEffect(() => {
    if (!session || !currentUserId || !sessionId) return;
    const isFacilitator = session.facilitatorId === currentUserId;
    const isComplete = session.state === 'RUN_COMPLETE' || session.state === 'PUBLISHED';
    const isInParticipantList = effectiveUserIds.includes(currentUserId);
    const isLiveSession = session.modeConfig?.timingMode !== 'async';
    if (!isFacilitator && !isComplete && isInParticipantList && !isLiveSession) {
      navigate(`/prioritization/sessions/${sessionId}/participate`, { replace: true });
    }
  }, [session, currentUserId, sessionId, navigate, effectiveUserIds]);

  // Join presence channel when entering a live session
  useEffect(() => {
    if (!sessionId || !currentUserId) return;
    const role = session?.facilitatorId === currentUserId ? 'facilitator' : 'participant';
    void joinPresence(sessionId, currentUserId, currentUserName || 'User', role);
    return () => { void leavePresence(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, currentUserId]);

  // Derive unread count: total messages minus how many were visible when panel was last open.
  // We subscribe to the messages array so this re-evaluates whenever messages change.
  const allMessages = useSessionMessagesStore(s => s.messages);
  const allSessionMessages = useMemo(
    () => sessionId ? allMessages.filter(m => m.sessionId === sessionId) : [],
    [sessionId, allMessages]
  );
  useEffect(() => {
    if (isChatOpen) {
      lastSeenCountRef.current = allSessionMessages.length;
      if (chatSeenKey) localStorage.setItem(chatSeenKey, String(allSessionMessages.length));
      setUnreadCount(0);
    } else {
      const unseen = allSessionMessages.length - lastSeenCountRef.current;
      setUnreadCount(prev => (unseen > 0 ? unseen : prev));
    }
  }, [allSessionMessages.length, isChatOpen, chatSeenKey]);

  // Close chat panel when navigating away from the run tab
  useEffect(() => {
    if (activeTab !== 'run') {
      setIsChatOpen(false);
    }
  }, [activeTab]);

  if (!session) {
    if (sessionsLoading || !sessionLookupDone) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
        </div>
      );
    }
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-4">Session not found</h2>
        <div className="flex items-center justify-center gap-4">
          <Link
            to={eventDesignerHref({ resume: sessionId })}
            className="text-primary-600 hover:text-primary-700"
          >
            Open in Event Designer
          </Link>
          <Link to="/prioritization/sessions" className="text-primary-600 hover:text-primary-700">
            Back to Sessions
          </Link>
        </div>
      </div>
    );
  }
  
  // Tabs organised into four phases. Each group renders with a visual divider
  // between it and the next. iconColor tints the icon for inactive tabs so
  // each section is visually distinct without needing text labels.
  type TabDef = { id: Tab; label: string; icon: typeof Settings; count?: number };
  const tabGroups: { label: string; iconColor: string; tabs: TabDef[] }[] = [
    {
      label: 'Overview',
      iconColor: 'text-[var(--text-ter)] dark:text-[var(--text-dim)]',
      tabs: [
        { id: 'overview', label: 'Overview', icon: LayoutDashboard },
      ],
    },
    {
      // What the session is working with — inherits from track / cycle defaults
      label: 'Configure',
      iconColor: 'text-[var(--text-ter)] dark:text-[var(--text-ter)]',
      tabs: [
        { id: 'participants', label: 'Participants', icon: Users, count: effectiveParticipantCount || undefined },
        { id: 'items', label: 'Items', icon: ListChecks, count: (session.sessionContextMode === 'isolated' ? session.isolatedItems?.length : session.itemIds?.length) || undefined },
        { id: 'criteria', label: 'Criteria', icon: Target, count: effectiveCriteriaCount || undefined },
        { id: 'value-ranges', label: 'Value ranges', icon: Sliders },
        { id: 'values', label: 'Values', icon: BarChart3 },
      ],
    },
    {
      // The two steps to start and run the session
      label: 'Launch',
      iconColor: 'text-[var(--green)] dark:text-[var(--text-ter)]',
      tabs: [
        { id: 'setup', label: 'Setup', icon: Settings },
        ...(waitingRoomEnabled ? [{ id: 'waiting-room' as const, label: 'Waiting Room', icon: DoorOpen }] : []),
        { id: 'run', label: 'Run', icon: Play },
      ],
    },
    {
      // Post-run analysis: raw results → structured review → final ranking.
      // Review and Ranking both read `session.rankingOutput` and render nothing
      // but a "come back after the run" placard while it is null, so before the
      // run they are two tabs that can only tell you they are empty. Hide them
      // until there is output to work on. Results stays — it is the tab that
      // aggregates responses *into* rankingOutput, so it is live pre-run. Run
      // and Waiting Room are deliberately NOT gated the other way: re-running a
      // completed session is a supported flow (`reRunOf`).
      label: 'Outcomes',
      iconColor: 'text-[var(--mustard)] dark:text-[var(--mustard)]',
      tabs: [
        { id: 'results', label: 'Results', icon: Flag },
        ...(session.rankingOutput
          ? [
              { id: 'review' as const, label: 'Review', icon: Pencil },
              { id: 'ranking' as const, label: 'Ranking', icon: ListOrdered },
            ]
          : []),
        // S1-S5 sessions can carry a nested prioritization event (cut line or Q&A)
        ...(session.intent !== 'rank_priorities'
          ? [{ id: 'prioritization-event' as const, label: 'Prioritization event', icon: Scissors }]
          : []),
      ],
    },
    {
      // Supporting record: the session's documents (recording, video, transcript, files, links) + live transcript + discussion thread
      label: 'Record',
      iconColor: 'text-[var(--text-ter)] dark:text-[var(--text-ter)]',
      tabs: [
        { id: 'artifacts', label: 'Documents', icon: FileText },
        { id: 'transcript', label: 'Transcript', icon: Mic },
        { id: 'discussion', label: 'Discussion', icon: MessageSquare },
      ],
    },
  ];
  return (
    <div className="space-y-6">
      {/* Sync-paused notice — Brief F correction §3: non-blocking, auto-clears on next successful event write */}
      {syncPaused && (
        <div className="flex items-center gap-2 px-3 py-2 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-[var(--mustard)] text-sm">
          <span className="w-2 h-2 rounded-full bg-[var(--mustard)] flex-shrink-0" />
          <span>Live sync paused — your actions are saving but may not appear to others immediately.</span>
        </div>
      )}

      {/* Decision Unit — accumulated state for this session's context */}
      {sessionContextId && (
        <DecisionUnitPanel
          contextId={sessionContextId}
          trackId={session.trackId ?? undefined}
          activeCycleId={session.cycleId ?? undefined}
          compact
        />
      )}

      {/* Header. The title row belongs to the title: the situation readout and
          the readiness card sit on their own rows below it, not beside it, so
          a long session name is never squeezed into a column by its
          neighbours (build contract §2.8). */}
      <div className="flex flex-col gap-4">
      {/* Wraps on a phone: name row first, the actions below it. */}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="flex items-center gap-4 min-w-0 flex-[1_1_22rem]">
          {/* Back dropdown: Sessions list | Track | Cycle */}
          <div className="relative" ref={backMenuRef}>
            <button
              type="button"
              onClick={() => setBackMenuOpen(!backMenuOpen)}
              className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors inline-flex items-center gap-1"
              aria-expanded={backMenuOpen}
              aria-haspopup="true"
            >
              <ArrowLeft className="h-5 w-5" />
              <ChevronDown className="h-4 w-4" />
            </button>
            {backMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  aria-hidden="true"
                  onClick={() => setBackMenuOpen(false)}
                />
                <div className="absolute left-0 top-full mt-1 w-56 bg-[var(--bg-raised)] shadow-lg border border-[var(--rule)] py-1 z-20">
                  <button
                    type="button"
                    onClick={() => {
                      setBackMenuOpen(false);
                      navigate('/prioritization/sessions');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)] text-left"
                  >
                    Sessions list
                  </button>
                  {session.trackId && (
                    <button
                      type="button"
                      onClick={() => {
                        setBackMenuOpen(false);
                        navigate(`/prioritization/tracks/${session.trackId}`);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)] text-left"
                    >
                      Track: {track?.name ?? 'Track'}
                    </button>
                  )}
                  {session.cycleId && (
                    <button
                      type="button"
                      onClick={() => {
                        setBackMenuOpen(false);
                        navigate(`/prioritization/cycles/${session.cycleId}`);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)] text-left"
                    >
                      Cycle: {cycle?.name ?? 'Cycle'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{session.name}</h1>
              <SessionTypeBadge intent={session.intent} size="md" />
              <DecisionClassBadge intent={session.intent} size="md" />
              <SessionStateBadge state={session.state} />
              {/* Stage 2: Session context mode */}
              {(() => {
                const mode: SessionContextMode = session.sessionContextMode ?? (session.trackId != null && session.trackId !== '' ? 'cycle_linked' : 'app_linked');
                const classByMode = { cycle_linked: 'bg-[var(--slate-dim)] text-[var(--text-sec)]', app_linked: 'bg-[var(--slate-dim)] text-[var(--text-sec)]', isolated: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' };
                const labelByMode = { cycle_linked: 'Cycle-linked', app_linked: 'App-linked', isolated: 'Isolated' };
                return (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${classByMode[mode]}`}>
                    {labelByMode[mode]}
                  </span>
                );
              })()}
            </div>
            <StructureVersionFinePrint table="prioritization_sessions" id={session.id} mode="started" className="mt-1" />
            <div className="flex items-center gap-4 mt-1 text-sm text-[var(--text-ter)] flex-wrap">
              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                Updated {new Date(session.updatedAt).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1.5">
                <Users className="h-4 w-4" />
                {effectiveParticipantCount} participants
              </span>
              {/* Cycle attachment */}
              {cycle ? (
                <button
                  type="button"
                  onClick={() => navigate(`/prioritization/cycles/${cycle.id}`)}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)] hover:bg-[var(--slate-dim)] transition-colors font-medium text-xs"
                  title={`Go to cycle: ${cycle.name}`}
                >
                  <Flag className="h-3 w-3 flex-shrink-0" />
                  {cycle.name}
                </button>
              ) : (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--hover-tint)] text-[var(--text-ter)] text-xs">
                  <Flag className="h-3 w-3 flex-shrink-0" />
                  No cycle
                </span>
              )}
              {/* Track attachment */}
              {track ? (
                <button
                  type="button"
                  onClick={() => navigate(`/prioritization/tracks/${track.id}`)}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)] hover:bg-[var(--slate-dim)] transition-colors font-medium text-xs"
                  title={`Go to track: ${track.name}`}
                >
                  <Target className="h-3 w-3 flex-shrink-0" />
                  {track.name}
                </button>
              ) : (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--hover-tint)] text-[var(--text-ter)] text-xs">
                  <Target className="h-3 w-3 flex-shrink-0" />
                  No track
                </span>
              )}
              {/* Realtime connection indicator — always visible when in a session (Brief F correction §2) */}
              {sessionId && <SessionRealtimeIndicator sessionId={sessionId} />}
              {/* Presence strip — live online avatars */}
              {presentUsers.length > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-[var(--green)] flex-shrink-0" />
                  <span className="text-[var(--green)] dark:text-[var(--text-ter)] font-medium">
                    {presentUsers.length} online
                  </span>
                  {!hidePresenceNames && <span className="flex -space-x-1 ml-0.5">
                    {presentUsers.slice(0, 5).map(u => (
                      <span
                        key={u.userId}
                        className="w-5 h-5 rounded-full bg-[var(--slate)] border-2 border-white text-white text-[9px] font-bold flex items-center justify-center"
                        title={`${u.userName}${u.role === 'facilitator' ? ' (facilitator)' : ''}`}
                      >
                        {u.userName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                      </span>
                    ))}
                    {presentUsers.length > 5 && (
                      <span className="w-5 h-5 rounded-full bg-[var(--text-ter)] border-2 border-white text-[var(--text-sec)] text-[9px] font-bold flex items-center justify-center">
                        +{presentUsers.length - 5}
                      </span>
                    )}
                  </span>}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Link to Track — only for sessions without a track */}
          {!session.trackId && (
            <button
              type="button"
              onClick={() => openLinkTrackModal()}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] border border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors"
              title="Attach this session to a Track to enable inheritance"
            >
              <Link2 className="h-4 w-4" />
              Link to Track
            </button>
          )}
          {/* Unlink from track — the reversal of the button above (migration 738/#694) */}
          {session.trackId && session.facilitatorId === currentUserId && (
            <ReverseActionButton
              label="Unlink from track"
              confirmTitle="Unlink from track"
              explain={`This removes "${session.name}" from ${track?.name ?? 'its track'}. Refused once the session has run or produced results.`}
              run={(reason) => unlinkSessionFromTrack(session.id, reason)}
              onDone={() => fetchSessionById(session.id)}
              variant="secondary"
              size="sm"
            />
          )}
          {session.state !== 'PUBLISHED' && (
            <AskButton
              home={{
                type: 'session',
                id: session.id,
                title: session.name,
                cycleId: session.cycleId ?? null,
                trackId: session.trackId ?? null,
                itemIds: session.itemIds ?? [],
              }}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
            />
          )}
          <FollowButton
            type="session"
            id={session.id}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
          />
        </div>
      </div>

        <SituationReadout vector={session.situationVector} />

        <SessionReadinessCard
          session={session}
          effectiveParticipantCount={effectiveParticipantCount}
          effectiveCriteriaCount={effectiveCriteriaCount}
          sessionItemCount={sessionItemCount}
        />
      </div>

      {/* Session copilot (Slice 2) — work lane only; drafts, never actions */}
      {sessionCopilotModuleOn && sessionCopilotCapabilityOn && (
        <SessionCopilotPanel
          session={session}
          participantCount={effectiveParticipantCount}
          itemCount={sessionItemCount}
        />
      )}

      {/* Tabs — grouped by phase.
          The five phases (Overview / Configure / Launch / Outcomes / Record) were
          already defined in `tabGroups` with their own labels and colours, but the
          strip rendered all fifteen tabs flat, separated only by a 1px hairline, and
          never drew the labels. Fifteen peer destinations is what made this screen
          read as overwhelming; four named phases is the same information with the
          structure its author already encoded. The single-tab Overview group draws
          no header — its label would just repeat the tab's. */}
      <div className="flex items-end gap-5 border-b border-[var(--rule)] dark:border-[var(--rule)] overflow-x-auto">
        {tabGroups.map((group) => (
          <div key={group.label} className="flex flex-col flex-shrink-0">
            {group.tabs.length > 1 && (
              <span
                className={`px-4 pt-2 pb-0.5 font-mono text-[0.62rem] uppercase tracking-[0.12em] ${group.iconColor}`}
              >
                {group.label}
              </span>
            )}
            <div className={`flex items-center ${group.tabs.length > 1 ? '' : 'pt-[1.35rem]'}`}>
            {group.tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap
                    ${isActive
                      ? 'text-primary-600 dark:text-primary-400 border-primary-600 dark:border-primary-400 bg-primary-50/50 dark:bg-primary-950/20'
                      : 'text-[var(--text-ter)] dark:text-[var(--text-ter)] border-transparent hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
                    }
                  `}
                >
                  <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? '' : group.iconColor}`} />
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                      isActive
                        ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                        : 'bg-[var(--hover-tint)] dark:bg-[var(--bg-raised)] text-[var(--text-sec)] dark:text-[var(--text-ter)]'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
            </div>
          </div>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && <SessionOverviewTab session={session} onNavigate={setActiveTab} effectiveParticipantCount={effectiveParticipantCount} effectiveCriteriaCount={effectiveCriteriaCount} track={track ?? undefined} />}
        {activeTab === 'setup' && <SetupTab session={session} onNavigate={setActiveTab} />}
        {activeTab === 'participants' && <ParticipantsTab session={session} />}
        {activeTab === 'items' && <SessionItemsTab session={session} />}
        {activeTab === 'criteria' && <CriteriaTab session={session} effectiveParticipantCount={effectiveParticipantCount} />}
        {activeTab === 'value-ranges' && <SessionValueRangesTab session={session} track={track ?? undefined} />}
        {activeTab === 'values' && <SessionValuesTab session={session} track={track ?? undefined} />}
        {activeTab === 'run' && <RunTab session={session} onSwitchTab={setActiveTab} effectiveParticipantCount={effectiveParticipantCount} effectiveParticipantIds={effectiveUserIds} autoRun={autoRun} onAutoRunConsumed={() => setSearchParams({}, { replace: true })} />}
        {activeTab === 'results' && <ResultsTab session={session} effectiveParticipantCount={effectiveParticipantCount} onGoToReview={() => setActiveTab('review')} />}
        {activeTab === 'review' && <ReviewTab session={session} onGoToRanking={() => setActiveTab('ranking')} />}
        {activeTab === 'ranking' && <RankingTab session={session} effectiveParticipantCount={effectiveParticipantCount} />}
        {activeTab === 'prioritization-event' && <NestedPrioritizationEventPanel session={session} />}

        {/* ── Documents Tab (id 'artifacts' for URL stability): recordings, videos, transcripts, files and links ── */}
        {activeTab === 'artifacts' && (
          <SessionArtifactsAndProvenance
            sessionId={session.id}
            workspaceId={session.workspaceId ?? getCurrentWorkspaceId() ?? ''}
          />
        )}

        {/* ── Transcript Tab: in-session audio recording and live transcript ── */}
        {activeTab === 'transcript' && (
          <SessionTranscriptTab session={session} />
        )}

        {/* ── Waiting Room Tab: pre-session gathering space (feature-flagged) ── */}
        {waitingRoomEnabled && activeTab === 'waiting-room' && (
          <SessionWaitingRoomTab session={session} />
        )}

        {/* ── Discussion Tab: async threaded discussion (separate from live run chat) ── */}
        {activeTab === 'discussion' && (
          <EntityCommentsTab
            entityType="session"
            entityId={session.id}
            workspaceId={session.workspaceId ?? getCurrentWorkspaceId() ?? ''}
          />
        )}
      </div>

      {/* What people were asked to do in this session (1054). Ask is in the header. */}
      <div className="mt-6">
        <AssignmentsSection home={{ type: 'session', id: session.id }} linkedItems={[]} />
      </div>

      {/* Link to Track Modal */}
      {showLinkTrackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (!isLinkingTrack) closeLinkTrackModal();
            }}
          />
          <div className="relative bg-[var(--bg-raised)] shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 bg-[var(--slate-dim)] flex items-center justify-center">
                <Link2 className="h-5 w-5 text-[var(--text-ter)]" />
              </div>
              <h2 className="text-lg font-semibold text-[var(--text-pri)]">
                {linkTrackStep === 'review' ? 'Review Track Requirements' : 'Link Session to Track'}
              </h2>
            </div>

            {linkTrackStep === 'select' ? (
              <>
                <p className="text-sm text-[var(--text-ter)] mb-5 pl-12">
                  Attach this session to a Track. The track's configuration will take precedence via the inheritance cascade.
                </p>
                {linkTrackError && (
                  <div className="mb-4 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">{linkTrackError}</div>
                )}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Track *</label>
                  <select
                    ref={linkTrackSelectRef}
                    value={linkTrackTargetId}
                    onChange={(e) => {
                      setLinkTrackTargetId(e.target.value);
                      bumpLinkTrackSelect();
                    }}
                    onBlur={() => bumpLinkTrackSelect()}
                    className="no-dark bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-sky-500 focus:border-[var(--slate)]"
                  >
                    <option value="">Select a Track</option>
                    {linkableToTracks.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name}{t.isStandalone ? ' (Unbound)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => closeLinkTrackModal()}
                    className="flex-1 px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                    disabled={isLinkingTrack}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleLinkTrackReview}
                    disabled={!resolvedLinkTrackId || isLinkingTrack}
                    className="flex-1 px-4 py-2 text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Continue
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-3">
                <BindingConflictReview
                  bindingLabel={`Linking to "${linkableToTracks.find(t => t.id === linkTrackTargetId)?.name ?? 'track'}"`}
                  conflicts={linkTrackConflicts}
                  onAccept={() => { void handleLinkTrackConfirm(); }}
                  onCancel={() => { setLinkTrackStep('select'); setLinkTrackConflicts([]); setLinkTrackError(null); }}
                  isAccepting={isLinkingTrack}
                  error={linkTrackError}
                  acceptLabel="Accept & Link"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chat panel — fixed overlay on the right edge, only during an active run */}
      {activeTab === 'run' && isChatOpen && ReactDOM.createPortal(
        <div
          className="fixed top-0 right-0 h-full z-50 shadow-2xl"
          style={{ width: 320 }}
          aria-label="Live chat panel"
        >
          <SessionChatPanel
            sessionId={session.id}
            sessionName={session.name}
            workspaceId={getCurrentWorkspace()?.id ?? ''}
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            isLive={session.modeConfig?.timingMode === 'live'}
            onClose={() => setIsChatOpen(false)}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
